import { getBackendUrl } from '@/utils/apiUrl';
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import ImageEditor from '../components/ImageEditor';
import { User, FileSignature, Camera, Calculator, FileText, ChevronRight, ArrowLeft, Search, FolderOpen, FileCheck, Mail, Building, Edit, Download, CheckCircle, X, Receipt, Plus, Trash2, Zap, Brain, Car } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import DATMyClaimIFrame from '../components/DATMyClaimIFrame';
import DatContractForm from '../components/DatContractForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const NewCase = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState('');
  const [currentPdfTitle, setCurrentPdfTitle] = useState('');
  const [selectedDocs, setSelectedDocs] = useState([]); // Für Checkbox-Auswahl
  const [bundlingPdfs, setBundlingPdfs] = useState(false); // Loading state
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]); // Track completed steps
  const [companySettings, setCompanySettings] = useState({});
  
  // Custom Invoice Items State
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [customInvoiceItems, setCustomInvoiceItems] = useState([]);
  const [invoiceCatalog, setInvoiceCatalog] = useState([]);
  const [newItem, setNewItem] = useState({
    description: '',
    quantity: 1,
    unit_price: 0,
    unit: 'Stk'
  });
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [datPositions, setDatPositions] = useState([]);
  const [loadingDatPositions, setLoadingDatPositions] = useState(false);
  const [scanningPdf, setScanningPdf] = useState(false);  // KI-PDF-Scanner State
  const [generatingAiText, setGeneratingAiText] = useState(false);  // KI-Abtretungstext State
  const [savingAbtretungText, setSavingAbtretungText] = useState(false);  // Speichern-State
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [editingImageIndex, setEditingImageIndex] = useState(null);
  const sigPadAuftraggeber = useRef(null);
  const sigPadVersicherer = useRef(null);
  const sigPadAbtretung = useRef(null);
  const caseCreationInProgress = useRef(false); // Prevent double case creation
  
  const [datIframeUrl, setDatIframeUrl] = useState('');
  const [loadingIframe, setLoadingIframe] = useState(false);
  const [datCalculationResult, setDatCalculationResult] = useState(null);
  const [datContractId, setDatContractId] = useState(null); // Store DAT Contract ID
  const [caseId, setCaseId] = useState(null); // Store the created case ID
  const [caseNumber, setCaseNumber] = useState(null); // Store the case number
  const [autoSaving, setAutoSaving] = useState(false);
  
  // DAT Credentials State
  const [datCredentialsNeeded, setDatCredentialsNeeded] = useState(false);
  const [datCredentialsStatus, setDatCredentialsStatus] = useState(null); // null = loading, true = has access, false = no access
  const [datCredentialsForm, setDatCredentialsForm] = useState({
    customerNumber: '',
    userLogin: '',
    signatureKey: ''
  });
  const [savingDatCredentials, setSavingDatCredentials] = useState(false);
  
  // Set default calculation tab based on user modules
  useEffect(() => {
    if (user?.modules && user.modules.length > 0) {
      const hasDatMyClaim = user.modules.includes('dat_myclaim');
      const hasDatGlass = user.modules.includes('dat_glass');
      
      // Wenn nur dat_glass, setze 'dat' als Standard-Tab
      if (hasDatGlass && !hasDatMyClaim && !formData.calculationTab) {
        setFormData(prev => ({ ...prev, calculationTab: 'dat' }));
      }
      // Wenn nur dat_myclaim, setze 'soap' als Standard-Tab
      else if (hasDatMyClaim && !hasDatGlass && !formData.calculationTab) {
        setFormData(prev => ({ ...prev, calculationTab: 'soap' }));
      }
    }
  }, [user?.modules]);
  
  // Check DAT credentials status on mount
  useEffect(() => {
    const checkDatCredentials = async () => {
      try {
        const response = await fetch(`${getBackendUrl()}/api/dat/credentials/status`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setDatCredentialsStatus(data.hasCredentials);
          if (!data.hasCredentials) {
            setDatCredentialsNeeded(true);
          }
        }
      } catch (error) {
        console.error('Error checking DAT credentials:', error);
        setDatCredentialsStatus(false);
      }
    };
    checkDatCredentials();
  }, []);
  
  // GDV Simulation: Scan counter for cycling through 3 rules
  const [scanCounter, setScanCounter] = useState(0);
  
  // DEKRA Rates State
  const [dekraRates, setDekraRates] = useState(null);
  const [loadingDekraRates, setLoadingDekraRates] = useState(false);
  
  // New state for PURE SOAP Solution
  const [loadingCalculation, setLoadingCalculation] = useState(false);
  const [calculationError, setCalculationError] = useState(null);
  
  // Insurance search/autocomplete
  const [insuranceSearch, setInsuranceSearch] = useState('');
  const [insuranceResults, setInsuranceResults] = useState([]);
  const [showInsuranceDropdown, setShowInsuranceDropdown] = useState(false);
  const [loadingInsurances, setLoadingInsurances] = useState(false);
  
  // Custom invoice positions
  const [customPositions, setCustomPositions] = useState([]);
  const [showAddPositionModal, setShowAddPositionModal] = useState(false);
  const [newPosition, setNewPosition] = useState({
    description: '',
    quantity: 1,
    price: 0,
    saveAsDefault: false
  });
  
  // Email state
  const [emailData, setEmailData] = useState({
    to: '',
    subject: 'Ihre Schadensdokumentation',
    body: `Sehr geehrte Damen und Herren,\n\nanbei erhalten Sie die Dokumentation zu Ihrem Schadensfall.\n\nMit freundlichen Grüßen`,
    attachments: ['auftrag', 'abtretung', 'lichtbilder', 'dat', 'rechnung']
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);
  
  // Company data for Firmenstempel
  const [companyData, setCompanyData] = useState(null);
  
  // Document Management State
  const [draggedDocIndex, setDraggedDocIndex] = useState(null);
  const [editingDocId, setEditingDocId] = useState(null);
  const [editingDocName, setEditingDocName] = useState('');
  const [documents, setDocuments] = useState([
    {
      id: 1,
      name: 'Abtretungserklärung_KH-011225-636.pdf',
      type: 'Abtretungserklärung',
      created: '1. Dez. 2025 11:23',
      modified: '1. Dez. 2025 11:23',
      size: '45,2 KB',
      url: '/sample.pdf',
      tags: ['Abtretungserklärung']
    },
    {
      id: 2,
      name: 'Lichtbilder_KH-011225-636.pdf',
      type: 'Lichtbilder',
      created: '1. Dez. 2025 12:08',
      modified: '1. Dez. 2025 12:08',
      size: '2,3 MB',
      url: '/sample.pdf',
      tags: ['Lichtbilder']
    },
    {
      id: 3,
      name: 'DAT-109276595-Kalkulation.pdf',
      type: 'Kalkulation',
      created: '1. Dez. 2025 14:27',
      modified: '1. Dez. 2025 14:27',
      size: '111,08 KB',
      url: '/sample.pdf',
      tags: ['Kalkulation', 'Hauptkalkulation'],
      isMain: true
    }
  ]);
  const [documentView, setDocumentView] = useState('list'); // 'list' or 'grid'
  const [documentSort, setDocumentSort] = useState('created');
  const [documentFilter, setDocumentFilter] = useState('all');
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    customer_first_name: '',
    customer_last_name: '',
    customer_name: '', // Für Kompatibilität behalten, wird automatisch befüllt
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    customer_plz_ort: '',
    vehicle_type: 'PKW',
    first_registration: '',
    vin: '',
    license_plate: '',
    manufacturer: '',
    model: '',
    year: new Date().getFullYear(),
    mileage: '',
    damage_location: 'windshield',
    damage_description: '',
    damageAreas: [],
    photoTab: 'photos',
    date_of_loss: new Date().toISOString().split('T')[0],
    photos: [],
    invoice_positions: [],
    // GDV Felder
    insurance_company: '',
    insurance_claim_number: '',
    insurance_policy_number: '',
    contract_begin: '',
    contract_end: '',
    insurance_po_name: '',
    insurance_po_telephone: '',
    insurance_po_fax: '',
    insurance_po_email: '',
    insurance_po_street: '',
    insurance_po_zip: '',
    insurance_po_city: '',
    manufacturer_name: '',
    type_name: '',
    registration_date: '',
    has_kasko: false,
    kasko_type: '',
    kasko_insurance_company: '',
    kasko_deductible: '',
    policy_number: '',
    claim_number: '',
    notes: '',
    // Auftragsdaten
    auftragstyp: 'austausch',
    vorsteuerabzugsberechtigt: 'nein',
    // Kalkulationsfelder
    material_cost: 0,
    labor_hours: 0,
    labor_rate: 85.00,
    labor_cost: 0,
    calibration_required: false,
    calibration_cost: 0,
    subtotal: 0,
    vat: 0,
    total: 0,
    // Abtretungserklärung
    abtretung_text: '',
    abtretung_date: new Date().toISOString().split('T')[0],
    abtretung_location: '',
    abtretung_signature: '',
    abtretung_signed: false
  });

  // Dynamisch Dokumente aus formData erstellen mit fester Reihenfolge
  useEffect(() => {
    const docsMap = {};
    
    // 1. Auftrags-PDF (order: 1)
    if (formData.auftrag_pdf_url) {
      docsMap['auftrag'] = {
        id: 'auftrag',
        name: `Auftrag_${caseNumber || 'NEU'}.pdf`,
        type: 'Auftrag',
        created: new Date().toLocaleDateString('de-DE'),
        modified: new Date().toLocaleDateString('de-DE'),
        url: `${getBackendUrl()}${formData.auftrag_pdf_url}`,
        tags: ['Auftrag', 'Versicherung'],
        order: 1
      };
    }
    
    // 2. Abtretungs-PDF (order: 2)
    if (formData.abtretung_pdf_url) {
      docsMap['abtretung'] = {
        id: 'abtretung',
        name: `Abtretung_${caseNumber || 'NEU'}.pdf`,
        type: 'Abtretungserklärung',
        created: new Date().toLocaleDateString('de-DE'),
        modified: new Date().toLocaleDateString('de-DE'),
        url: `${getBackendUrl()}${formData.abtretung_pdf_url}`,
        tags: ['Abtretungserklärung'],
        order: 2
      };
    }
    
    // 3. Lichtbilder-PDF (order: 3)
    if (formData.lichtbilder_pdf_url) {
      docsMap['lichtbilder'] = {
        id: 'lichtbilder',
        name: `Lichtbilder_${caseNumber || 'NEU'}.pdf`,
        type: 'Lichtbilder',
        created: new Date().toLocaleDateString('de-DE'),
        modified: new Date().toLocaleDateString('de-DE'),
        url: `${getBackendUrl()}${formData.lichtbilder_pdf_url}`,
        tags: ['Lichtbilder', `${formData.photos?.length || 0} Foto(s)`],
        order: 3
      };
    }
    
    // 4. Rechnung-PDF (order: 4)
    if (formData.invoice_pdf_url) {
      docsMap['rechnung'] = {
        id: 'rechnung',
        name: `Rechnung_${caseNumber || 'NEU'}.pdf`,
        type: 'Rechnung',
        created: new Date().toLocaleDateString('de-DE'),
        modified: new Date().toLocaleDateString('de-DE'),
        url: `${getBackendUrl()}${formData.invoice_pdf_url}`,
        tags: ['Rechnung'],
        order: 4
      };
    }
    
    // 5. DAT-Kalkulation (order: 5)
    if (formData.dat_pdf_url) {
      docsMap['dat'] = {
        id: 'dat',
        name: `DAT_Kalkulation_${caseNumber || 'NEU'}.pdf`,
        type: 'Kalkulation',
        created: new Date().toLocaleDateString('de-DE'),
        modified: new Date().toLocaleDateString('de-DE'),
        url: `${getBackendUrl()}${formData.dat_pdf_url}`,
        tags: ['Kalkulation', 'DAT'],
        isMain: true,
        order: 5
      };
    }
    
    // Sortiere nach order (Auftrag → Abtretung → Lichtbilder → Rechnung → DAT)
    const docs = Object.values(docsMap).sort((a, b) => a.order - b.order);
    
    setDocuments(docs);
  }, [formData.auftrag_pdf_url, formData.abtretung_pdf_url, formData.lichtbilder_pdf_url, formData.invoice_pdf_url, formData.dat_pdf_url, formData.photos, caseNumber, caseId]);


  // Auto-generate missing PDFs when entering Step 7
  useEffect(() => {
    if (currentStep !== 7 || !caseId) return;
    
    console.log('🔍 PDF-Check:', {
      step: currentStep,
      caseId: caseId,
      hasAbtretungPdf: !!formData.abtretung_pdf_url,
      hasSignature: !!formData.abtretung_signature,
      hasLichtbilderPdf: !!formData.lichtbilder_pdf_url,
      photosCount: formData.photos?.length || 0
    });
    
    const generateMissingPdfs = async () => {
      try {
        // 1. Generate Abtretung PDF if missing but has signature
        if (!formData.abtretung_pdf_url && formData.abtretung_signature) {
          console.log('🔄 Generiere fehlende Abtretungs-PDF...');
          const response = await fetch(
            `${getBackendUrl()}/api/cases/${caseId}/generate-abtretung-pdf`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          if (response.ok) {
            const result = await response.json();
            if (result.pdf_url) {
              setFormData(prev => ({
                ...prev,
                abtretung_pdf_url: result.pdf_url
              }));
              console.log('✅ Abtretungs-PDF erstellt');
            }
          }
        }
        
        // 2. Generate Lichtbilder PDF if missing but has photos
        if (!formData.lichtbilder_pdf_url && formData.photos && formData.photos.length > 0) {
          console.log('🔄 Generiere fehlende Lichtbilder-PDF...');
          
          // Speichere Fotos erst
          await fetch(`${getBackendUrl()}/api/cases/${caseId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              photos: formData.photos
            })
          });
          
          await new Promise(resolve => setTimeout(resolve, 300));
          
          const response = await fetch(
            `${getBackendUrl()}/api/cases/${caseId}/generate-lichtbilder-pdf`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          if (response.ok) {
            const result = await response.json();
            if (result.pdf_url) {
              setFormData(prev => ({
                ...prev,
                lichtbilder_pdf_url: result.pdf_url
              }));
              console.log('✅ Lichtbilder-PDF erstellt');
            }
          }
        }
      } catch (error) {
        console.error('Fehler beim Generieren fehlender PDFs:', error);
      }
    };
    
    generateMissingPdfs();
  }, [currentStep, caseId, formData.photos, formData.abtretung_signature, formData.abtretung_pdf_url, formData.lichtbilder_pdf_url]);

  const steps = [
    { id: 1, title: 'Auftrag', icon: FileText, testId: 'step-auftrag' },
    { id: 2, title: 'Versicherung', icon: Building, testId: 'step-versicherung' },
    { id: 3, title: 'Abtretungserklärung', icon: FileSignature, testId: 'step-abtretung' },
    { id: 4, title: 'Lichtbilder', icon: Camera, testId: 'step-lichtbilder' },
    { id: 5, title: 'Kalkulation', icon: Calculator, testId: 'step-kalkulation' },
    { id: 6, title: 'Übersicht', icon: FileText, testId: 'step-uebersicht' },
    { id: 7, title: 'Rechnung', icon: Receipt, testId: 'step-rechnung', showDividerBefore: true },
    { id: 8, title: 'Dokumentation', icon: FolderOpen, testId: 'step-dokumentation' },
    { id: 9, title: 'Email', icon: Mail, testId: 'step-email' },
  ];

  // Status-Berechnung für jeden Wizard-Step
  // Gibt zurück: 'empty' (grau), 'partial' (orange), 'complete' (grün mit Häkchen)
  const getStepStatus = (stepId) => {
    switch (stepId) {
      case 1: // Auftrag - Kundendaten & Fahrzeugdaten
        // Mindestens ein Feld ausgefüllt = partial (orange)
        const auftragAnyFields = ['customer_name', 'customer_vorname', 'customer_address', 'customer_plz_ort', 'license_plate', 'manufacturer', 'model'];
        // Wichtigste Felder für complete (grün) - Name + Kennzeichen + mindestens 2 weitere
        const auftragHasName = formData.customer_name?.toString().trim() && formData.customer_name !== 'Neuer Fall';
        const auftragHasPlate = formData.license_plate?.toString().trim() && formData.license_plate !== '-';
        const auftragFilledCount = auftragAnyFields.filter(f => formData[f]?.toString().trim() && formData[f] !== '-' && formData[f] !== 'Neuer Fall').length;
        
        if (auftragHasName && auftragHasPlate && auftragFilledCount >= 4) return 'complete';
        if (auftragFilledCount >= 1) return 'partial';
        return 'empty';

      case 2: // Versicherung
        const versicherungRequired = ['insurance_company'];
        const versicherungAll = ['insurance_company', 'insurance_policy_number', 'insurance_claim_number'];
        const versicherungRequiredFilled = versicherungRequired.every(f => formData[f]?.toString().trim());
        const versicherungAllFilled = versicherungAll.every(f => formData[f]?.toString().trim());
        if (versicherungAllFilled) return 'complete';
        if (versicherungRequiredFilled) return 'partial';
        return 'empty';

      case 3: // Abtretungserklärung
        const hasSignature = formData.abtretung_signature?.trim();
        const hasLocation = formData.abtretung_location?.trim();
        if (hasSignature && hasLocation) return 'complete';
        if (hasSignature || formData.abtretung_signed) return 'partial';
        return 'empty';

      case 4: // Lichtbilder
        const photosCount = formData.photos?.length || 0;
        if (photosCount >= 3) return 'complete';
        if (photosCount >= 1) return 'partial';
        return 'empty';

      case 5: // Kalkulation
        const hasPositions = (formData.invoice_positions?.length || 0) > 0;
        const hasTotal = formData.total > 0;
        if (hasPositions && hasTotal) return 'complete';
        if (hasPositions || hasTotal) return 'partial';
        return 'empty';

      case 6: // Übersicht - immer complete wenn vorherige Steps teilweise gefüllt
        const step1Status = getStepStatus(1);
        const step2Status = getStepStatus(2);
        if (step1Status !== 'empty' && step2Status !== 'empty') return 'complete';
        if (step1Status !== 'empty' || step2Status !== 'empty') return 'partial';
        return 'empty';

      case 7: // Rechnung
        const hasInvoicePdf = formData.invoice_pdf_url?.trim();
        if (hasInvoicePdf) return 'complete';
        const hasInvoicePositions = (formData.invoice_positions?.length || 0) > 0;
        if (hasInvoicePositions) return 'partial';
        return 'empty';

      case 8: // Dokumentation
        const docsCount = documents?.filter(d => d.url)?.length || 0;
        if (docsCount >= 2) return 'complete';
        if (docsCount >= 1) return 'partial';
        return 'empty';

      case 9: // Email - kein Status nötig, immer leer
        return 'empty';

      default:
        return 'empty';
    }
  };

  // Load existing case if edit mode
  // Load company data for Firmenstempel
  useEffect(() => {
    const loadCompanyData = async () => {
      try {
        const response = await fetch(`${getBackendUrl()}/api/company`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setCompanyData(data);
        }
      } catch (error) {
        console.error('Error loading company data:', error);
      }
    };
    loadCompanyData();
  }, []);

  // Load company settings
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const response = await fetch(`${getBackendUrl()}/api/settings/company`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setCompanySettings(data || {});
        }
      } catch (error) {
        console.error('Error loading company settings:', error);
      }
    };
    fetchCompanySettings();
    
    // Load invoice catalog
    const fetchInvoiceCatalog = async () => {
      try {
        const response = await fetch(`${getBackendUrl()}/api/invoice-items`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setInvoiceCatalog(data.items || []);
        }
      } catch (error) {
        console.error('Error loading invoice catalog:', error);
      }
    };
    fetchInvoiceCatalog();
  }, []);
  
  // Load custom items when case is loaded
  useEffect(() => {
    if (formData.custom_invoice_items) {
      setCustomInvoiceItems(formData.custom_invoice_items);
    }
  }, [formData.custom_invoice_items]);
  
  // Load DAT positions when Step 7 is opened
  useEffect(() => {
    if (currentStep === 7 && caseId && !datPositions.length) {
      extractDatPositions();
    }
  }, [currentStep, caseId]);
  
  // Generate invoice PDF when Step 8 (Dokumentation) is opened (only if not already generated)
  useEffect(() => {
    if (currentStep === 8 && caseId && !formData.invoice_pdf_url) {
      generateInvoicePDFAutomatically();
    }
  }, [currentStep, caseId, formData.invoice_pdf_url]);
  
  const generateInvoicePDFAutomatically = async () => {
    if (!caseId) return;
    
    try {
      const response = await fetch(
        `${getBackendUrl()}/api/cases/${caseId}/generate-invoice-pdf`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Rechnung PDF automatisch erstellt:', data.pdf_url);
        
        // Update formData to trigger automatic document sorting
        setFormData(prev => ({
          ...prev,
          invoice_pdf_url: data.pdf_url
        }));
      } else {
        console.warn('⚠️ Rechnung PDF konnte nicht generiert werden, aber Fall ist gespeichert');
      }
    } catch (error) {
      console.error('❌ Error auto-generating invoice PDF:', error);
      // Kein Toast - Die Rechnung wird beim nächsten Mal generiert
    }
  };
  
  // Extract DAT positions
  const extractDatPositions = async () => {
    if (!caseId) return;
    
    setLoadingDatPositions(true);
    try {
      const response = await fetch(
        `${getBackendUrl()}/api/cases/${caseId}/extract-dat-positions`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setDatPositions(data.positions || []);
        
        if (data.count > 0) {
          toast.success(`${data.count} Positionen aus DAT-Kalkulation übernommen`);
          
          // Automatisch Gesamtbetrag berechnen
          calculateTotalsFromPositions(data.positions);
        }
      }
    } catch (error) {
      console.error('Error extracting DAT positions:', error);
    } finally {
      setLoadingDatPositions(false);
    }
  };
  
  // Berechne Gesamtbetrag aus Positionen
  const calculateTotalsFromPositions = (positions = null, customItems = null) => {
    // Use provided parameters or fall back to state
    const positionsToUse = positions !== null ? positions : datPositions;
    const customItemsToUse = customItems !== null ? customItems : customInvoiceItems;
    
    // Summe aller Positionen (DAT + Custom)
    const allPositions = [...positionsToUse, ...customItemsToUse];
    
    // Zwischensumme (Netto)
    const subtotal = allPositions.reduce((sum, item) => {
      return sum + (item.total_price || 0);
    }, 0);
    
    // MwSt berechnen (Standard 19%)
    const taxRate = formData.tax_rate || 19;
    const taxAmount = subtotal * (taxRate / 100);
    
    // Gesamtbetrag (Brutto)
    const total = subtotal + taxAmount;
    
    // FormData aktualisieren
    setFormData(prev => ({
      ...prev,
      total_netto: subtotal,
      tax_rate: taxRate,
      tax_amount: taxAmount,
      total: total
    }));
    
    // Auch in Datenbank speichern
    if (caseId) {
      updateCase({
        total_netto: subtotal,
        tax_amount: taxAmount,
        total: total
      });
    }
  };
  
  // Handle description input for autocomplete
  const handleDescriptionChange = (value) => {
    setNewItem({...newItem, description: value});
    
    if (value.length > 1) {
      const filtered = invoiceCatalog.filter(item =>
        item.description.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSuggestions(filtered);
    } else {
      setFilteredSuggestions([]);
    }
  };
  
  // Select suggestion
  const selectSuggestion = (item) => {
    setNewItem({
      description: item.description,
      quantity: 1,
      unit_price: item.unit_price,
      unit: item.unit
    });
    setFilteredSuggestions([]);
  };
  
  // Add custom item
  const addCustomItem = async () => {
    if (!newItem.description || !newItem.unit_price) {
      toast.error('Bitte Beschreibung und Preis eingeben');
      return;
    }
    
    try {
      const response = await fetch(`${getBackendUrl()}/api/cases/${caseId}/add-custom-item`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newItem)
      });
      
      if (response.ok) {
        const data = await response.json();
        const updatedItems = [...customInvoiceItems, data.item];
        setCustomInvoiceItems(updatedItems);
        setFormData({...formData, custom_invoice_items: updatedItems});
        setShowAddItemModal(false);
        setNewItem({ description: '', quantity: 1, unit_price: 0, unit: 'Stk' });
        toast.success('Position hinzugefügt');
        
        // Automatisch Gesamtbetrag neu berechnen mit aktualisierter Liste
        calculateTotalsFromPositions(datPositions, updatedItems);
      } else {
        toast.error('Fehler beim Hinzufügen');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };
  
  // Delete custom item
  const deleteCustomItem = async (itemId) => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/cases/${caseId}/custom-item/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const updatedItems = customInvoiceItems.filter(item => item.id !== itemId);
        setCustomInvoiceItems(updatedItems);
        setFormData({...formData, custom_invoice_items: updatedItems});
        toast.success('Position gelöscht');
        
        // Automatisch Gesamtbetrag neu berechnen mit aktualisierter Liste
        calculateTotalsFromPositions(datPositions, updatedItems);
      } else {
        toast.error('Fehler beim Löschen');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  // Track completed steps based on form data
  useEffect(() => {
    const completed = [];
    
    // Step 1: Auftrag - has customer name
    if (formData.customer_name || formData.license_plate) completed.push(1);
    
    // Step 2: Versicherung - has insurance or date of loss
    if (formData.insurance_company || formData.date_of_loss) completed.push(2);
    
    // Step 3: Abtretung - has signature
    if (formData.signature) completed.push(3);
    
    // Step 4: Lichtbilder - has photos
    if (formData.photos && formData.photos.length > 0) completed.push(4);
    
    // Step 5: Kalkulation - has calculation data
    if (datPositions.length > 0 || customInvoiceItems.length > 0 || formData.total > 0) completed.push(5);
    
    // Step 6: Übersicht - always available if previous steps have data
    if (completed.length >= 2) completed.push(6);
    
    // Step 7: Rechnung - has invoice
    if (formData.invoice_generated) completed.push(7);
    
    // Step 8: Dokumentation - has documents
    if (formData.documentation_complete) completed.push(8);
    
    // Step 9: Email - email sent
    if (formData.email_sent) completed.push(9);
    
    setCompletedSteps(completed);
  }, [formData, datPositions, customInvoiceItems]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const editCaseId = searchParams.get('edit') || searchParams.get('id'); // Support both 'edit' and 'id' parameters
    const auftragstyp = searchParams.get('auftragstyp');
    const isNewCase = searchParams.get('new');
    
    // If "new" parameter is present, always create a new case
    if (isNewCase) {
      // Reset the creation flag for new case
      caseCreationInProgress.current = false;
      
      // Reset all state for a fresh case
      setIsEditMode(false);
      setCaseId(null);
      setCurrentStep(1);
      setDatPositions([]);
      setCustomInvoiceItems([]);
      setFormData({
        auftragstyp: auftragstyp || 'austausch',
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        customer_address: '',
        customer_plz_ort: '',
        vin: '',
        license_plate: '',
        manufacturer: '',
        model: '',
        year: new Date().getFullYear(),
        mileage: '',
        damage_location: 'windshield',
        damage_type: 'crack',
        damage_description: '',
        severity: 3,
        insurance_company: '',
        insurance_claim_number: '',
        photos: [],
        invoice_positions: [],
        material_cost: 0,
        labor_cost: 0,
        calibration_cost: 0,
        subtotal: 0,
        vat: 0,
        total: 0,
        total_netto: 0,
        tax_amount: 0,
        tax_rate: 19,
        status: 'neu',
        date_of_loss: new Date().toISOString().split('T')[0]
      });
      
      // Create a new case in the backend
      createInitialCase();
      return;
    }
    
    // Set auftragstyp from URL parameter if provided
    if (auftragstyp && ['gutachten', 'reparatur', 'austausch'].includes(auftragstyp)) {
      setFormData(prev => ({
        ...prev,
        auftragstyp: auftragstyp
      }));
    }
    
    if (editCaseId) {
      console.log('🔄 Lade bestehenden Fall:', editCaseId);
      setIsEditMode(true);
      loadExistingCase(editCaseId);
    } else if (!caseId) {
      // Only create if no caseId exists yet
      createInitialCase();
    }
  }, [location.search]); // Changed dependency to location.search only

  const loadExistingCase = async (caseId) => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/cases/${caseId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const caseData = await response.json();
        
        // Populate form with existing case data
        setFormData({
          // Customer data
          customer_name: caseData.customer_name || '',
          customer_email: caseData.customer_email || '',
          customer_phone: caseData.customer_phone || '',
          customer_address: caseData.customer_address || '',
          customer_plz_ort: caseData.customer_plz_ort || '', // PLZ und Ort
          
          // Vehicle data
          vin: caseData.vin || caseData.vehicle?.vin || '',
          license_plate: caseData.license_plate || caseData.vehicle?.license_plate || '',
          manufacturer: caseData.manufacturer || caseData.vehicle?.manufacturer || '',
          model: caseData.model || caseData.vehicle?.model || '',
          year: caseData.year || caseData.vehicle?.year || new Date().getFullYear(),
          mileage: caseData.vehicle?.mileage || '',
          
          // Damage data
          damage_location: caseData.damage_location || caseData.damage?.location || 'windshield',
          damage_type: caseData.damage?.damage_type || 'crack',
          damage_description: caseData.damage_description || caseData.damage?.description || '',
          severity: caseData.damage?.severity || 3,
          
          // Insurance
          insurance_company: caseData.insurance_company || '',
          insurance_claim_number: caseData.insurance_claim_number || '',
          
          // Photos
          photos: caseData.photos || [],
          
          // Invoice
          invoice_positions: caseData.calculation?.parts || [],
          material_cost: caseData.calculation?.material_cost || 0,
          labor_cost: caseData.calculation?.labor_cost || 0,
          calibration_cost: caseData.calculation?.calibration_cost || 0,
          subtotal: caseData.calculation?.subtotal || 0,
          vat: caseData.calculation?.vat || 0,
          total: caseData.total || caseData.calculation?.total || 0,
          
          // DAT PDF (neu)
          dat_pdf_url: caseData.dat_pdf_url || '',
          dat_pdf_base64: caseData.dat_pdf_base64 || '',
          
          // Dokumente PDFs
          auftrag_pdf_url: caseData.auftrag_pdf_url || '',
          abtretung_pdf_url: caseData.abtretung_pdf_url || '',
          lichtbilder_pdf_url: caseData.lichtbilder_pdf_url || '',
          
          // Abtretungs-Felder
          abtretung_text: caseData.abtretung_text || '',
          abtretung_signature: caseData.abtretung_signature || '',
          // Wenn Unterschrift-URL existiert (startet mit /api/), setze abtretung_signed auf true
          abtretung_signed: caseData.abtretung_signed || (caseData.abtretung_signature && caseData.abtretung_signature.startsWith('/api/')) || false,
          abtretung_date: caseData.abtretung_date || new Date().toISOString().split('T')[0],
          // Ort automatisch aus PLZ/Ort extrahieren (z.B. "12345 Berlin" → "Berlin")
          abtretung_location: caseData.abtretung_location || (caseData.customer_plz_ort ? caseData.customer_plz_ort.split(' ').slice(1).join(' ') : ''),
          
          // DEBUG: Log Abtretung beim Laden
          ...(console.log('📥 Fall geladen - Abtretung:', {
            hasSignature: !!caseData.abtretung_signature,
            signatureLength: caseData.abtretung_signature?.length || 0,
            signed: caseData.abtretung_signed
          }) || {}),
          
          // Zusätzliche Felder für Abtretung
          vehicle_type: caseData.vehicle_type || 'PKW',
          first_registration: caseData.first_registration || '',
          policy_number: caseData.policy_number || '',
          claim_number: caseData.claim_number || '',
          kasko_deductible: caseData.kasko_deductible || '',
          
          // DAT Verrechnungssätze (neu)
          dat_plz_stundensatz: caseData.dat_plz_stundensatz || '',
          dat_lackierer_stundensatz: caseData.dat_lackierer_stundensatz || '',
          dat_lackiermethode: caseData.dat_lackiermethode || 'lack-hersteller-system',
          dat_lackmaterial: caseData.dat_lackmaterial || '',
          dat_farbauszug: caseData.dat_farbauszug || 'metallic-2-schicht',
          dat_farbmischungen: caseData.dat_farbmischungen || '1',
          dat_musterbleche: caseData.dat_musterbleche || '1',
          dat_ersatzteile_zuschlag: caseData.dat_ersatzteile_zuschlag || '',
          dat_kleinmaterial_prozent: caseData.dat_kleinmaterial_prozent || '',
          dat_montageteile_lackieren: caseData.dat_montageteile_lackieren || false,
          
          // Notes
          notes: caseData.notes || ''
        });
        
        setCaseId(caseData._id);
        setCaseNumber(caseData.case_number);
        
        // Load DAT Contract ID if present
        if (caseData.dat_contract_id) {
          setDatContractId(caseData.dat_contract_id);
          console.log('✅ DAT Contract ID geladen:', caseData.dat_contract_id);
        }
        
        // Load invoice positions from DAT if available
        if (caseData.invoice_positions && caseData.invoice_positions.length > 0) {
          setDatPositions(caseData.invoice_positions);
          console.log('✅ DAT-Positionen geladen:', caseData.invoice_positions.length);
        }
        
        toast.success(`Akte ${caseData.case_number} geladen zur Bearbeitung`);
      } else {
        toast.error('Akte konnte nicht geladen werden');
        navigate('/cases');
      }
    } catch (error) {
      console.error('Error loading case:', error);
      toast.error('Fehler beim Laden der Akte');
      navigate('/cases');
    }
  };

  // Create initial case on mount
  const createInitialCase = async () => {
      // Use sessionStorage to prevent double creation across StrictMode re-renders
      const searchParams = new URLSearchParams(location.search);
      const newParam = searchParams.get('new');
      const lockKey = `case_creation_lock_${newParam}`;
      
      // Check if case creation is already in progress for this "new" parameter
      if (sessionStorage.getItem(lockKey) || caseCreationInProgress.current) {
        console.log('⏭️ Case-Erstellung übersprungen (Lock aktiv)');
        return;
      }
      
      // Set both locks
      sessionStorage.setItem(lockKey, 'true');
      caseCreationInProgress.current = true;
      
      try {
        // Use original fetch to avoid rrweb fetch override issues
        const fetchFn = window.__originalFetch || fetch;
        
        const initialCaseData = {
          customer_name: '',  // Leer lassen - Aktennummer wird erst generiert wenn Name eingegeben
          customer_email: null,
          customer_phone: null,
          customer_address: null,
          vehicle: {
            vin: '',
            license_plate: '',  // Leer lassen - Aktennummer wird erst nach Fahrzeugschein-Scan generiert
            manufacturer: '',
            model: '',
            year: new Date().getFullYear(),
            mileage: null
          },
          damage: {
            location: 'windshield',
            damage_type: 'crack',
            severity: 3,
            description: 'In Bearbeitung',
            photos: []
          },
          calculation: null,
          insurance_company: null,
          insurance_claim_number: null,
          notes: null,
          is_draft: true  // Akte ist Entwurf bis Fahrzeugschein hochgeladen wird
        };

        const response = await fetchFn(getBackendUrl() + '/api/cases', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(initialCaseData)
        });

        if (response.ok) {
          const result = await response.json();
          setCaseId(result._id);
          setCaseNumber(result.case_number);
          // Keine Toast-Nachricht mehr - erst nach Fahrzeugschein-Scan
          console.log(`📋 Entwurf erstellt: ${result.case_number}`);
          
          // Clear the lock after 5 seconds (enough time for the component to stabilize)
          setTimeout(() => {
            sessionStorage.removeItem(lockKey);
          }, 5000);
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('❌ Fehler beim Erstellen der Akte:', response.status, errorData);
          toast.error('Fehler beim Erstellen der Akte. Bitte versuchen Sie es erneut.');
          sessionStorage.removeItem(lockKey);
          caseCreationInProgress.current = false;
        }
      } catch (error) {
        console.error('❌ Fehler beim Erstellen der Akte:', error);
        toast.error('Verbindungsfehler. Bitte versuchen Sie es erneut.');
        sessionStorage.removeItem(lockKey);
        caseCreationInProgress.current = false;
      }
    };

  // Auto-save function
  const autoSaveCase = async () => {
    if (!caseId) return;
    
    setAutoSaving(true);
    try {
      // DEBUG: Log Abtretung Daten
      console.log('💾 Auto-Save Abtretung Debug:', {
        hasSignature: !!formData.abtretung_signature,
        signatureLength: formData.abtretung_signature?.length || 0,
        signed: formData.abtretung_signed,
        textLength: formData.abtretung_text?.length || 0
      });
      
      // NUR Abtretungs-Felder senden wenn sie tatsächlich Inhalt haben
      // WICHTIG: Signatur wird NIEMALS durch Auto-Save gesendet!
      // Signatur wird ausschließlich in handleAbtretungSignatureEnd gespeichert
      const abtretungData = {};
      if (formData.abtretung_text) {
        abtretungData.abtretung_text = formData.abtretung_text;
      }
      if (formData.abtretung_date) {
        abtretungData.abtretung_date = formData.abtretung_date;
      }
      if (formData.abtretung_location) {
        abtretungData.abtretung_location = formData.abtretung_location;
      }
      // SIGNATUR wird EXPLIZIT NICHT gesendet - verhindert Race Conditions!
      
      const caseData = {
        customer_name: formData.customer_name || '',  // Leer lassen wenn kein Name
        customer_first_name: formData.customer_first_name || '',  // Vorname separat
        customer_last_name: formData.customer_last_name || '',    // Nachname separat
        customer_email: formData.customer_email || null,
        customer_phone: formData.customer_phone || null,
        customer_address: formData.customer_address || null,
        vehicle: {
          vin: formData.vin || '',
          license_plate: formData.license_plate || '',  // Leer für Aktennummer-Generierung nach Scan
          manufacturer: formData.manufacturer || '',
          model: formData.model || '',
          year: parseInt(formData.year) || new Date().getFullYear(),
          mileage: parseInt(formData.mileage) || null
        },
        damage: {
          location: formData.damage_location || 'windshield',
          damage_type: formData.damage_type || 'crack',
          severity: parseInt(formData.severity) || 3,
          description: formData.damage_description || 'In Bearbeitung',
          photos: []
        },
        photos: formData.photos || [],
        calculation: formData.total ? {
          material_cost: parseFloat(formData.material_cost) || 0,
          labor_cost: parseFloat(formData.labor_cost) || 0,
          total: parseFloat(formData.total) || 0,
          vat: parseFloat(formData.vat) || 0
        } : null,
        insurance_company: formData.insurance_company || null,
        insurance_claim_number: formData.insurance_claim_number || null,
        notes: formData.notes || null,
        ...abtretungData,  // Nur senden wenn Daten vorhanden sind
        customer_plz_ort: formData.customer_plz_ort || null,
        vehicle_type: formData.vehicle_type || null,
        first_registration: formData.first_registration || null,
        policy_number: formData.policy_number || null,
        claim_number: formData.claim_number || null,
        kasko_deductible: formData.kasko_deductible || null
      };

      const response = await fetch(`${getBackendUrl()}/api/cases/${caseId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(caseData)
      });

      if (response.ok) {
      }
    } catch (error) {
      console.error('❌ Auto-Save Fehler:', error);
    } finally {
      setAutoSaving(false);
    }
  };

  // Auto-save when formData changes (debounced)
  // WICHTIG: Signatur wird NICHT durch Auto-Save gespeichert (separater Handler)
  useEffect(() => {
    if (!caseId) return;
    
    const timeoutId = setTimeout(() => {
      autoSaveCase();
    }, 5000); // Auto-save 5 seconds after last change (increased for better performance)

    return () => clearTimeout(timeoutId);
  }, [
    formData.customer_name,
    formData.customer_email,
    formData.customer_phone,
    formData.customer_address,
    formData.vin,
    formData.license_plate,
    formData.manufacturer,
    formData.model,
    formData.year,
    formData.insurance_company,
    formData.abtretung_text,
    formData.abtretung_date,
    formData.abtretung_location,
    // EXPLIZIT NICHT: abtretung_signature (wird separat in handleAbtretungSignatureEnd gespeichert)
    caseId
  ]);

  // Auto-start DAT calculation when entering step 5 - DEAKTIVIERT (jetzt manuell via Button)
  useEffect(() => {
    // DEAKTIVIERT - Benutzer startet manuell via Button
    return;
    
    // Debug log
    console.log('🔍 Auto-Start Check (Step 5):', {
      currentStep,
      hasResult: !!datCalculationResult,
      isLoading: loadingCalculation,
      hasVin: !!formData.vin,
      hasCustomer: !!formData.customer_name,
      vin: formData.vin,
      customer: formData.customer_name
    });
    
    // Start DAT calc if: on step 5, no result yet, not loading, and have VIN OR customer_name
    if (currentStep === 5 && !datCalculationResult && !loadingCalculation && (formData.vin || formData.customer_name)) {
      // Automatically start DAT calculation
      const startDatCalculation = async () => {
        setLoadingCalculation(true);
        setCalculationError(null);
        
        try {
          console.log('🚀 Auto-Start: DAT-Kalkulation wird geladen...');
          const response = await fetch(
            `${getBackendUrl()}/api/dat/generate-iframe-url`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: JSON.stringify({
                case_number: caseNumber || '',  // Aktennummer für DAT
                vin: formData.vin,
                license_plate: formData.license_plate || '',
                manufacturer: formData.manufacturer || '',
                model: formData.model || '',
                damage_location: formData.damage_location,
                damage_description: formData.damage_description || 'Fahrzeugschaden - Siehe Schadenbeschreibung',
                customer_name: formData.customer_name || 'Unbekannter Kunde',
                // Additional customer data
                customer_address: formData.customer_address || '',
                customer_city: formData.customer_plz_ort ? formData.customer_plz_ort.split(' ').slice(1).join(' ') : '',
                customer_zip: formData.customer_plz_ort ? formData.customer_plz_ort.split(' ')[0] : '',
                customer_phone: formData.customer_phone || '',
                customer_email: formData.customer_email || '',
                mileage: formData.mileage || '',
                first_registration: formData.first_registration || '',
                // Verrechnungssätze aus Schritt 5 (NEU)
                dat_plz_stundensatz: formData.dat_plz_stundensatz || '',
                dat_lackierer_stundensatz: formData.dat_lackierer_stundensatz || '',
                dat_lackiermethode: formData.dat_lackiermethode || '',
                dat_lackmaterial: formData.dat_lackmaterial || '',
                dat_farbauszug: formData.dat_farbauszug || '',
                dat_farbmischungen: formData.dat_farbmischungen || '',
                dat_musterbleche: formData.dat_musterbleche || '',
                dat_ersatzteile_zuschlag: formData.dat_ersatzteile_zuschlag || '',
                dat_kleinmaterial_prozent: formData.dat_kleinmaterial_prozent || '',
                dat_montageteile_lackieren: formData.dat_montageteile_lackieren || false
              })
            }
          );
          
          if (response.ok) {
            const data = await response.json();
            console.log('✅ DAT iFrame geladen:', data);
            
            if (data.success && data.iframe_url) {
              // Store contract ID
              if (data.contract_id) {
                setDatContractId(data.contract_id);
                toast.success(`✅ Schadenauftrag erstellt! Contract-ID: ${data.contract_id}`);
              }
              
              // Store calculation result
              setDatCalculationResult(data);
              toast.success('🚗 DAT myClaim Kalkulationsmaske wird geladen...');
            } else {
              setCalculationError(data.message || 'Fehler beim Laden der Kalkulation');
              toast.error(`❌ ${data.message || 'Konnte DAT nicht laden'}`);
            }
          } else {
            const error = await response.json();
            console.error('❌ API-Fehler:', error);
            setCalculationError(error.detail || 'Fehler beim Laden der Kalkulation');
            toast.error(`❌ ${error.detail || 'Konnte Kalkulation nicht laden'}`);
          }
        } catch (error) {
          console.error('❌ Netzwerkfehler:', error);
          setCalculationError('Verbindungsfehler zum Server');
          toast.error('Verbindungsfehler beim Laden der Kalkulation');
        } finally {
          setLoadingCalculation(false);
        }
      };
      
      startDatCalculation();
    }
  }, [currentStep, datCalculationResult, loadingCalculation, formData.vin, formData.license_plate, formData.manufacturer, formData.model, formData.damage_location, formData.damage_description, formData.customer_name, caseNumber]);


  // Helper function to generate the Abtretungstext with form data
  const generateAbtretungsTextFromForm = () => {
    const kundenname = formData.customer_name || '[Kundenname]';
    const kennzeichen = formData.license_plate || '[Kennzeichen]';
    const marke = formData.manufacturer || '[Marke]';
    const modell = formData.model || '[Modell]';
    const kmStand = formData.mileage || '[KM-Stand]';
    const schadentag = formData.date_of_loss || '[Schadentag]';
    const versicherung = formData.insurance_company || '[Versicherung]';
    const werkstattName = user?.company_name || '[Werkstattname]';
    const schadensort = formData.damage_location === 'windshield' ? 'Frontscheibe' : 
                        formData.damage_location === 'rear' ? 'Heckscheibe' :
                        formData.damage_location === 'side_left' ? 'Seitenscheibe links' :
                        formData.damage_location === 'side_right' ? 'Seitenscheibe rechts' : 'Frontscheibe';
    const schadensart = formData.damage_type === 'crack' ? 'Riss' : 
                        formData.damage_type === 'chip' ? 'Steinschlag' : 'Bruch';
    
    return `SCHADENS- UND ABTRETUNGSERKLÄRUNG
Autoglas / Fahrzeuginstandsetzung

1. ANGABEN ZUM FAHRZEUG UND SCHADEN
Kunde: ${kundenname} | Kennzeichen: ${kennzeichen} | Fahrzeug: ${marke} ${modell}
Kilometerstand: ${kmStand} km | Schadentag: ${schadentag} | Versicherung: ${versicherung}

2. SCHADENSBESCHREIBUNG
Am oben genannten Fahrzeug ist am ${schadentag} während der Fahrt an der ${schadensort} ein Glasschaden entstanden. Die ${schadensort} weist einen ${schadensart} auf, der die Sicht des Fahrers, die Stabilität der Scheibe sowie die Verkehrssicherheit des Fahrzeugs beeinträchtigt. Der Schaden macht eine Reparatur bzw. den Austausch der ${schadensort} zwingend erforderlich. Der Kunde bestätigt hiermit ausdrücklich, dass der Schaden durch eine äußere Einwirkung (z. B. Steinschlag) während der Fahrt entstanden ist und kein Vorschaden bekannt ist.

3. REPARATURAUFTRAG & KOSTENÜBERNAHME
Der Kunde beauftragt die ausführende Autoglas-Werkstatt ${werkstattName} mit der Durchführung der erforderlichen Glasreparatur bzw. dem Austausch der ${schadensort} nach den anerkannten Regeln der Technik. Der Kunde verpflichtet sich, die Reparaturkosten zu tragen, soweit diese nicht oder nicht vollständig durch die Versicherung übernommen werden.

4. ABTRETUNGSERKLÄRUNG (erfüllungshalber)
Der Unterzeichner tritt hiermit seine Ansprüche aus dem oben genannten Schadenereignis auf Erstattung der Reparatur- bzw. Austauschkosten erfüllungshalber und unwiderruflich gegenüber der zuständigen Versicherungsgesellschaft ${versicherung} an die ${werkstattName} ab. Die Werkstatt ist berechtigt, diese Abtretung offenzulegen und die abgetretenen Ansprüche im eigenen Namen außergerichtlich und gerichtlich geltend zu machen sowie direkt mit der Versicherung abzurechnen.

5. SELBSTBETEILIGUNG & ZAHLUNGSVERPFLICHTUNG
Sofern im Rahmen der Kaskoversicherung eine Selbstbeteiligung vereinbart ist, verpflichtet sich der Kunde, diesen Betrag nach Rechnungsstellung unmittelbar an die Werkstatt zu zahlen. Dies gilt ebenfalls, falls die Versicherung die Regulierung teilweise oder vollständig verweigert oder einzelne Positionen nicht übernimmt.

6. VERJÄHRUNG
Ansprüche aus diesem Reparaturauftrag verjähren gemäß §§ 195, 199 BGB in drei (3) Jahren, beginnend mit dem Schluss des Jahres, in dem der Anspruch entstanden ist.

7. SCHLUSSBESTIMMUNGEN
Sollten einzelne Bestimmungen dieser Erklärung ganz oder teilweise unwirksam sein oder werden, bleibt die Wirksamkeit der übrigen Regelungen unberührt.`;
  };

  // 🎯 Auto-load Standard-Vorlage for Abtretungserklärung when entering Step 3
  useEffect(() => {
    if (currentStep === 3 && !formData.abtretung_text) {
      // Automatically load the standard template
      const defaultText = generateAbtretungsTextFromForm();
      setFormData(prev => ({ ...prev, abtretung_text: defaultText }));
      console.log('✅ Standard-Vorlage automatisch geladen');
    }
  }, [currentStep, formData.abtretung_text, formData.customer_name, formData.license_plate, formData.manufacturer, formData.model, formData.date_of_loss, formData.damage_location, formData.damage_type, formData.insurance_company, formData.insurance_policy_number, formData.insurance_number, formData.mileage, user]);

  // Automatically fetch DAT calculation when entering Step 6 (Rechnung) with intelligent polling
  useEffect(() => {
    const contractId = datContractId || datCalculationResult?.contract_id;
    
    console.log('🔍 Step 5 Auto-Import Check:', {
      currentStep,
      contractId,
      hasPositions: formData.invoice_positions.length > 0,
      positionsCount: formData.invoice_positions.length
    });
    
    // Only start polling if in Step 6, have a contract ID, and no positions yet
    if (currentStep !== 6 || !contractId || formData.invoice_positions.length > 0) {
      console.log('⏭️ Polling nicht gestartet - Bedingungen nicht erfüllt');
      return;
    }
    
    console.log('🔄 Starting automatic DAT calculation polling for Contract:', contractId);
    toast.info('⏳ Lade DAT-Kalkulationsdaten...', { duration: 3000 });
    
    let pollCount = 0;
    const maxPolls = 12; // Maximum 12 attempts (1 minute total with 5 second intervals)
    let pollingInterval = null;
    
    const fetchDatCalculation = async () => {
      try {
        pollCount++;
        console.log(`🔍 Poll attempt ${pollCount}/${maxPolls} for Contract ${contractId}`);
        
        const response = await fetch(
          `${getBackendUrl()}/api/dat/fetch-calculation`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              contract_id: String(contractId)
            })
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.positions && data.positions.length > 0) {
            // SUCCESS: Data found and imported
            console.log('✅ DAT Calculation data found:', data);
            console.log('📋 Raw positions from API:', data.positions);
            
            // Transform positions to invoice format matching the app's structure
            // The app uses: id, bezeichnung, menge, einzelpreis, gesamt
            const invoicePositions = data.positions.map((pos, index) => ({
              id: Date.now() + index, // Generate unique ID
              description: pos.description || 'Position',
              quantity: parseFloat(pos.quantity) || 1,
              price: parseFloat(pos.unit_price) || 0,
              total: parseFloat(pos.total) || 0
            }));
            
            console.log('🔄 Transformed positions:', invoicePositions);
            
            // Calculate totals
            const subtotal = invoicePositions.reduce((sum, p) => sum + (p.total || 0), 0);
            const vat = subtotal * 0.19;
            const total = subtotal + vat;
            
            console.log('💰 Berechnung:', { subtotal, vat, total });
            console.log('💾 Setze FormData mit Positionen...');
            
            setFormData(prev => ({
              ...prev,
              invoice_positions: invoicePositions,
              subtotal: subtotal,
              vat: vat,
              total: total
            }));
            
            console.log('✅ FormData updated!');
            toast.success(`✅ ${data.positions.length} Positionen automatisch aus DAT übernommen!`, { duration: 5000 });
            console.log('✅ Invoice positions imported:', invoicePositions);
            
            // Stop polling - we're done!
            if (pollingInterval) {
              clearInterval(pollingInterval);
              console.log('✅ Polling stopped - data successfully imported');
            }
          } else {
            // No data yet - continue polling
            console.log(`⏳ Poll ${pollCount}: Noch keine Daten verfügbar, warte weiter...`);
            
            // Show info message only on first poll
            if (pollCount === 1) {
              toast.info(
                '⏳ Warte auf DAT-Kalkulationsdaten...\n\n' +
                'Die Daten werden automatisch übernommen, sobald die Kalkulation in DAT gespeichert wurde.',
                { duration: 5000 }
              );
            }
            
            // Stop polling after max attempts
            if (pollCount >= maxPolls && pollingInterval) {
              clearInterval(pollingInterval);
              console.log('⏱️ Polling stopped - max attempts reached');
              toast.warning(
                '⚠️ Keine DAT-Kalkulationsdaten gefunden.\n\n' +
                '➜ Stellen Sie sicher, dass die Kalkulation im DAT iFrame gespeichert wurde\n' +
                '➜ Klicken Sie dann auf "DAT Kalkulation übernehmen" oben rechts',
                { duration: 8000 }
              );
            }
          }
        }
      } catch (error) {
        console.error('❌ Polling fetch error:', error);
      }
    };
    
    // Initial fetch immediately
    fetchDatCalculation();
    
    // Then poll every 5 seconds
    pollingInterval = setInterval(fetchDatCalculation, 5000);
    
    // Cleanup function to stop polling when component unmounts or dependencies change
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        console.log('🛑 Polling cleanup - interval cleared');
      }
    };
  }, [currentStep, datContractId, datCalculationResult, formData.invoice_positions.length]);

  // Check if email is configured when entering Step 9
  useEffect(() => {
    if (currentStep === 9) {
      checkEmailConfiguration();
      // Set insurance email as default recipient if available, otherwise customer email
      const defaultRecipient = formData.insurance_po_email || formData.customer_email || '';
      if (defaultRecipient) {
        setEmailData(prev => ({ ...prev, to: defaultRecipient }));
      }
    }
  }, [currentStep, formData.insurance_po_email, formData.customer_email]);

  const checkEmailConfiguration = async () => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/email/config`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEmailConfigured(data.configured);
        
        if (!data.configured) {
          toast.error('⚠️ Email nicht konfiguriert. Bitte konfigurieren Sie Ihre Email-Einstellungen unter "Einstellungen".');
        }
      }
    } catch (error) {
      console.error('Error checking email config:', error);
    }
  };

  // Drag & Drop Handlers für Dokumente (Reordering)
  const handleDocDragStart = (e, index) => {
    setDraggedDocIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDocDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDocDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedDocIndex === null || draggedDocIndex === dropIndex) {
      setDraggedDocIndex(null);
      return;
    }

    const newDocuments = [...documents];
    const draggedDoc = newDocuments[draggedDocIndex];
    
    // Entferne das gezogene Element
    newDocuments.splice(draggedDocIndex, 1);
    
    // Füge es an der neuen Position ein
    newDocuments.splice(dropIndex, 0, draggedDoc);
    
    setDocuments(newDocuments);
    setDraggedDocIndex(null);
    
    toast.success('Reihenfolge aktualisiert');
  };

  const handleDocDragEnd = () => {
    setDraggedDocIndex(null);
  };

  const sendCaseEmail = async () => {
    if (!caseId) {
      toast.error('Fehler: Fall-ID nicht gefunden');
      return;
    }

    if (!emailData.to) {
      toast.error('Bitte geben Sie eine Empfänger-Email-Adresse ein');
      return;
    }

    setSendingEmail(true);
    
    try {
      const response = await fetch(
        `${getBackendUrl()}/api/cases/${caseId}/send-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            to: emailData.to,
            subject: emailData.subject,
            body: emailData.body,
            attach_documents: emailData.attachments
          })
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success(`✅ ${data.message}`);
      } else {
        toast.error(`❌ ${data.detail || 'Fehler beim Senden der Email'}`);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('❌ Verbindungsfehler beim Senden der Email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Erst speichern, dann PDF generieren
    try {
      // 1. Speichere Abtretungs-Daten (falls noch nicht durch Auto-Save passiert)
      console.log('💾 Speichere Abtretungserklärung vor PDF-Generierung...');
      
      const saveResponse = await fetch(
        `${getBackendUrl()}/api/cases/${caseId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            customer_name: formData.customer_name,
            customer_email: formData.customer_email,
            customer_phone: formData.customer_phone,
            customer_address: formData.customer_address,
            vehicle: {
              vin: formData.vin,
              license_plate: formData.license_plate,
              manufacturer: formData.manufacturer,
              model: formData.model,
              year: formData.year,
              mileage: formData.mileage
            },
            damage: {
              location: formData.damage_location,
              damage_type: formData.damage_type,
              severity: formData.severity,
              description: formData.damage_description,
              photos: formData.photos
            },
            calculation: null,
            insurance_company: formData.insurance_company,
            insurance_claim_number: formData.insurance_claim_number,
            insurance_policy_number: formData.insurance_policy_number,
            notes: formData.notes,
            abtretung_text: formData.abtretung_text,
            abtretung_signature: formData.abtretung_signature,
            abtretung_signed: formData.abtretung_signed
          })
        }
      );
      
      if (saveResponse.ok) {
        console.log('✅ Abtretung gespeichert');
      }
      
      // 2. Generiere Abtretungs-PDF
      toast.info('Erstelle Abtretungs-PDF...', { duration: 2000 });
      
      const response = await fetch(
        `${getBackendUrl()}/api/cases/${caseId}/generate-abtretung-pdf`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        
        // Speichere PDF-URL im formData
        if (data.pdf_url) {
          setFormData(prev => ({
            ...prev,
            abtretung_pdf_url: data.pdf_url
          }));
        }
        
        toast.success('✅ Abtretungs-PDF erstellt!');
        
        // Gehe zu Schritt 7 "Dokumente"
        setTimeout(() => {
          setCurrentStep(7);
          toast.info('📁 Ihre Dokumente wurden aktualisiert', { duration: 3000 });
        }, 1000);
      } else {
        toast.warning('PDF konnte nicht erstellt werden - Fall wird trotzdem gespeichert');
        setCurrentStep(7);
      }
    } catch (error) {
      console.error('Fehler beim Erstellen der Abtretungs-PDF:', error);
      toast.warning('PDF-Erstellung fehlgeschlagen - Fall ist gespeichert');
      setCurrentStep(7);
    }
  };

  const handleOldSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare case data with proper structure
      const caseData = {
        customer_name: formData.customer_name || 'Unbekannter Kunde',
        customer_email: formData.customer_email || null,
        customer_phone: formData.customer_phone || null,
        customer_address: formData.customer_address || null,
        vehicle: {
          vin: formData.vin || 'UNKNOWN00000000000',
          license_plate: formData.license_plate || 'UNBEKANNT',
          manufacturer: formData.manufacturer || 'Unbekannt',
          model: formData.model || 'Unbekannt',
          year: parseInt(formData.year) || new Date().getFullYear(),
          mileage: parseInt(formData.mileage) || null
        },
        damage: {
          location: formData.damage_location || 'Windschutzscheibe',
          damage_type: formData.damage_type || 'crack',
          severity: parseInt(formData.severity) || 3,
          description: formData.damage_description || 'Keine Beschreibung',
          photos: formData.photos || []
        },
        calculation: formData.total ? {
          material_cost: parseFloat(formData.material_cost) || 0,
          labor_cost: parseFloat(formData.labor_cost) || 0,
          total: parseFloat(formData.total) || 0,
          vat: parseFloat(formData.vat) || 0
        } : null,
        insurance_company: formData.insurance_company || null,
        insurance_claim_number: formData.insurance_claim_number || null,
        notes: formData.notes || null
      };


      const response = await fetch(getBackendUrl() + '/api/cases', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(caseData)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Fall erfolgreich erstellt!');
        // Navigate to cases list after short delay
        setTimeout(() => {
          navigate('/cases');
        }, 500);
      } else {
        const error = await response.json();
        console.error('❌ Backend Fehler:', error);
        toast.error(`Fehler: ${error.detail || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      console.error('❌ Error creating case:', error);
      toast.error('Verbindungsfehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    // Debug logging for abtretung_text
    if (name === 'abtretung_text') {
      console.log('📝 Abtretung text changed:', {
        length: newValue.length,
        preview: newValue.substring(0, 50) + '...'
      });
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  // Search insurances - loads all if query is empty, searches if query provided
  const searchInsurances = async (query) => {
    setLoadingInsurances(true);
    try {
      let url;
      
      if (!query || query.trim() === '') {
        // Load all insurances (alle 229 Versicherungen)
        url = `${getBackendUrl()}/api/insurances?limit=500`;
      } else if (query.length >= 2) {
        // Search with query
        url = `${getBackendUrl()}/api/insurances/search?q=${encodeURIComponent(query)}&limit=100`;
      } else {
        // Query too short
        setInsuranceResults([]);
        setLoadingInsurances(false);
        return;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInsuranceResults(data.insurances || []);
      }
    } catch (error) {
      console.error('Insurance search error:', error);
    } finally {
      setLoadingInsurances(false);
    }
  };

  // Select insurance from dropdown
  const selectInsurance = (insurance) => {
    const insuranceName = insurance.name || insurance.Name;
    setFormData(prev => ({
      ...prev,
      insurance_company: insuranceName,
      insurance_po_street: insurance.street || insurance.Straße || '',
      insurance_po_zip: insurance.postal_code || insurance.PLZ || '',
      insurance_po_city: insurance.city || insurance.Ort || '',
      insurance_po_telephone: insurance.phone || insurance.Telefon || '',
      insurance_po_fax: insurance.fax || insurance.Fax || '',
      insurance_po_email: insurance.email || insurance['E-Mail'] || ''
    }));
    setInsuranceSearch('');
    setShowInsuranceDropdown(false);
    setInsuranceResults([]);
    
    // Zeige Erfolgsmeldung mit Details
    const hasContact = insurance.street || insurance.phone || insurance.email;
    if (hasContact) {
      toast.success(`✅ ${insuranceName} mit Kontaktdaten übernommen`);
    } else {
      toast.success(`✅ ${insuranceName} ausgewählt`);
    }
  };

  // Auto-save signature when customer signs Abtretungserklärung
  const handleAbtretungSignatureEnd = async () => {
    if (sigPadAbtretung.current && !sigPadAbtretung.current.isEmpty() && caseId) {
      try {
        console.log('🖊️ Signatur wird komprimiert und hochgeladen...');
        
        // Canvas mit MAXIMALER Auflösung für gestochen scharfe Unterschrift
        const canvas = sigPadAbtretung.current.getCanvas();
        const compressedCanvas = document.createElement('canvas');
        const targetWidth = 1600; // MAXIMALE Auflösung für gestochen scharfe Unterschrift
        const targetHeight = Math.floor(canvas.height * (targetWidth / canvas.width));
        
        compressedCanvas.width = targetWidth;
        compressedCanvas.height = targetHeight;
        
        const ctx = compressedCanvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, targetWidth, targetHeight);
        ctx.imageSmoothingEnabled = true; // Leichte Glättung für bessere Qualität
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(canvas, 0, 0, targetWidth, targetHeight);
        
        // Als BLOB mit MAXIMALER Qualität erstellen
        const blob = await new Promise(resolve => {
          compressedCanvas.toBlob(resolve, 'image/png'); // PNG statt JPEG - verlustfrei!
        });
        
        console.log('📦 Signatur-Blob erstellt:', (blob.size / 1024).toFixed(2) + ' KB');
        
        // Als FormData hochladen (viel effizienter als Base64!)
        const formData = new FormData();
        formData.append('signature', blob, 'signature.jpg');
        
        const response = await fetch(
          `${getBackendUrl()}/api/cases/${caseId}/upload-signature`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: formData
          }
        );

        if (response.ok) {
          const result = await response.json();
          console.log('✅ Signatur erfolgreich hochgeladen!');
          
          // Speichere URL im State
          setFormData(prev => ({
            ...prev,
            abtretung_signature: result.signature_url,
            abtretung_signed: true
          }));
          
          toast.success('✅ Unterschrift gespeichert');
        } else {
          const errorText = await response.text();
          console.error('❌ Upload-Fehler:', response.status, errorText);
          toast.error('Fehler beim Speichern');
        }
      } catch (error) {
        console.error('❌ Fehler:', error);
        toast.error('Fehler beim Speichern der Unterschrift');
      }
    }
  };

  // Clear Abtretung signature
  const clearAbtretungSignature = async () => {
    // Clear canvas if ref exists
    if (sigPadAbtretung.current) {
      sigPadAbtretung.current.clear();
    }
    
    // Update local state
    setFormData(prev => ({
      ...prev,
      abtretung_signature: '',
      abtretung_signed: false
    }));
    
    // Delete from backend if case exists
    if (caseId) {
      try {
        await fetch(`${getBackendUrl()}/api/cases/${caseId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            abtretung_signature: '',
            abtretung_signed: false
          })
        });
      } catch (error) {
        console.error('Fehler beim Löschen der Signatur:', error);
      }
    }
    
    toast.info('Unterschrift gelöscht');
  };

  // Add custom position
  const addCustomPosition = async () => {
    if (!newPosition.description || newPosition.price <= 0) {
      toast.error('Bitte Beschreibung und Preis eingeben');
      return;
    }

    const position = {
      id: Date.now(),
      description: newPosition.description,
      quantity: newPosition.quantity,
      price: newPosition.price,
      total: newPosition.quantity * newPosition.price
    };

    // Add to current invoice
    setCustomPositions(prev => [...prev, position]);

    // Update material cost
    setFormData(prev => {
      const newMaterialCost = prev.material_cost + position.total;
      const newSubtotal = newMaterialCost + prev.labor_cost + prev.calibration_cost;
      const newVat = newSubtotal * 0.19;
      return {
        ...prev,
        material_cost: newMaterialCost,
        subtotal: newSubtotal,
        vat: newVat,
        total: newSubtotal + newVat
      };
    });

    // Save as default if requested
    if (newPosition.saveAsDefault) {
      try {
        const response = await fetch(`${getBackendUrl()}/api/settings/default-positions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            description: newPosition.description,
            quantity: newPosition.quantity,
            price: newPosition.price
          })
        });

        if (response.ok) {
          toast.success('Position hinzugefügt und als Standard gespeichert');
        } else {
          toast.success('Position hinzugefügt (Standard-Speicherung fehlgeschlagen)');
        }
      } catch (error) {
        console.error('Error saving default position:', error);
        toast.success('Position hinzugefügt');
      }
    } else {
      toast.success('Position hinzugefügt');
    }

    // Reset form
    setNewPosition({
      description: '',
      quantity: 1,
      price: 0,
      saveAsDefault: false
    });
    setShowAddPositionModal(false);
  };

  // Remove custom position
  const removeCustomPosition = (positionId) => {
    const position = customPositions.find(p => p.id === positionId);
    if (position) {
      setCustomPositions(prev => prev.filter(p => p.id !== positionId));
      
      // Update material cost
      setFormData(prev => {
        const newMaterialCost = prev.material_cost - position.total;
        const newSubtotal = newMaterialCost + prev.labor_cost + prev.calibration_cost;
        const newVat = newSubtotal * 0.19;
        return {
          ...prev,
          material_cost: newMaterialCost,
          subtotal: newSubtotal,
          vat: newVat,
          total: newSubtotal + newVat
        };
      });

      toast.info('Position entfernt');
    }
  };

  const queryGDV = async (kennzeichen) => {
    if (!kennzeichen) {
      return;
    }


    try {
      toast.info('🔍 GDV Versicherungsabfrage läuft...');

      const url = `${getBackendUrl()}/api/integrations/gdv/lookup?licence_number=${encodeURIComponent(kennzeichen)}&date_of_loss=${encodeURIComponent(formData.date_of_loss)}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const gdvData = await response.json();

        if (gdvData.response_code === '0' || gdvData.response_code === 0) {
          // Erfolgreiche Abfrage - befülle ALLE Versicherungsdaten
          
          setFormData(prev => {
            const updated = {
              ...prev,
              // Versicherungsdaten
              insurance_company: gdvData.insurance_company_name || prev.insurance_company,
              insurance_claim_number: gdvData.insurance_policy_number || prev.insurance_claim_number,
              insurance_policy_number: gdvData.insurance_policy_number || prev.insurance_policy_number,
              contract_begin: gdvData.contract_begin || prev.contract_begin,
              contract_end: gdvData.contract_end || prev.contract_end,
              // Versicherungs-Kontaktdaten
              insurance_po_name: gdvData.insurance_po_name || prev.insurance_po_name,
              insurance_po_telephone: gdvData.insurance_po_telephone_no || prev.insurance_po_telephone,
              insurance_po_fax: gdvData.insurance_po_fax_no || prev.insurance_po_fax,
              insurance_po_email: gdvData.insurance_po_email || prev.insurance_po_email,
              insurance_po_street: gdvData.insurance_po_street || prev.insurance_po_street,
              insurance_po_zip: gdvData.insurance_po_zip_city || prev.insurance_po_zip,
              insurance_po_city: gdvData.insurance_po_city || prev.insurance_po_city,
              // Fahrzeugdaten aus GDV
              manufacturer_name: gdvData.manufacturer_name || prev.manufacturer_name,
              type_name: gdvData.type_name || prev.type_name,
              registration_date: gdvData.registration_date || prev.registration_date,
              // Kasko-Informationen
              has_kasko: gdvData.has_kasko || prev.has_kasko,
              kasko_type: gdvData.kasko_type || prev.kasko_type,
              kasko_insurance_company: gdvData.kasko_insurance_company_name || prev.kasko_insurance_company,
              kasko_deductible: gdvData.kasko_deductible || prev.kasko_deductible
            };
            return updated;
          });

          const kaskoInfo = gdvData.has_kasko 
            ? `\nKasko: ${gdvData.kasko_insurance_company_name || 'Ja'}` 
            : '\nKasko: Nein';
          
          // Prüfe ob Dummy-Daten
          if (gdvData.dummy) {
            toast.info(`🔍 Demo-Modus: Beispieldaten geladen\n${gdvData.message}`, { duration: 8000 });
          } else {
            toast.success(`✅ Versicherung gefunden: ${gdvData.insurance_company_name}${kaskoInfo}`, { duration: 7000 });
          }
        } else if (gdvData.response_code === '-2' || gdvData.response_code === -2) {
          toast.warning(`⚠️ Kennzeichen "${kennzeichen}" nicht in GDV-Datenbank gefunden`, { duration: 5000 });
        } else {
          toast.info(`ℹ️ GDV Response Code: ${gdvData.response_code} - Keine Versicherungsdaten`, { duration: 5000 });
        }
      } else {
        const error = await response.json();
        console.error('queryGDV: API Error:', error);
        toast.warning('GDV Abfrage nicht möglich - bitte manuell eingeben');
      }
    } catch (error) {
      console.error('queryGDV: Exception:', error);
      // Stiller Fehler - Benutzer kann manuell eingeben
    }
  };

  const handleFahrzeugscheinScan = async (e) => {
    const file = e.target?.files?.[0] || e.dataTransfer?.files?.[0];
    if (!file) return;

    setIsDragging(false);

    if (!file.type.match(/image\/(jpeg|jpg|png)/) && !file.type.match(/application\/pdf/)) {
      toast.error('Bitte nur JPG, PNG oder PDF hochladen');
      return;
    }

    setScanning(true);
    
    // 🎭 GDV SIMULATION: Cycling through 3 rules
    const currentRule = scanCounter % 3; // 0, 1, or 2
    const simulationData = [
      {
        // Rule 1: Vollkasko
        insurance: 'Allianz Versicherungs-AG',
        policyNumber: 'VK-2024-' + Math.random().toString(36).substr(2, 9).toUpperCase(),
        coverageType: 'Vollkasko',
        deductible: '500',
        street: 'Königinstraße 28',
        zip: '80802',
        city: 'München',
        message: '🎯 Scan #' + (scanCounter + 1) + ': Vollkasko-Simulation'
      },
      {
        // Rule 2: Teilkasko
        insurance: 'HUK-COBURG Haftpflicht-Unterstützungs-Kasse',
        policyNumber: 'TK-2024-' + Math.random().toString(36).substr(2, 9).toUpperCase(),
        coverageType: 'Teilkasko',
        deductible: '150',
        street: 'Willi-Hussong-Straße 2',
        zip: '96450',
        city: 'Coburg',
        message: '🎯 Scan #' + (scanCounter + 1) + ': Teilkasko-Simulation'
      },
      {
        // Rule 3: Nur Haftpflicht
        insurance: 'DEVK Deutsche Eisenbahn Versicherung',
        policyNumber: 'HP-2024-' + Math.random().toString(36).substr(2, 9).toUpperCase(),
        coverageType: 'Haftpflicht',
        deductible: '0',
        street: 'Riehler Straße 190',
        zip: '50735',
        city: 'Köln',
        message: '🎯 Scan #' + (scanCounter + 1) + ': Haftpflicht-Simulation'
      }
    ];
    
    const currentSimulation = simulationData[currentRule];
    toast.info(currentSimulation.message + ' wird angewendet... ⏳');

    try {
      // 1. Upload Fahrzeugschein-Bild auch zu Lichtbildern (nur wenn es ein Bild ist)
      let uploadedImageUrl = null;
      if (file.type.match(/image\/(jpeg|jpg|png)/)) {
        try {
          const uploadFormData = new FormData();
          uploadFormData.append('file', file);
          
          const uploadResponse = await fetch(getBackendUrl() + '/api/upload', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: uploadFormData
          });
          
          if (uploadResponse.ok) {
            const uploadResult = await uploadResponse.json();
            uploadedImageUrl = uploadResult.url;
            console.log('✅ Fahrzeugschein als Bild hochgeladen:', uploadedImageUrl);
          }
        } catch (uploadError) {
          console.warn('⚠️ Bild-Upload zu Lichtbildern fehlgeschlagen:', uploadError);
        }
      }

      // 2. Scan Fahrzeugschein
      const scanFormData = new FormData();
      scanFormData.append('file', file);
      
      const response = await fetch(getBackendUrl() + '/api/scan-fahrzeugschein', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: scanFormData
      });

      const result = await response.json();

      if (response.ok && result) {
        // API gibt direkt die Felder zurück, nicht in result.data
        const vehicleData = result;
        
        // Auto-fill form fields mit korrekten Feldnamen - OHNE Formatierung
        // Separate Vor- und Nachname (firstname OHNE Unterstrich ist der korrekte API-Key!)
        const firstName = vehicleData.firstname || vehicleData.first_name || vehicleData.Vorname || vehicleData.name2 || '';
        const lastName = vehicleData.last_or_company_name || vehicleData.Name || vehicleData.name || vehicleData.name1 || '';
        const fullName = vehicleData.full_name || (firstName && lastName ? `${firstName} ${lastName}` : lastName);
        
        // 🎭 Apply GDV Simulation Rules
        const hasKasko = currentSimulation.coverageType !== 'Haftpflicht';
        // Ort aus PLZ/Ort extrahieren für Abtretung (z.B. "12345 Berlin" → "Berlin")
        const plzOrt = vehicleData.address2 || formData.customer_plz_ort || '';
        const extractedOrt = plzOrt.split(' ').slice(1).join(' ');
        
        const updatedData = {
          ...formData,
          customer_first_name: firstName,
          customer_last_name: lastName,
          customer_name: fullName, // Kombiniert für Kompatibilität
          customer_address: vehicleData.address1 || formData.customer_address,
          customer_plz_ort: plzOrt,
          vin: vehicleData.VIN || vehicleData.vin || formData.vin,
          license_plate: vehicleData.number_plate || vehicleData.registrationNumber || formData.license_plate, // KEINE Formatierung
          manufacturer: vehicleData.intelligent_maker || vehicleData.car_brand || vehicleData.maker || formData.manufacturer, // Intelligente Marke verwenden
          model: vehicleData.intelligent_model || vehicleData.car_model || vehicleData.model || formData.model, // Intelligentes Modell verwenden
          first_registration: vehicleData.intelligent_first_registration || vehicleData.date_first_registration || vehicleData.firstRegistration || vehicleData.b || vehicleData.ez_string || formData.first_registration, // Intelligente Erstzulassung
          photos: uploadedImageUrl ? [...formData.photos, uploadedImageUrl] : formData.photos, // Fahrzeugschein zu Fotos hinzufügen
          
          // ✅ Abtretung: Datum (heute) und Ort (aus Fahrzeugschein) automatisch setzen
          abtretung_date: formData.abtretung_date || new Date().toISOString().split('T')[0],
          abtretung_location: formData.abtretung_location || extractedOrt,
          
          // 🎭 GDV SIMULATION: Auto-fill insurance data
          insurance_company: currentSimulation.insurance,
          insurance_policy_number: currentSimulation.policyNumber,
          insurance_number: currentSimulation.policyNumber, // Auch insurance_number setzen
          has_kasko: hasKasko,
          kasko_type: hasKasko ? currentSimulation.coverageType : '',
          kasko_insurance_company: hasKasko ? currentSimulation.insurance : '',
          kasko_deductible: currentSimulation.deductible,
          // 🎭 GDV SIMULATION: Auto-fill insurance address
          insurance_po_street: currentSimulation.street,
          insurance_po_zip: currentSimulation.zip,
          insurance_po_city: currentSimulation.city
        };

        setFormData(updatedData);
        
        // 🎯 NEU: Extrahiere PLZ und lade DEKRA-Sätze automatisch
        if (vehicleData.address2) {
          // address2 format: "PLZ Stadt" z.B. "51145 Köln"
          const plzMatch = vehicleData.address2.match(/^(\d{5})/);
          if (plzMatch) {
            const extractedPLZ = plzMatch[1];
            console.log('🏠 PLZ aus Fahrzeugschein extrahiert:', extractedPLZ);
            
            // Update formData with PLZ for Step 5
            setFormData(prev => ({
              ...prev,
              dat_plz_stundensatz: extractedPLZ
            }));
            
            // Automatically fetch DEKRA rates
            fetchDekraRates(extractedPLZ);
            
            toast.success(`📍 PLZ ${extractedPLZ} automatisch übernommen`, { 
              duration: 3000 
            });
          }
        }
        
        // Increment scan counter for next scan
        setScanCounter(prev => prev + 1);
        
        // Zeige welche Felder befüllt wurden
        const filledFields = [];
        if (vehicleData.vin) filledFields.push('VIN');
        if (vehicleData.registrationNumber) filledFields.push('Kennzeichen');
        if (vehicleData.maker) filledFields.push('Marke');
        if (vehicleData.model) filledFields.push('Modell');
        if (vehicleData.name1) filledFields.push('Kunde');
        
        if (filledFields.length > 0) {
          toast.success(`✨ ${filledFields.join(', ')} übertragen!`, { duration: 5000 });
        }
        
        // 🎭 GDV SIMULATION: Show applied rule
        const simulationInfo = [
          `📋 Versicherung: ${currentSimulation.insurance}`,
          `📍 Adresse: ${currentSimulation.street}, ${currentSimulation.zip} ${currentSimulation.city}`,
          `🔢 Vers.-Nr.: ${currentSimulation.policyNumber}`,
          `🛡️ Art: ${currentSimulation.coverageType}`,
          currentSimulation.deductible !== '0' ? `💰 SB: ${currentSimulation.deductible} €` : '✓ Keine SB'
        ].filter(Boolean);
        
        toast.success('🎭 GDV-Simulation angewendet!\n' + simulationInfo.join('\n'), { 
          duration: 7000,
          style: { whiteSpace: 'pre-line' }
        });
        
        // Zeige Info über Bild-Upload
        if (uploadedImageUrl) {
          toast.success('📸 Fahrzeugschein wurde zu Lichtbildern hinzugefügt!', { duration: 3000 });
        } else {
          toast.warning('Keine Daten im Bild erkannt. Bitte manuell eingeben.');
        }

        // Aktualisiere Fall mit neuem Kundennamen → Neue Aktennummer wird generiert
        if (vehicleData.full_name && caseId) {
          const updateResponse = await fetch(`${getBackendUrl()}/api/cases/${caseId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              customer_name: vehicleData.full_name,
              customer_email: formData.customer_email || null,
              customer_phone: formData.customer_phone || null,
              customer_address: vehicleData.address1 || formData.customer_address || null,
              vehicle: {
                vin: vehicleData.VIN || vehicleData.vin || 'XXXXXXXXXXXXXXXX',
                license_plate: vehicleData.number_plate || vehicleData.registrationNumber || '-',
                manufacturer: vehicleData.intelligent_maker || vehicleData.car_brand || vehicleData.maker || '-',
                model: vehicleData.intelligent_model || vehicleData.car_model || vehicleData.model || '-',
                year: parseInt(vehicleData.firstRegistration?.split('.')[2]) || new Date().getFullYear(),
                mileage: parseInt(formData.mileage) || null
              },
              damage: {
                location: formData.damage_location || 'Windschutzscheibe',
                damage_type: formData.damage_type || 'crack',
                severity: parseInt(formData.severity) || 3,
                description: formData.damage_description || 'In Bearbeitung',
                photos: formData.photos || []
              },
              calculation: null,
              insurance_company: formData.insurance_company || null,
              insurance_claim_number: formData.insurance_claim_number || null,
              notes: formData.notes || null,
              is_draft: false  // Akte ist jetzt aktiv nach Fahrzeugschein-Scan
            })
          });

          if (updateResponse.ok) {
            const updatedCase = await updateResponse.json();
            if (updatedCase.case_number) {
              setCaseNumber(updatedCase.case_number);
              toast.success(`✅ Akte gespeichert! Aktennummer: ${updatedCase.case_number}`, { duration: 4000 });
            }
          }
        } else if (caseId) {
          // Auch ohne Namen speichern - mit Fahrzeugschein-Bild
          const updateResponse = await fetch(`${getBackendUrl()}/api/cases/${caseId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              customer_name: formData.customer_name || '',
              license_plate: vehicleData.number_plate || vehicleData.registrationNumber || formData.license_plate || '',
              manufacturer: vehicleData.intelligent_maker || vehicleData.car_brand || vehicleData.maker || formData.manufacturer || '',
              model: vehicleData.intelligent_model || vehicleData.car_model || vehicleData.model || formData.model || '',
              vin: vehicleData.VIN || vehicleData.vin || formData.vin || '',
              first_registration: vehicleData.firstRegistration || formData.first_registration || '',
              vehicle: {
                vin: vehicleData.VIN || vehicleData.vin || formData.vin || '',
                license_plate: vehicleData.number_plate || vehicleData.registrationNumber || formData.license_plate || '',
                manufacturer: vehicleData.intelligent_maker || vehicleData.car_brand || vehicleData.maker || formData.manufacturer || '',
                model: vehicleData.intelligent_model || vehicleData.car_model || vehicleData.model || formData.model || '',
                year: parseInt(vehicleData.firstRegistration?.split('.')[2]) || new Date().getFullYear(),
                mileage: parseInt(formData.mileage) || null
              },
              fahrzeugschein_image: uploadedImageUrl || null,
              photos: uploadedImageUrl ? [...(formData.photos || []), uploadedImageUrl] : formData.photos || [],
              insurance_company: formData.insurance_company || currentSimulation.insurance || '',
              insurance_policy_number: formData.insurance_policy_number || currentSimulation.policyNumber || '',
              kasko_type: currentSimulation.coverageType || '',
              kasko_deductible: currentSimulation.deductible || '',
              is_draft: false  // Akte ist jetzt aktiv nach Fahrzeugschein-Scan
            })
          });

          if (updateResponse.ok) {
            const updatedCase = await updateResponse.json();
            if (updatedCase.case_number) {
              setCaseNumber(updatedCase.case_number);
            }
            toast.success(`✅ Akte mit Fahrzeugschein gespeichert!`, { duration: 4000 });
          }
        }

        // Automatisch GDV Abfrage starten wenn Kennzeichen vorhanden - OHNE Formatierung
        if (vehicleData.registrationNumber) {
          await queryGDV(vehicleData.registrationNumber); // DIREKT ohne formatKennzeichen()
        } else {
        }
      } else {
        console.error('API Error:', result);
        toast.warning('Scannen abgeschlossen. Bitte Daten überprüfen.');
      }
    } catch (error) {
      console.error('Scan error:', error);
      toast.error('Verbindungsfehler beim Scannen');
    } finally {
      setScanning(false);
    }
  };

  // Bildkomprimierung für schnelleren Upload
  const compressImage = (file, maxWidth = 1920, quality = 0.8) => {
    return new Promise((resolve) => {
      // Nur Bilder komprimieren
      if (!file.type.startsWith('image/') || file.type === 'image/gif' || file.type === 'image/svg+xml') {
        resolve(file);
        return;
      }
      
      // HEIC/HEIF Dateien ohne Komprimierung durchlassen (Browser-Unterstützung begrenzt)
      if (file.type === 'image/heic' || file.type === 'image/heif' || file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')) {
        console.log(`📸 HEIC/HEIF Datei erkannt: ${file.name} - wird ohne Komprimierung hochgeladen`);
        resolve(file);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Nur verkleinern wenn größer als maxWidth
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
              console.log(`📦 Komprimiert: ${file.name} ${(file.size/1024).toFixed(0)}KB → ${(blob.size/1024).toFixed(0)}KB`);
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          }, 'image/jpeg', quality);
        };
        img.onerror = () => {
          console.warn(`⚠️ Konnte Bild nicht laden: ${file.name}`);
          resolve(file);
        };
        img.src = e.target.result;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target?.files || e.dataTransfer?.files || []);
    setIsDragging(false);
    
    if (files.length === 0) {
      return;
    }

    // Validate file sizes (max 50MB per file)
    const maxSize = 50 * 1024 * 1024; // 50MB in bytes
    const oversizedFiles = files.filter(file => file.size > maxSize);
    
    if (oversizedFiles.length > 0) {
      toast.error(`❌ Folgende Dateien sind zu groß (max. 50MB): ${oversizedFiles.map(f => f.name).join(', ')}`);
      return;
    }

    // Validate file types
    const allowedTypes = [
      // Images
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 
      'image/tiff', 'image/svg+xml', 'image/heic', 'image/heif',
      // Videos
      'video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 
      'video/mpeg', 'video/x-matroska',
      // Documents
      'application/pdf',
      // Archives
      'application/zip', 'application/x-rar-compressed', 'application/x-7z-compressed'
    ];

    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|gif|webp|bmp|tiff|svg|heic|heif|mp4|mov|avi|webm|mpeg|mkv|pdf|zip|rar|7z)$/i));
    
    if (invalidFiles.length > 0) {
      toast.error(`❌ Nicht unterstützte Dateiformate: ${invalidFiles.map(f => f.name).join(', ')}`);
      return;
    }

    toast.info(`📤 ${files.length} Datei(en) werden hochgeladen...`, { duration: 2000 });
    
    // 1. Bilder komprimieren (parallel)
    const compressedFiles = await Promise.all(files.map(file => compressImage(file)));
    
    // 2. Alle Dateien PARALLEL hochladen (viel schneller!)
    const uploadPromises = compressedFiles.map(async (file) => {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      try {
        const response = await fetch(getBackendUrl() + '/api/upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formDataUpload
        });
        
        if (response.ok) {
          const data = await response.json();
          return data.url;
        } else {
          console.error('❌ Upload failed:', file.name);
          return null;
        }
      } catch (error) {
        console.error('❌ Upload error:', file.name, error);
        return null;
      }
    });
    
    const uploadResults = await Promise.all(uploadPromises);
    const uploadedPhotos = uploadResults.filter(url => url !== null);

    if (uploadedPhotos.length > 0) {
      const allPhotos = [...formData.photos, ...uploadedPhotos];
      
      setFormData((prevFormData) => ({
        ...prevFormData,
        photos: allPhotos
      }));
      
      toast.success(`✅ ${uploadedPhotos.length} Datei(en) hochgeladen`);
      
      if (caseId) {
        try {
          // Fotos in DB speichern
          const saveResponse = await fetch(`${getBackendUrl()}/api/cases/${caseId}/photos`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ photos: allPhotos })
          });
          
          if (!saveResponse.ok) {
            toast.error('Fehler beim Speichern der Fotos');
            return;
          }
          
          // PDF im Hintergrund generieren (ohne zu warten)
          toast.info('📄 Erstelle Lichtbilder-PDF...', { duration: 1500 });
          
          fetch(`${getBackendUrl()}/api/cases/${caseId}/generate-lichtbilder-pdf`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          }).then(async (pdfResponse) => {
            if (pdfResponse.ok) {
              const result = await pdfResponse.json();
              if (result.pdf_url) {
                setFormData(prev => ({ ...prev, lichtbilder_pdf_url: result.pdf_url }));
                toast.success('✅ Lichtbilder-PDF erstellt!', { duration: 2000 });
              }
            }
          }).catch(console.error);
          
        } catch (error) {
          console.error('❌ Fehler:', error);
        }
      }
    } else if (files.length > 0) {
      toast.error('❌ Upload fehlgeschlagen');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (currentStep === 1) {
      handleFahrzeugscheinScan(e);
    } else if (currentStep === 4) {
      handleFileUpload(e);
    }
  };

  const nextStep = async () => {
    // Speichere currentStep in lokaler Variable (React State ist asynchron!)
    const fromStep = currentStep;
    console.log('🚀 nextStep aufgerufen von Schritt:', fromStep);
    
    // Special handling for Step 2 -> Step 3: Generate Auftrag+Versicherung PDF
    if (fromStep === 2 && caseId) {
      try {
        toast.info('Erstelle Auftrags-PDF...', { duration: 2000 });
        
        const response = await fetch(
          `${getBackendUrl()}/api/cases/${caseId}/generate-auftrag-pdf`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.ok) {
          const result = await response.json();
          
          if (result.pdf_url) {
            setFormData(prev => ({
              ...prev,
              auftrag_pdf_url: result.pdf_url
            }));
          }
          
          toast.success('✅ Auftrags-PDF erstellt!', { duration: 2000 });
        }
      } catch (error) {
        console.error('Fehler beim Erstellen der Auftrags-PDF:', error);
        // Weiter navigieren auch bei Fehler
      }
    }
    
    // Special handling for Step 3 -> Step 4: Generate Abtretung PDF
    if (fromStep === 3 && caseId) {
      try {
        // WICHTIG: Überprüfe ob Signatur existiert (aus formData oder aus der Canvas)
        let hasSignature = formData.abtretung_signature && formData.abtretung_signature.length > 100;
        
        // Falls noch nicht im formData, versuche von Canvas zu holen
        if (!hasSignature && sigPadAbtretung.current && !sigPadAbtretung.current.isEmpty()) {
          console.log('⚠️ Signatur noch nicht im formData - hole direkt von Canvas');
          const signatureData = sigPadAbtretung.current.toDataURL('image/jpeg', 0.5);
          
          // Speichere sofort
          toast.info('⏳ Speichere Unterschrift...', { duration: 1500 });
          const saveResponse = await fetch(`${getBackendUrl()}/api/cases/${caseId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              abtretung_signature: signatureData,
              abtretung_signed: true
            })
          });
          
          if (saveResponse.ok) {
            hasSignature = true;
            setFormData(prev => ({
              ...prev,
              abtretung_signature: signatureData,
              abtretung_signed: true
            }));
            console.log('✅ Signatur nachträglich gespeichert');
            // Kurze Wartezeit damit DB definitiv aktualisiert ist
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        }
        
        // NUR PDF generieren wenn Signatur vorhanden UND noch keine PDF existiert
        if (hasSignature && !formData.abtretung_pdf_url) {
          toast.info('Erstelle Abtretungs-PDF...', { duration: 2000 });
          
          const response = await fetch(
            `${getBackendUrl()}/api/cases/${caseId}/generate-abtretung-pdf`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          if (response.ok) {
            const result = await response.json();
            
            if (result.pdf_url) {
              setFormData(prev => ({
                ...prev,
                abtretung_pdf_url: result.pdf_url
              }));
            }
            
            toast.success('✅ Abtretungs-PDF erstellt!', { duration: 2000 });
          }
        } else if (!hasSignature) {
          console.log('⏭️ Keine Signatur vorhanden - PDF-Generierung übersprungen');
        }
      } catch (error) {
        console.error('Fehler beim Erstellen der Abtretungs-PDF:', error);
        // Weiter navigieren auch bei Fehler
      }
    }
    
    // Special handling for Step 4 -> Step 5: IMMER Lichtbilder PDF neu generieren
    // (weil die Reihenfolge der Fotos geändert sein könnte)
    const step4Check = {
      currentStep,
      fromStep,
      isStep4: fromStep === 4,
      caseId,
      hasCaseId: !!caseId,
      photos: formData.photos,
      photosLength: formData.photos?.length,
      hasPhotos: !!(formData.photos && formData.photos.length > 0),
      lichtbilderPdfUrl: formData.lichtbilder_pdf_url,
      willRegenerate: fromStep === 4 && caseId && formData.photos && formData.photos.length > 0
    };
    console.log('📸 Lichtbilder-Check DETAILED:', step4Check);
    
    // IMMER neu generieren wenn Fotos vorhanden (Reihenfolge könnte geändert sein)
    if (fromStep === 4 && caseId && formData.photos && formData.photos.length > 0) {
      try {
        console.log('🔄 Generiere Lichtbilder-PDF mit aktueller Foto-Reihenfolge...');
        toast.info('Aktualisiere Lichtbilder-PDF...', { duration: 2000 });
        
        // WICHTIG: Erst alle Fotos mit korrekter Reihenfolge speichern!
        console.log('💾 Speichere Fotos in korrekter Reihenfolge:', formData.photos);
        await fetch(`${getBackendUrl()}/api/cases/${caseId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            photos: formData.photos  // Die Reihenfolge aus dem UI wird übernommen
          })
        });
        
        // Kurze Wartezeit damit DB definitiv aktualisiert ist
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // PDF generieren - Backend verwendet die Foto-Reihenfolge aus der DB
        const response = await fetch(
          `${getBackendUrl()}/api/cases/${caseId}/generate-lichtbilder-pdf`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              photos: formData.photos  // Foto-Reihenfolge explizit übergeben
            })
          }
        );
        
        if (response.ok) {
          const result = await response.json();
          
          if (result.pdf_url) {
            // Timestamp anhängen um Cache zu umgehen
            const urlWithTimestamp = result.pdf_url + '?t=' + Date.now();
            setFormData(prev => ({
              ...prev,
              lichtbilder_pdf_url: result.pdf_url
            }));
          }
          
          toast.success('✅ Lichtbilder-PDF aktualisiert!', { duration: 2000 });
        }
      } catch (error) {
        console.error('Fehler beim Erstellen der Lichtbilder-PDF:', error);
        // Weiter navigieren auch bei Fehler
      }
    }
    
    // Special handling for Step 5 -> Step 6: Automatically fetch DAT PDF
    if (fromStep === 5) {
      const contractId = datContractId || datCalculationResult?.contract_id;
      
      if (contractId && !formData.dat_pdf_url) {
        console.log('🔄 Automatischer DAT-PDF-Download beim Übergang zu Schritt 6...');
        
        try {
          // Show loading toast
          toast.info('DAT-Kalkulation wird abgerufen...', {
            duration: 5000
          });
          
          const response = await fetch(
            `${getBackendUrl()}/api/cases/${caseId}/fetch-dat-pdf`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          if (response.ok) {
            const result = await response.json();
            console.log('✅ DAT-PDF erfolgreich abgerufen:', result);
            
            // Update form data with PDF URL
            setFormData(prev => ({
              ...prev,
              dat_pdf_url: result.pdf_url,
              dat_pdf_base64: result.pdf_base64
            }));
            
            toast.success('DAT-Kalkulation erfolgreich abgerufen!', {
              duration: 3000
            });
          } else {
            const error = await response.json();
            console.error('❌ DAT-PDF Fehler:', error);
            
            // Show error but don't block navigation
            toast.error(error.detail || 'DAT-PDF konnte nicht abgerufen werden', {
              duration: 5000
            });
          }
        } catch (error) {
          console.error('❌ Fehler beim DAT-PDF-Download:', error);
          
          // Show error but don't block navigation
          toast.error('Fehler beim Abrufen der DAT-Kalkulation', {
            duration: 5000
          });
        }
      }
    }
    
    // Special handling for Step 7 -> Step 8: Generate Invoice PDF automatically
    if (fromStep === 7 && caseId) {
      try {
        toast.info('Erstelle Rechnung-PDF...', { duration: 2000 });
        
        const response = await fetch(
          `${getBackendUrl()}/api/cases/${caseId}/generate-invoice-pdf`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          toast.success('✅ Rechnung-PDF erstellt!', { duration: 2000 });
          
          // Add to documents list
          const invoiceDoc = {
            id: Date.now(),
            name: `Rechnung-${formData.case_number || caseId}.pdf`,
            type: 'Rechnung',
            date: new Date().toISOString(),
            url: data.pdf_url,
            size: 'N/A'
          };
          
          // Check if invoice already exists
          const exists = documents.some(doc => doc.type === 'Rechnung');
          if (!exists) {
            setDocuments([invoiceDoc, ...documents]);
          }
        } else {
          toast.error('Rechnung-PDF konnte nicht erstellt werden');
        }
      } catch (error) {
        console.error('Error generating invoice PDF:', error);
        toast.error('Fehler beim Erstellen der Rechnung');
        // Weiter navigieren auch bei Fehler
      }
    }
    
    if (fromStep < 8) setCurrentStep(fromStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  // Fetch DEKRA rates based on PLZ
  const fetchDekraRates = async (plz) => {
    if (!plz || plz.length < 2) {
      setDekraRates(null);
      return;
    }
    
    try {
      setLoadingDekraRates(true);
      console.log('🔍 Lade DEKRA-Sätze für PLZ:', plz);
      
      const response = await fetch(
        `${getBackendUrl()}/api/dekra-rates?plz=${plz}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ DEKRA-Sätze geladen:', data);
        setDekraRates(data);
        
        // Auto-fill if values are empty
        if (data.found && data.rates) {
          if (!formData.dat_lackierer_stundensatz) {
            setFormData(prev => ({
              ...prev,
              dat_lackierer_stundensatz: data.rates.lack || ''
            }));
          }
        }
        
        // Show toast with region info
        if (data.found) {
          toast.success(`DEKRA-Sätze für ${data.region} geladen`, {
            duration: 3000
          });
        }
      } else {
        console.error('❌ Fehler beim Laden der DEKRA-Sätze');
        setDekraRates(null);
      }
    } catch (error) {
      console.error('❌ Fehler beim Laden der DEKRA-Sätze:', error);
      setDekraRates(null);
    } finally {
      setLoadingDekraRates(false);
    }
  };

  // Toggle damage area selection
  const toggleDamageArea = (area) => {
    setFormData(prev => {
      const currentAreas = prev.damageAreas || [];
      const isSelected = currentAreas.includes(area);
      
      if (isSelected) {
        // Remove area
        return {
          ...prev,
          damageAreas: currentAreas.filter(a => a !== area)
        };
      } else {
        // Add area
        return {
          ...prev,
          damageAreas: [...currentAreas, area]
        };
      }
    });
  };

  // Document Management Functions
  const handleDocumentUpload = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const newDocs = Array.from(files).map((file, index) => ({
        id: documents.length + index + 1,
        name: file.name,
        type: 'Dokument',
        created: new Date().toLocaleString('de-DE'),
        modified: new Date().toLocaleString('de-DE'),
        size: `${(file.size / 1024).toFixed(1)} KB`,
        url: URL.createObjectURL(file),
        tags: ['Hochgeladen']
      }));
      setDocuments([...documents, ...newDocs]);
      toast.success(`${files.length} Dokument(e) hochgeladen`);
    }
  };

  const handleDocumentDelete = (docId) => {
    if (window.confirm('Möchten Sie dieses Dokument wirklich löschen?')) {
      setDocuments(documents.filter(doc => doc.id !== docId));
      toast.success('Dokument gelöscht');
    }
  };

  const handleDocumentDownload = (doc) => {
    const link = document.createElement('a');
    link.href = doc.url;
    link.download = doc.name;
    link.click();
    toast.success('Download gestartet');
  };

  const handleDocumentPrint = (doc) => {
    // PDF im Modal öffnen statt in neuem Tab
    setCurrentPdfTitle(doc.name);
    const fullUrl = doc.url.startsWith('http') 
      ? doc.url 
      : `${getBackendUrl()}${doc.url}`;
    setCurrentPdfUrl(fullUrl);
    setPdfViewerOpen(true);
  };

  const handlePDFBundle = async () => {
    if (selectedDocs.length === 0) {
      toast.warning('Bitte wählen Sie mindestens ein Dokument aus');
      return;
    }
    
    setBundlingPdfs(true);
    try {
      const response = await fetch(
        `${getBackendUrl()}/api/cases/${caseId}/bundle-pdfs`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            document_ids: selectedDocs
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        
        // Füge gebündelte PDF zur Liste hinzu
        if (data.pdf_url) {
          setFormData(prev => ({
            ...prev,
            bundled_pdf_url: data.pdf_url
          }));
          
          // Füge zur Dokumentenliste hinzu
          const bundledDoc = {
            id: Date.now(),
            name: `Gebuendelt-${formData.case_number || caseId}.pdf`,
            type: 'Gebündelte PDFs',
            date: new Date().toISOString(),
            url: data.pdf_url,
            size: `${data.page_count} Seiten`
          };
          setDocuments([bundledDoc, ...documents]);
        }
        
        toast.success(`✅ ${data.page_count} Seiten erfolgreich gebündelt!`);
        setSelectedDocs([]); // Reset Auswahl
      } else {
        toast.error('Fehler beim Bündeln');
      }
    } catch (error) {
      console.error('PDF Bundle Error:', error);
      toast.error('Verbindungsfehler');
    } finally {
      setBundlingPdfs(false);
    }
  };

  const handleLockCase = () => {
    if (window.confirm('Fall jetzt sperren? Nach dem Sperren können keine Änderungen mehr vorgenommen werden.')) {
      toast.success('Fall wurde gesperrt');
    }
  };

  const handleWhatsAppSend = () => {
    toast.info('WhatsApp-Versand wird vorbereitet...');
  };

  const handleEmailSend = () => {
    toast.info('E-Mail-Versand wird vorbereitet...');
  };

  return (
    <Layout user={user}>
      <div className="relative flex w-full max-w-full bg-gray-50 px-6 gap-6">
        {/* Wizard Sidebar - fixed beim Scrollen */}
        <div className="w-64 hidden lg:block flex-shrink-0">
          <aside className="w-64 bg-white rounded-lg shadow-xl fixed top-20 z-30" style={{ border: '1px solid #E1E4E8', maxHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}>
          <div className="p-4 border-b" style={{ borderColor: '#E1E4E8' }}>
            <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm">
              <span>← Zurück</span>
            </Link>
          </div>
          <nav className="p-4 pb-2">
            {/* Aktennummer */}
            {caseNumber && (
              <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs font-medium text-blue-600 mb-1">Aktennummer</p>
                <p className="text-sm font-bold text-blue-900">{caseNumber}</p>
              </div>
            )}
            
            <div className="mb-4">
              <h2 className="text-sm font-medium text-gray-500 mb-1">Erfassen Sie die Grunddaten</h2>
              <p className="text-xs text-gray-400">für eine neue Akte</p>
            </div>
            <div className="space-y-1">
              {steps.map((step) => {
                const Icon = step.icon;
                const isActive = currentStep === step.id;
                const stepStatus = getStepStatus(step.id);
                
                return (
                  <button
                    key={step.id}
                    onClick={() => setCurrentStep(step.id)}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md transition-all text-sm font-normal text-left"
                    style={{
                      backgroundColor: isActive ? '#3B82F6' : 'transparent',
                      color: isActive ? 'white' : '#4A5568'
                    }}
                    data-testid={step.testId}
                  >
                    {/* Status-Kreis neben Icon (hohl) */}
                    <div 
                      className={`w-3 h-3 rounded-full flex items-center justify-center text-[8px] font-bold flex-shrink-0 bg-transparent
                        ${stepStatus === 'complete' ? 'border-2 border-green-500 text-green-500' : ''}
                        ${stepStatus === 'partial' ? 'border-2 border-orange-400' : ''}
                        ${stepStatus === 'empty' ? 'border-2 border-gray-300' : ''}
                      `}
                      title={stepStatus === 'complete' ? 'Vollständig' : stepStatus === 'partial' ? 'Teilweise ausgefüllt' : 'Nicht begonnen'}
                    >
                      {stepStatus === 'complete' && '✓'}
                    </div>
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span>{step.title}</span>
                  </button>
                );
              })}
            </div>
          </nav>
        </aside>
        </div>

        {/* Mobile Wizard Bottom Navigation */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50" style={{ borderColor: '#E1E4E8' }}>
          <div className="flex items-center justify-between px-2 py-2 overflow-x-auto">
            {steps.map((step) => {
              const Icon = step.icon;
              const isCompleted = completedSteps.includes(step.id);
              const isCurrent = currentStep === step.id;
              
              return (
                <button
                  key={step.id}
                  onClick={() => setCurrentStep(step.id)}
                  className={`flex flex-col items-center justify-center min-w-[50px] py-1 px-1 rounded-lg transition-all ${
                    isCurrent 
                      ? 'bg-blue-100 text-blue-600' 
                      : isCompleted 
                        ? 'text-green-600' 
                        : 'text-gray-400'
                  }`}
                  data-testid={`mobile-step-${step.id}`}
                >
                  <div className={`relative ${isCurrent ? 'scale-110' : ''}`}>
                    <Icon className="w-5 h-5" />
                    {isCompleted && !isCurrent && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
                        <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <span className={`text-[8px] font-medium mt-0.5 truncate max-w-[45px] ${
                    isCurrent ? 'text-blue-600' : ''
                  }`}>
                    {step.title.length > 6 ? step.title.substring(0, 6) + '.' : step.title}
                  </span>
                </button>
              );
            })}
          </div>
          {/* Progress indicator */}
          <div className="flex gap-0.5 px-2 pb-2">
            {steps.map((step) => (
              <div
                key={step.id}
                className="h-1 flex-1 rounded-full transition-all"
                style={{
                  backgroundColor: currentStep >= step.id ? '#3B82F6' : completedSteps.includes(step.id) ? '#22C55E' : '#E1E4E8'
                }}
              ></div>
            ))}
          </div>
        </div>

        {/* Mobile Header */}
        <div className="lg:hidden fixed top-16 left-0 right-0 bg-white border-b z-10 px-4 py-3" style={{ borderColor: '#E1E4E8' }}>
          <div className="flex items-center justify-between mb-2">
            <Link to="/" className="flex items-center gap-2 text-gray-600">
              <span className="text-sm font-medium">← Zurück</span>
            </Link>
            <div className="text-sm font-medium text-gray-900">{steps.find(s => s.id === currentStep)?.title}</div>
          </div>
        </div>

        {/* Main Content - Consistent alignment for all steps */}
        <main className="flex-1 pt-20 pb-24 lg:pt-0 lg:pb-0 w-full overflow-x-hidden min-w-0">
          <div className="w-full max-w-full">
            <div className="w-full">
              
              {/* Step 1: Auftrag */}
              {currentStep === 1 && (
                <div className="animate-fade-in w-full">
                  <div className="bg-white rounded-lg shadow-lg border w-full" style={{borderColor: '#E1E4E8'}}>
                    <div className="p-6 rounded-t-lg" style={{backgroundColor: '#3B82F6'}}>
                      <div className="flex items-center gap-3 text-white">
                        <FileText className="w-6 h-6" />
                        <h1 className="text-xl font-bold">Auftragsformular</h1>
                      </div>
                    </div>

                    <div className="space-y-6 overflow-x-hidden">
                      {/* Fahrzeugschein Scanner */}
                      <div className="bg-blue-50 rounded-lg p-6 m-6 w-auto">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#3B82F6'}}>
                            <Camera className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">Fahrzeugschein Scanner (mit GDV-Simulation)</h3>
                            <p className="text-sm text-gray-600">Fahrzeugschein hochladen und automatisch ausfüllen lassen</p>
                          </div>
                        </div>
                        
                        {/* GDV Test Info */}
                        <div className="bg-white border border-blue-200 rounded-lg p-3 mb-4">
                          <p className="text-xs text-gray-700">
                            <strong className="text-blue-600">💡 Test-Modus:</strong> Datei mit "test", "gdv" oder "demo" im Namen hochladen für GDV-Simulation
                          </p>
                        </div>
                        <div 
                          className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${isDragging ? 'bg-blue-100 border-blue-500 scale-105' : 'bg-white'}`}
                          style={{borderColor: isDragging ? '#3B82F6' : '#93C5FD'}}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                        >
                          <input 
                            type="file" 
                            accept="image/jpeg,image/jpg,image/png,application/pdf" 
                            className="hidden" 
                            id="fahrzeugschein-upload" 
                            data-testid="fahrzeugschein-upload"
                            onChange={handleFahrzeugscheinScan}
                          />
                          <label htmlFor="fahrzeugschein-upload" className="cursor-pointer">
                            {scanning ? (
                              <>
                                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mx-auto mb-3"></div>
                                <p className="text-base font-medium text-gray-900">Wird gescannt...</p>
                              </>
                            ) : (
                              <>
                                <svg className={`w-12 h-12 mx-auto mb-3 transition-colors ${isDragging ? 'text-blue-600' : 'text-blue-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <p className="text-base font-medium text-gray-900 mb-1">
                                  {isDragging ? 'Datei hier ablegen!' : 'Fahrzeugschein hochladen'}
                                </p>
                                <p className="text-sm text-gray-500">Klicken zum Hochladen oder Drag & Drop</p>
                                <p className="text-xs text-gray-400 mt-2">PDF, JPG, PNG - Max. 10MB</p>
                              </>
                            )}
                          </label>
                        </div>
                        <p className="text-sm text-center mt-3 flex items-center justify-center gap-2" style={{color: '#D97706'}}>
                          <span>✨</span>
                          <span>Daten werden automatisch in die Felder übertragen</span>
                        </p>
                      </div>

                      {/* Kundendaten */}
                      <div className="px-6 pt-6">
                        <div className="flex items-center gap-2 mb-4">
                          <User className="w-5 h-5" style={{color: '#3B82F6'}} />
                          <h3 className="font-semibold text-gray-900">Kundendaten</h3>
                        </div>
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Vorname *</label>
                              <input 
                                type="text" 
                                name="customer_first_name" 
                                value={formData.customer_first_name} 
                                onChange={(e) => {
                                  const newData = { ...formData, customer_first_name: e.target.value };
                                  // Auto-update customer_name
                                  newData.customer_name = `${e.target.value} ${formData.customer_last_name}`.trim();
                                  setFormData(newData);
                                }}
                                className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                                style={{borderColor: formData.customer_first_name ? '#3B82F6' : '#D1D5DB', backgroundColor: formData.customer_first_name ? '#ECFEFF' : 'white'}} 
                                required 
                                data-testid="customer-first-name-input" 
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Nachname *</label>
                              <input 
                                type="text" 
                                name="customer_last_name" 
                                value={formData.customer_last_name} 
                                onChange={(e) => {
                                  const newData = { ...formData, customer_last_name: e.target.value };
                                  // Auto-update customer_name
                                  newData.customer_name = `${formData.customer_first_name} ${e.target.value}`.trim();
                                  setFormData(newData);
                                }}
                                className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                                style={{borderColor: formData.customer_last_name ? '#3B82F6' : '#D1D5DB', backgroundColor: formData.customer_last_name ? '#ECFEFF' : 'white'}} 
                                required 
                                data-testid="customer-last-name-input" 
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                              <input type="text" name="customer_address" value={formData.customer_address} onChange={handleChange} className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" style={{borderColor: formData.customer_address ? '#3B82F6' : '#D1D5DB', backgroundColor: formData.customer_address ? '#ECFEFF' : 'white'}} data-testid="customer-address-input" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">PLZ / Ort</label>
                              <input type="text" name="customer_plz_ort" value={formData.customer_plz_ort || ''} onChange={handleChange} className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" style={{borderColor: formData.customer_plz_ort ? '#3B82F6' : '#D1D5DB', backgroundColor: formData.customer_plz_ort ? '#ECFEFF' : 'white'}} placeholder="z.B. 10115 Berlin" data-testid="customer-plz-input" />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                              <input type="tel" name="customer_phone" value={formData.customer_phone} onChange={handleChange} className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" style={{borderColor: formData.customer_phone ? '#3B82F6' : '#D1D5DB', backgroundColor: formData.customer_phone ? '#ECFEFF' : 'white'}} data-testid="customer-phone-input" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                              <input type="email" name="customer_email" value={formData.customer_email} onChange={handleChange} className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" style={{borderColor: formData.customer_email ? '#3B82F6' : '#D1D5DB', backgroundColor: formData.customer_email ? '#ECFEFF' : 'white'}} data-testid="customer-email-input" />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Fahrzeugdaten */}
                      <div className="px-6 pt-6">
                        <div className="flex items-center gap-2 mb-4">
                          <svg className="w-5 h-5" style={{color: '#3B82F6'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          <h3 className="font-semibold text-gray-900">Fahrzeugdaten</h3>
                        </div>
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Fahrzeugtyp *</label>
                              <select name="vehicle_type" value={formData.vehicle_type} onChange={handleChange} className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" style={{borderColor: '#3B82F6', backgroundColor: '#ECFEFF'}} data-testid="vehicle-type-select">
                                <option value="PKW">PKW</option>
                                <option value="LKW">LKW</option>
                                <option value="Motorrad">Motorrad</option>
                                <option value="Transporter">Transporter</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Kennzeichen</label>
                              <div className="flex gap-2">
                                <input 
                                  type="text" 
                                  name="license_plate" 
                                  value={formData.license_plate} 
                                  onChange={handleChange} 
                                  className="flex-1 px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                                  style={{
                                    borderColor: formData.license_plate ? '#3B82F6' : '#D1D5DB', 
                                    backgroundColor: formData.license_plate ? '#ECFEFF' : 'white'
                                  }} 
                                  placeholder="z.B. K-XY 123" 
                                  data-testid="license-plate-input" 
                                />
                                <button
                                  type="button"
                                  onClick={() => queryGDV(formData.license_plate)}
                                  disabled={!formData.license_plate}
                                  className="px-4 py-2 md:py-3 rounded-lg text-white font-medium transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                  style={{backgroundColor: '#3B82F6'}}
                                  title="GDV Versicherungsabfrage"
                                  data-testid="gdv-lookup-button"
                                >
                                  <Search className="w-5 h-5" />
                                  <span className="hidden md:inline text-sm">GDV</span>
                                </button>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                <Search className="w-3 h-3 inline mr-1" />
                                Klicken Sie auf die Lupe für GDV-Abfrage
                              </p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Marke</label>
                              <input type="text" name="manufacturer" value={formData.manufacturer} onChange={handleChange} className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" style={{borderColor: formData.manufacturer ? '#3B82F6' : '#D1D5DB', backgroundColor: formData.manufacturer ? '#ECFEFF' : 'white'}} placeholder="z.B. BMW" data-testid="manufacturer-input" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Modell</label>
                              <input type="text" name="model" value={formData.model} onChange={handleChange} className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" style={{borderColor: formData.model ? '#3B82F6' : '#D1D5DB', backgroundColor: formData.model ? '#ECFEFF' : 'white'}} placeholder="z.B. 3er (330D)" data-testid="model-input" />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">VIN</label>
                              <input type="text" name="vin" value={formData.vin} onChange={handleChange} maxLength={17} className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm md:text-base" style={{borderColor: formData.vin ? '#3B82F6' : '#D1D5DB', backgroundColor: formData.vin ? '#ECFEFF' : 'white'}} placeholder="17-stellige VIN" data-testid="vin-input" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Baujahr</label>
                              <input type="number" name="year" value={formData.year} onChange={handleChange} min={1900} max={2100} className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" style={{borderColor: formData.year ? '#3B82F6' : '#D1D5DB', backgroundColor: formData.year ? '#ECFEFF' : 'white'}} data-testid="year-input" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Erstzulassung</label>
                              <input type="date" name="first_registration" value={formData.first_registration || ''} onChange={handleChange} className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" style={{borderColor: formData.first_registration ? '#3B82F6' : '#D1D5DB', backgroundColor: formData.first_registration ? '#ECFEFF' : 'white'}} data-testid="first-registration-input" />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Laufleistung (km)</label>
                              <input type="number" name="mileage" value={formData.mileage || ''} onChange={handleChange} min={0} className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" style={{borderColor: formData.mileage ? '#3B82F6' : '#D1D5DB', backgroundColor: formData.mileage ? '#ECFEFF' : 'white'}} placeholder="z.B. 50000" data-testid="mileage-input" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Vorsteuerabzugsberechtigt</label>
                              <select 
                                name="vorsteuerabzugsberechtigt"
                                value={formData.vorsteuerabzugsberechtigt}
                                onChange={handleChange}
                                className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                                style={{
                                  borderColor: formData.vorsteuerabzugsberechtigt ? '#3B82F6' : '#D1D5DB',
                                  backgroundColor: formData.vorsteuerabzugsberechtigt ? '#EFF6FF' : 'white'
                                }}
                                data-testid="vorsteuer-select"
                              >
                                <option value="nein">Nein</option>
                                <option value="ja">Ja</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Bottom spacing */}
                      <div className="pb-6"></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Versicherung */}
              {currentStep === 2 && (
                <div className="animate-fade-in w-full">
                  <div className="bg-white rounded-lg shadow-lg border w-full" style={{borderColor: '#E1E4E8'}}>
                    <div className="p-6 rounded-t-lg" style={{backgroundColor: '#3B82F6'}}>
                      <div className="flex items-center gap-3 text-white">
                        <Building className="w-6 h-6" />
                        <h1 className="text-xl font-bold">Versicherung</h1>
                      </div>
                    </div>
                    <div className="space-y-6 pb-6">
                      {/* Fahrzeug & Schadendaten */}
                      <div className="px-6 pt-6">
                        <div className="flex items-center gap-2 mb-4">
                          <Car className="w-5 h-5 text-blue-600" />
                          <h3 className="font-semibold text-gray-900">Fahrzeug & Schadendaten</h3>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Kennzeichen (automatisch vom Kunden) */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Kennzeichen</label>
                            <div className="w-full px-3 py-2 border rounded-lg text-sm bg-blue-50 border-blue-300 font-medium text-blue-900">
                              {formData.license_plate || 'Nicht angegeben'}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Aus Schritt 1 übernommen</p>
                          </div>
                          
                          {/* Schadentag (kleiner) */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Schadentag *</label>
                            <input 
                              type="date" 
                              name="date_of_loss" 
                              value={formData.date_of_loss} 
                              onChange={handleChange} 
                              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" 
                              style={{
                                borderColor: formData.date_of_loss ? '#3B82F6' : '#D1D5DB',
                                backgroundColor: formData.date_of_loss ? '#EFF6FF' : 'white'
                              }}
                              required
                              data-testid="date-of-loss-input" 
                            />
                          </div>
                          
                          {/* GDV Button */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">GDV-Abfrage</label>
                            <button
                              type="button"
                              onClick={() => queryGDV(formData.license_plate)}
                              disabled={!formData.license_plate || !formData.date_of_loss || loading}
                              className={`w-full px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                                formData.license_plate && formData.date_of_loss 
                                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              }`}
                              data-testid="gdv-query-button"
                            >
                              {loading ? (
                                <>
                                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                  </svg>
                                  Abfrage...
                                </>
                              ) : (
                                <>
                                  <Search className="w-4 h-4" />
                                  GDV Abfrage starten
                                </>
                              )}
                            </button>
                            <p className="text-xs text-gray-500 mt-1">
                              {!formData.license_plate ? 'Kennzeichen fehlt' : !formData.date_of_loss ? 'Schadentag fehlt' : 'Bereit für Abfrage'}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Versicherungsdaten */}
                      <div className="px-6 pt-4 border-t" style={{ borderColor: '#E1E4E8' }}>
                        <div className="flex items-center gap-2 mb-4">
                          <Building className="w-5 h-5 text-blue-600" />
                          <h3 className="font-semibold text-gray-900">Versicherungsdaten</h3>
                        </div>
                        
                        {/* Einheitliches Grid für alle Felder */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Versicherungsname - volle Breite */}
                          <div className="relative md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Versicherungsname
                              <span className="text-xs text-gray-500 ml-2">(GDV, Suchen oder Liste durchsuchen)</span>
                            </label>
                            
                            {/* Show selected insurance OR search input with dropdown */}
                            {formData.insurance_company && !showInsuranceDropdown ? (
                              <div className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg text-sm md:text-base bg-blue-50 border-blue-500 flex items-center justify-between">
                                <span className="font-medium text-blue-900">{formData.insurance_company}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setFormData(prev => ({ ...prev, insurance_company: '' }));
                                    setShowInsuranceDropdown(true);
                                    setInsuranceSearch('');
                                    // Load all insurances
                                    searchInsurances('');
                                  }}
                                  className="text-blue-600 hover:text-blue-800 text-sm underline"
                                >
                                  Ändern
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="relative flex gap-2">
                                  <input
                                    type="text"
                                    value={insuranceSearch}
                                    onChange={(e) => {
                                      setInsuranceSearch(e.target.value);
                                      searchInsurances(e.target.value);
                                    }}
                                    onFocus={() => {
                                      setShowInsuranceDropdown(true);
                                      // Load all if search is empty
                                      if (!insuranceSearch) {
                                        searchInsurances('');
                                      }
                                    }}
                                    className="flex-1 px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                                    placeholder="Suchen oder Pfeil klicken für alle..."
                                    data-testid="insurance-search-input"
                                  />
                                  
                                  {/* Dropdown Toggle Button */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setShowInsuranceDropdown(!showInsuranceDropdown);
                                      // Load all insurances when opening
                                      if (!showInsuranceDropdown) {
                                        searchInsurances('');
                                      }
                                    }}
                                    className="px-4 py-2 md:py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
                                    data-testid="insurance-dropdown-toggle"
                                  >
                                    <svg 
                                      className={`w-5 h-5 transition-transform ${showInsuranceDropdown ? 'rotate-180' : ''}`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                  
                                  {loadingInsurances && (
                                    <div className="absolute right-20 top-1/2 transform -translate-y-1/2">
                                      <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Dropdown Results */}
                                {showInsuranceDropdown && (
                                  <div className="absolute z-10 w-full mt-1 bg-white border-2 border-blue-300 rounded-lg shadow-xl overflow-hidden" style={{ maxHeight: '500px' }}>
                                    <div className="sticky top-0 bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-2 border-b border-blue-200">
                                      <p className="text-xs font-medium text-blue-900">
                                        {insuranceResults.length > 0 
                                          ? `${insuranceResults.length} Versicherung${insuranceResults.length !== 1 ? 'en' : ''} gefunden`
                                          : 'Suchen oder scrollen Sie durch die Liste'}
                                      </p>
                                    </div>
                                    
                                    <div id="insurance-dropdown-list" className="overflow-y-auto" style={{ maxHeight: '420px', scrollbarWidth: 'thin' }}>
                                      {insuranceResults.length > 0 ? (
                                        insuranceResults.map((insurance, idx) => (
                                          <button
                                            key={idx}
                                            type="button"
                                            onClick={() => selectInsurance(insurance)}
                                            className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors focus:bg-blue-100 focus:outline-none"
                                          >
                                            <div className="font-medium text-gray-900 text-sm">{insurance.name || insurance.Name}</div>
                                          </button>
                                        ))
                                      ) : (
                                        <div className="px-4 py-8 text-center">
                                          <p className="text-sm text-gray-600">
                                            {insuranceSearch 
                                              ? `Keine Versicherung gefunden für "${insuranceSearch}"`
                                              : 'Klicken Sie auf den Pfeil oder beginnen Sie zu tippen'}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                    
                                    <div className="sticky bottom-0 bg-gray-50 px-4 py-2 border-t border-gray-200">
                                      <button
                                        type="button"
                                        onClick={() => setShowInsuranceDropdown(false)}
                                        className="text-xs text-gray-600 hover:text-gray-800"
                                      >
                                        Schließen
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Versicherungsscheinnummer</label>
                            <input 
                              type="text" 
                              name="insurance_policy_number" 
                              value={formData.insurance_policy_number} 
                              onChange={handleChange} 
                              className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-green-500 text-sm md:text-base" 
                              style={{
                                borderColor: formData.insurance_policy_number ? '#22C55E' : '#D1D5DB',
                                backgroundColor: formData.insurance_policy_number ? '#F0FDF4' : 'white'
                              }}
                              placeholder="Versicherungsscheinnummer aus GDV" 
                              data-testid="insurance-policy-input" 
                            />
                          </div>
                          
                          {/* Schadennummer */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Schadennummer</label>
                            <input 
                              type="text" 
                              name="claim_number" 
                              value={formData.claim_number} 
                              onChange={handleChange} 
                              className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                              style={{
                                borderColor: formData.claim_number ? '#3B82F6' : '#D1D5DB',
                                backgroundColor: formData.claim_number ? '#EFF6FF' : 'white'
                              }}
                              placeholder="Schadennummer eingeben" 
                              data-testid="claim-number-input" 
                            />
                          </div>
                        </div>
                        
                        {/* Kasko-Information */}
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Kaskoversicherung</label>
                            <div className={`w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg text-sm md:text-base font-medium flex items-center justify-between ${formData.has_kasko ? 'bg-green-50 border-green-500 text-green-700' : 'bg-gray-50 border-gray-300 text-gray-500'}`}>
                              <span>{formData.has_kasko ? `✅ ${formData.kasko_type}` : '❌ Keine Kasko'}</span>
                              {formData.has_kasko && !formData.kasko_deductible && (
                                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">SB manuell eintragen</span>
                              )}
                            </div>
                            {formData.kasko_insurance_company && (
                              <p className="text-xs text-gray-600 mt-1">Kasko bei: {formData.kasko_insurance_company}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Selbstbeteiligung (SB)</label>
                            <select 
                              name="kasko_deductible" 
                              value={formData.kasko_deductible} 
                              onChange={handleChange} 
                              className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                              style={{
                                borderColor: formData.kasko_deductible ? '#3B82F6' : '#D1D5DB',
                                backgroundColor: formData.kasko_deductible ? '#EFF6FF' : 'white'
                              }}
                              disabled={!formData.has_kasko}
                              data-testid="kasko-deductible-select" 
                            >
                              <option value="">Bitte auswählen</option>
                              <option value="150 EUR">150 EUR</option>
                              <option value="300 EUR">300 EUR</option>
                              <option value="500 EUR">500 EUR</option>
                              <option value="1000 EUR">1000 EUR</option>
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                              {formData.has_kasko 
                                ? (formData.kasko_deductible ? '✓ Selbstbeteiligung eingetragen' : '⚠️ Bitte Selbstbeteiligung auswählen')
                                : 'Keine Kasko vorhanden'}
                            </p>
                          </div>
                        </div>
                        
                        {/* Kontaktdaten (ohne Überschrift, direkt integriert) */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Straße</label>
                            <input 
                              type="text" 
                              name="insurance_po_street" 
                              value={formData.insurance_po_street} 
                              onChange={handleChange} 
                              className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                              style={{
                                borderColor: formData.insurance_po_street ? '#3B82F6' : '#D1D5DB',
                                backgroundColor: formData.insurance_po_street ? '#EFF6FF' : 'white'
                              }}
                              data-testid="insurance-street-input" 
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">PLZ</label>
                            <input 
                              type="text" 
                              name="insurance_po_zip" 
                              value={formData.insurance_po_zip} 
                              onChange={handleChange} 
                              className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                              style={{
                                borderColor: formData.insurance_po_zip ? '#3B82F6' : '#D1D5DB',
                                backgroundColor: formData.insurance_po_zip ? '#EFF6FF' : 'white'
                              }}
                              data-testid="insurance-zip-input" 
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Stadt</label>
                            <input 
                              type="text" 
                              name="insurance_po_city" 
                              value={formData.insurance_po_city} 
                              onChange={handleChange} 
                              className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                              style={{
                                borderColor: formData.insurance_po_city ? '#3B82F6' : '#D1D5DB',
                                backgroundColor: formData.insurance_po_city ? '#EFF6FF' : 'white'
                              }}
                              data-testid="insurance-city-input" 
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                            <input 
                              type="tel" 
                              name="insurance_po_telephone" 
                              value={formData.insurance_po_telephone} 
                              onChange={handleChange} 
                              className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                              style={{
                                borderColor: formData.insurance_po_telephone ? '#3B82F6' : '#D1D5DB',
                                backgroundColor: formData.insurance_po_telephone ? '#EFF6FF' : 'white'
                              }}
                              data-testid="insurance-phone-input" 
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Fax</label>
                            <input 
                              type="text" 
                              name="insurance_po_fax" 
                              value={formData.insurance_po_fax} 
                              onChange={handleChange} 
                              className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                              style={{
                                borderColor: formData.insurance_po_fax ? '#3B82F6' : '#D1D5DB',
                                backgroundColor: formData.insurance_po_fax ? '#EFF6FF' : 'white'
                              }}
                              data-testid="insurance-fax-input" 
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <input 
                              type="email" 
                              name="insurance_po_email" 
                              value={formData.insurance_po_email} 
                              onChange={handleChange} 
                              className="w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                              style={{
                                borderColor: formData.insurance_po_email ? '#3B82F6' : '#D1D5DB',
                                backgroundColor: formData.insurance_po_email ? '#EFF6FF' : 'white'
                              }}
                              data-testid="insurance-email-input" 
                            />
                          </div>
                        </div>
                      </div>

                      {/* Hinweis-Box */}
                      <div className="px-6 pb-6">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex gap-3">
                            <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="text-sm text-blue-900">
                              <p className="font-medium mb-1">Hinweis zur Abtretung</p>
                              <p className="text-blue-700">Die Abtretungserklärung ermöglicht die direkte Abrechnung mit der Versicherung. Alle Daten werden automatisch aus der GDV-Abfrage übernommen.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Abtretungserklärung */}
              {currentStep === 3 && (
                <div className="animate-fade-in w-full">
                  <div className="bg-white rounded-lg shadow-lg border w-full" style={{borderColor: '#E1E4E8'}}>
                    <div className="p-6 rounded-t-lg" style={{backgroundColor: '#3B82F6'}}>
                      <div className="flex items-center gap-3 text-white">
                        <FileSignature className="w-6 h-6" />
                        <h1 className="text-xl font-bold">Abtretungserklärung</h1>
                      </div>
                    </div>
                    <div className="space-y-6 pb-6">
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 m-6">
                        <p className="text-sm text-blue-900 font-medium mb-2">📄 Was ist eine Abtretungserklärung?</p>
                        <p className="text-sm text-blue-800">
                          Mit der Abtretungserklärung überträgt der Kunde seine Ansprüche gegenüber der Versicherung an Ihr Unternehmen. 
                          Dadurch können Sie direkt mit der Versicherung abrechnen.
                        </p>
                      </div>

                      {/* Kundendaten aus Schritt 1 - Bearbeitbar */}
                      <div className="space-y-4 px-6">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          <User className="w-5 h-5 text-blue-600" />
                          Kundendaten
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Name / Firma</label>
                            <input 
                              type="text" 
                              name="customer_name"
                              value={formData.customer_name}
                              onChange={handleChange}
                              className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                            <input 
                              type="text" 
                              name="customer_address"
                              value={formData.customer_address}
                              onChange={handleChange}
                              className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">PLZ / Ort</label>
                            <input 
                              type="text" 
                              name="customer_plz_ort"
                              value={formData.customer_plz_ort}
                              onChange={handleChange}
                              className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                            <input 
                              type="tel" 
                              name="customer_phone"
                              value={formData.customer_phone}
                              onChange={handleChange}
                              className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <input 
                              type="email" 
                              name="customer_email"
                              value={formData.customer_email}
                              onChange={handleChange}
                              className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                            />
                          </div>
                        </div>
                      </div>

                      {/* Fahrzeugdaten aus Schritt 1 - Bearbeitbar */}
                      <div className="space-y-4 px-6">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          Fahrzeugdaten
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Fahrzeugtyp</label>
                            <select 
                              name="vehicle_type" 
                              value={formData.vehicle_type} 
                              onChange={handleChange} 
                              className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                            >
                              <option value="PKW">PKW</option>
                              <option value="LKW">LKW</option>
                              <option value="Motorrad">Motorrad</option>
                              <option value="Transporter">Transporter</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Kennzeichen</label>
                            <input 
                              type="text" 
                              name="license_plate"
                              value={formData.license_plate}
                              onChange={handleChange}
                              className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Marke</label>
                            <input 
                              type="text" 
                              name="manufacturer"
                              value={formData.manufacturer}
                              onChange={handleChange}
                              className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Modell</label>
                            <input 
                              type="text" 
                              name="model"
                              value={formData.model}
                              onChange={handleChange}
                              className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">VIN</label>
                            <input 
                              type="text" 
                              name="vin"
                              value={formData.vin}
                              onChange={handleChange}
                              maxLength={17}
                              className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm md:text-base" 
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Baujahr</label>
                            <input 
                              type="number" 
                              name="year"
                              value={formData.year}
                              onChange={handleChange}
                              className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Erstzulassung</label>
                            <input 
                              type="date" 
                              name="first_registration"
                              value={formData.first_registration}
                              onChange={handleChange}
                              className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                            />
                          </div>
                        </div>
                      </div>

                      {/* Versicherungsdaten aus Schritt 2 - Bearbeitbar */}
                      <div className="space-y-4 px-6 pb-6">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Building className="w-5 h-5 text-blue-600" />
                          Versicherungsdaten
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Versicherungsgesellschaft</label>
                            <input 
                              type="text" 
                              name="insurance_company"
                              value={formData.insurance_company}
                              onChange={handleChange}
                              className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Versicherungsnummer</label>
                            <input 
                              type="text" 
                              name="policy_number"
                              value={formData.policy_number}
                              onChange={handleChange}
                              className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Schadennummer</label>
                            <input 
                              type="text" 
                              name="claim_number"
                              value={formData.claim_number}
                              onChange={handleChange}
                              className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base" 
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Selbstbeteiligung</label>
                            <select 
                              name="kasko_deductible" 
                              value={formData.kasko_deductible} 
                              onChange={handleChange} 
                              className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                            >
                              <option value="">Bitte auswählen</option>
                              <option value="150 EUR">150 EUR</option>
                              <option value="300 EUR">300 EUR</option>
                              <option value="500 EUR">500 EUR</option>
                              <option value="1000 EUR">1000 EUR</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Abtretungserklärung Text - Bearbeitbar */}
                      <div className="space-y-4 px-6 pb-6">
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                          <h3 className="font-semibold text-gray-900">Abtretungserklärung</h3>
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* KI Text generieren Button */}
                            <button
                              type="button"
                              disabled={generatingAiText || !caseId}
                              onClick={async () => {
                                if (!caseId) {
                                  toast.error('Bitte warten Sie, bis die Akte erstellt wurde');
                                  return;
                                }
                                setGeneratingAiText(true);
                                try {
                                  // Erst Auto-Save ausführen um aktuelle Daten zu speichern
                                  await autoSaveCase();
                                  
                                  const response = await fetch(`${getBackendUrl()}/api/cases/${caseId}/generate-abtretung-text-ai`, {
                                    method: 'POST',
                                    headers: {
                                      'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                      'Content-Type': 'application/json'
                                    }
                                  });
                                  
                                  if (response.ok) {
                                    const data = await response.json();
                                    if (data.success && data.abtretung_text) {
                                      setFormData(prev => ({ ...prev, abtretung_text: data.abtretung_text }));
                                      toast.success('KI-Text erfolgreich generiert!');
                                    } else {
                                      toast.error('Fehler bei der KI-Generierung');
                                    }
                                  } else {
                                    const errorData = await response.json().catch(() => ({}));
                                    toast.error(errorData.detail || 'Fehler bei der KI-Generierung');
                                  }
                                } catch (error) {
                                  console.error('Error generating AI text:', error);
                                  toast.error('Verbindungsfehler bei der KI-Generierung');
                                } finally {
                                  setGeneratingAiText(false);
                                }
                              }}
                              className={`px-3 py-1.5 text-xs rounded-md transition-colors flex items-center gap-1 ${
                                generatingAiText 
                                  ? 'bg-purple-400 text-white cursor-wait' 
                                  : 'bg-purple-600 hover:bg-purple-700 text-white'
                              }`}
                              data-testid="ai-generate-button"
                            >
                              {generatingAiText ? (
                                <>
                                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Generiere...
                                </>
                              ) : (
                                <>
                                  <Brain className="w-4 h-4" />
                                  KI-Text generieren
                                </>
                              )}
                            </button>
                            
                            {/* Standard-Vorlage Button */}
                            <button
                              type="button"
                              onClick={() => {
                                const defaultText = generateAbtretungsTextFromForm();
                                setFormData({ ...formData, abtretung_text: defaultText });
                                toast.success('Standard-Vorlage mit aktuellen Daten geladen');
                              }}
                              className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors flex items-center gap-1"
                              data-testid="ai-template-button"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Standard-Vorlage
                            </button>
                            
                            {/* Daten aktualisieren Button */}
                            <button
                              type="button"
                              onClick={() => {
                                const updatedText = generateAbtretungsTextFromForm();
                                setFormData({ ...formData, abtretung_text: updatedText });
                                toast.success('Daten im Text aktualisiert');
                              }}
                              className="px-3 py-1.5 text-xs bg-orange-500 hover:bg-orange-600 text-white rounded-md transition-colors flex items-center gap-1"
                              data-testid="update-data-button"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Daten aktualisieren
                            </button>
                            
                            {/* Text speichern Button */}
                            <button
                              type="button"
                              disabled={savingAbtretungText || !caseId || !formData.abtretung_text}
                              onClick={async () => {
                                if (!caseId) {
                                  toast.error('Bitte warten Sie, bis die Akte erstellt wurde');
                                  return;
                                }
                                setSavingAbtretungText(true);
                                try {
                                  const response = await fetch(`${getBackendUrl()}/api/cases/${caseId}`, {
                                    method: 'PUT',
                                    headers: {
                                      'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                      'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify({
                                      abtretung_text: formData.abtretung_text
                                    })
                                  });
                                  
                                  if (response.ok) {
                                    toast.success('Abtretungstext gespeichert!');
                                  } else {
                                    toast.error('Fehler beim Speichern');
                                  }
                                } catch (error) {
                                  console.error('Error saving text:', error);
                                  toast.error('Verbindungsfehler beim Speichern');
                                } finally {
                                  setSavingAbtretungText(false);
                                }
                              }}
                              className={`px-3 py-1.5 text-xs rounded-md transition-colors flex items-center gap-1 ${
                                savingAbtretungText 
                                  ? 'bg-green-400 text-white cursor-wait' 
                                  : 'bg-green-600 hover:bg-green-700 text-white'
                              }`}
                              data-testid="save-abtretung-button"
                            >
                              {savingAbtretungText ? (
                                <>
                                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Speichern...
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="w-4 h-4" />
                                  Text speichern
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                        <textarea 
                          name="abtretung_text"
                          rows="16"
                          value={formData.abtretung_text}
                          onChange={handleChange}
                          placeholder="Geben Sie hier Ihren eigenen Abtretungstext ein oder nutzen Sie die Buttons oben, um einen Text zu generieren..."
                          className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base leading-relaxed"
                          data-testid="abtretung-text-input"
                        />
                        <p className="text-xs text-gray-500">
                          💡 <strong>KI-Text generieren:</strong> Erstellt einen professionellen Text basierend auf allen eingetragenen Daten | 
                          <strong> Standard-Vorlage:</strong> Fügt eine einfache Vorlage mit Platzhaltern ein | 
                          <strong> Text speichern:</strong> Speichert Ihren bearbeiteten Text
                        </p>
                      </div>

                      {/* Unterschrift und Bestätigung */}
                      <div className="space-y-4 px-6 pb-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Datum der Abtretung</label>
                            <input 
                              type="date"
                              name="abtretung_date"
                              value={formData.abtretung_date}
                              onChange={handleChange}
                              className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                              data-testid="abtretung-date-input"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Ort der Unterzeichnung</label>
                            <input 
                              type="text"
                              name="abtretung_location"
                              value={formData.abtretung_location}
                              onChange={handleChange}
                              placeholder="z.B. Hagen"
                              className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                              data-testid="abtretung-location-input"
                            />
                          </div>
                        </div>

                        {/* Signature Canvas */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Unterschrift Kunde
                            {formData.abtretung_signed && (
                              <span className="ml-2 text-green-600 text-xs">✓ Gespeichert</span>
                            )}
                          </label>
                          
                          {/* Zeige gespeicherte Unterschrift wenn vorhanden */}
                          {formData.abtretung_signature && (formData.abtretung_signed || formData.abtretung_signature.startsWith('/api/')) ? (
                            <div className="space-y-3">
                              <div className="border-2 border-green-300 rounded-lg overflow-hidden bg-white p-4">
                                <img 
                                  src={formData.abtretung_signature.startsWith('/api/') 
                                    ? `${getBackendUrl()}${formData.abtretung_signature}`
                                    : formData.abtretung_signature
                                  } 
                                  alt="Gespeicherte Unterschrift"
                                  className="h-40 mx-auto"
                                  style={{maxWidth: '100%', imageRendering: '-webkit-optimize-contrast'}}
                                  onError={(e) => {
                                    console.error('Unterschrift konnte nicht geladen werden:', e.target.src);
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                              <button
                                type="button"
                                onClick={clearAbtretungSignature}
                                className="w-full px-4 py-2 text-sm bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors font-medium"
                                data-testid="clear-abtretung-signature-btn"
                              >
                                🗑️ Unterschrift löschen und neu erstellen
                              </button>
                            </div>
                          ) : (
                            <>
                              <div className="border-2 border-gray-300 rounded-lg overflow-hidden" style={{ backgroundColor: '#FAFAFA' }}>
                                <SignatureCanvas
                                  ref={sigPadAbtretung}
                                  penColor="black"
                                  minWidth={1.5}
                                  maxWidth={3}
                                  velocityFilterWeight={0.7}
                                  canvasProps={{
                                    className: 'signature-canvas',
                                    style: { width: '100%', height: '200px' }
                                  }}
                                  onEnd={handleAbtretungSignatureEnd}
                                />
                              </div>
                              
                              <div className="mt-2 flex gap-2">
                                <button
                                  type="button"
                                  onClick={clearAbtretungSignature}
                                  className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
                                  data-testid="clear-abtretung-signature-btn"
                                >
                                  🗑️ Löschen
                                </button>
                              </div>
                            </>
                          )}
                          
                          {formData.abtretung_signed && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 rounded-lg text-sm mt-2">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Automatisch gespeichert
                            </div>
                          )}
                        </div>

                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                          <div className="flex items-start gap-2">
                            <FileSignature className="w-5 h-5 text-blue-600 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-blue-900">💡 Auto-Speicherung aktiv</p>
                              <p className="text-xs text-blue-700 mt-1">
                                Die Unterschrift wird automatisch gespeichert, sobald Sie mit dem Unterschreiben fertig sind. 
                                Sie können die Unterschrift jederzeit löschen und neu erstellen.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Lichtbilder */}
              {currentStep === 4 && (
                <div className="animate-fade-in w-full">
                  <div className="bg-white rounded-lg shadow-lg border w-full" style={{borderColor: '#E1E4E8'}}>
                    <div className="p-6 rounded-t-lg" style={{backgroundColor: '#3B82F6'}}>
                      <div className="flex items-center gap-3 text-white">
                        <Camera className="w-6 h-6" />
                        <h1 className="text-xl font-bold">Lichtbilder & Schadeninformationen</h1>
                      </div>
                    </div>

                    {/* Tabs */}
                    <div className="border-b" style={{borderColor: '#E1E4E8'}}>
                      <div className="flex">
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, photoTab: 'photos'})}
                          className={`px-6 py-3 font-medium transition-colors ${
                            (!formData.photoTab || formData.photoTab === 'photos')
                              ? 'text-blue-600 border-b-2 border-blue-600'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          Fotos hochladen
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, photoTab: 'damage'})}
                          className={`px-6 py-3 font-medium transition-colors ${
                            formData.photoTab === 'damage'
                              ? 'text-blue-600 border-b-2 border-blue-600'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          Schadeninformationen
                        </button>
                      </div>
                    </div>

                    {/* Tab Content */}
                    <div className="p-6 space-y-4">
                      {/* Photos Tab */}
                      {(!formData.photoTab || formData.photoTab === 'photos') && (
                        <div className="space-y-4">
                          {/* Header Section */}
                          <div className="mb-6">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                                <Camera className="w-6 h-6 text-white" />
                              </div>
                              <h3 className="text-xl font-bold text-gray-900">Schadensfotos hochladen</h3>
                            </div>
                            <p className="text-sm text-gray-600 ml-13">Mehrere Bilder gleichzeitig hochladen</p>
                          </div>

                          {/* Bildergalerie - JETZT OBEN */}
                          {formData.photos.length > 0 && (
                            <div>
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                            <p className="text-sm text-blue-800">
                              <strong>Tipp:</strong> Ziehen Sie die Bilder, um die Reihenfolge zu ändern. Das erste Bild wird in der Aktenübersicht angezeigt.
                            </p>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                            {formData.photos.map((photo, idx) => (
                              <div 
                                key={idx} 
                                draggable
                                onDragStart={(e) => {
                                  e.dataTransfer.effectAllowed = 'move';
                                  e.dataTransfer.setData('text/plain', idx.toString());
                                  e.currentTarget.style.opacity = '0.5';
                                }}
                                onDragEnd={(e) => {
                                  e.currentTarget.style.opacity = '1';
                                }}
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  e.dataTransfer.dropEffect = 'move';
                                }}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const draggedIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
                                  if (!isNaN(draggedIdx) && draggedIdx !== idx) {
                                    setFormData((prevFormData) => {
                                      const newPhotos = [...prevFormData.photos];
                                      const draggedPhoto = newPhotos[draggedIdx];
                                      newPhotos.splice(draggedIdx, 1);
                                      newPhotos.splice(idx, 0, draggedPhoto);
                                      toast.success('Bild verschoben');
                                      return { ...prevFormData, photos: newPhotos };
                                    });
                                  }
                                }}
                                className="relative group cursor-move border-2 rounded-lg overflow-hidden hover:border-blue-400 transition-all"
                                style={{borderColor: idx === 0 ? '#3B82F6' : '#E1E4E8'}}
                              >
                                {idx === 0 && (
                                  <div className="absolute top-2 left-2 px-2 py-1 bg-blue-500 text-white text-xs font-bold rounded z-10">
                                    Titelbild
                                  </div>
                                )}
                                <img 
                                  src={getBackendUrl() + photo} 
                                  alt={`Foto ${idx + 1}`} 
                                  className="w-full h-32 object-cover cursor-pointer" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingImage(getBackendUrl() + photo);
                                    setEditingImageIndex(idx);
                                  }}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center">
                                  <div className="opacity-0 group-hover:opacity-100 flex gap-2">
                                    <button 
                                      type="button" 
                                      onClick={() => {
                                        setEditingImage(getBackendUrl() + photo);
                                        setEditingImageIndex(idx);
                                      }} 
                                      className="p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600"
                                      title="Bearbeiten"
                                      data-testid={`edit-photo-${idx}`}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </button>
                                    <button 
                                      type="button" 
                                      onClick={async () => {
                                        const newPhotos = formData.photos.filter((_, i) => i !== idx);
                                        setFormData({ ...formData, photos: newPhotos });
                                        toast.success('Bild gelöscht');
                                        // Sofort speichern
                                        if (caseId) {
                                          setTimeout(() => autoSaveCase(), 100);
                                        }
                                      }} 
                                      className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                                      title="Löschen"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                                <div className="absolute bottom-2 right-2 px-2 py-1 bg-gray-900 bg-opacity-75 text-white text-xs rounded">
                                  #{idx + 1}
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="flex items-center justify-between text-sm text-gray-600 pt-2">
                            <span>{formData.photos.length} Foto(s) hochgeladen</span>
                            {formData.photos.length > 0 && (
                              <span className="text-blue-600">
                                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                </svg>
                                Drag & Drop zum Sortieren
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Upload Box - JETZT UNTEN */}
                      <div 
                        className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all ${isDragging ? 'bg-blue-50 border-blue-400 scale-[1.02]' : 'bg-gray-50 border-gray-300'}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <input 
                          type="file" 
                          accept="image/*,video/*,.pdf,.zip,.rar,.7z" 
                          multiple 
                          onChange={handleFileUpload} 
                          className="hidden" 
                          id="photo-upload" 
                          data-testid="photo-upload-input" 
                        />
                        <label htmlFor="photo-upload" className="cursor-pointer block">
                          {/* Upload Icon */}
                          <div className="mb-6">
                            <div className="inline-flex flex-col items-center">
                              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-2 transition-colors ${isDragging ? 'bg-blue-500' : 'bg-blue-400'}`}>
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                              </div>
                              <div className={`w-1 h-8 transition-colors ${isDragging ? 'bg-blue-500' : 'bg-blue-400'}`}></div>
                            </div>
                          </div>

                          {/* Title & Subtitle */}
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">Schadensfotos & Dokumente hochladen</h4>
                          <p className="text-base text-gray-600 mb-6">Klicken zum Hochladen oder Drag & Drop</p>

                          {/* Supported Formats */}
                          <div className="space-y-2 text-sm text-gray-500">
                            <p className="font-medium text-gray-700">Alle gängigen Dateiformate:</p>
                            <p>Bilder (JPG, PNG, GIF, WEBP, BMP, TIFF, SVG, HEIC)</p>
                            <p>Videos (MP4, MOV, AVI, WEBM, MPEG, MKV)</p>
                            <p>Dokumente (PDF) • Archive (ZIP, RAR, 7Z)</p>
                            <p className="font-medium text-gray-700 mt-3">Max. 50MB pro Datei - Mehrere Dateien möglich</p>
                          </div>
                        </label>
                      </div>
                      </div>
                      )}

                      {/* Damage Info Tab */}
                      {formData.photoTab === 'damage' && (
                        <div className="space-y-6 pb-6">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-sm text-blue-800">
                              <strong>Anleitung:</strong> Klicken Sie auf die beschädigten Bereiche des Fahrzeugs. Ausgewählte Bereiche werden blau markiert.
                            </p>
                          </div>

                          {/* Damage Diagram */}
                          <div className="relative bg-gray-50 rounded-lg p-6 border-2" style={{borderColor: '#E1E4E8'}}>
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Fahrzeug-Schadendiagramm</h3>
                            <div className="relative max-w-2xl mx-auto">
                              <img 
                                src="/damage-diagram.png" 
                                alt="Fahrzeug Schadendiagramm"
                                className="w-full h-auto"
                              />
                              
                              {/* Clickable overlay buttons */}
                              <div className="absolute top-0 left-0 w-full h-full">
                                {/* Motorhaube */}
                                <button
                                  type="button"
                                  onClick={() => toggleDamageArea('Motorhaube')}
                                  className={`absolute transition-all rounded ${
                                    formData.damageAreas?.includes('Motorhaube') 
                                      ? 'bg-blue-500 bg-opacity-50 border-2 border-blue-700' 
                                      : 'bg-transparent hover:bg-blue-300 hover:bg-opacity-40 border border-transparent hover:border-blue-400'
                                  }`}
                                  style={{left: '38%', top: '8%', width: '24%', height: '18%'}}
                                  title="Motorhaube"
                                />
                                {/* Windschutzscheibe */}
                                <button
                                  type="button"
                                  onClick={() => toggleDamageArea('Windschutzscheibe')}
                                  className={`absolute transition-all rounded ${
                                    formData.damageAreas?.includes('Windschutzscheibe') 
                                      ? 'bg-blue-500 bg-opacity-50 border-2 border-blue-700' 
                                      : 'bg-transparent hover:bg-blue-300 hover:bg-opacity-40 border border-transparent hover:border-blue-400'
                                  }`}
                                  style={{left: '38%', top: '27%', width: '24%', height: '8%'}}
                                  title="Windschutzscheibe"
                                />
                                {/* Dach */}
                                <button
                                  type="button"
                                  onClick={() => toggleDamageArea('Dach')}
                                  className={`absolute transition-all rounded ${
                                    formData.damageAreas?.includes('Dach') 
                                      ? 'bg-blue-500 bg-opacity-50 border-2 border-blue-700' 
                                      : 'bg-transparent hover:bg-blue-300 hover:bg-opacity-40 border border-transparent hover:border-blue-400'
                                  }`}
                                  style={{left: '38%', top: '36%', width: '24%', height: '20%'}}
                                  title="Dach"
                                />
                                {/* Linke Vordertür */}
                                <button
                                  type="button"
                                  onClick={() => toggleDamageArea('Linke Vordertür')}
                                  className={`absolute transition-all rounded ${
                                    formData.damageAreas?.includes('Linke Vordertür') 
                                      ? 'bg-blue-500 bg-opacity-50 border-2 border-blue-700' 
                                      : 'bg-transparent hover:bg-blue-300 hover:bg-opacity-40 border border-transparent hover:border-blue-400'
                                  }`}
                                  style={{left: '13%', top: '36%', width: '24%', height: '20%'}}
                                  title="Linke Vordertür"
                                />
                                {/* Rechte Vordertür */}
                                <button
                                  type="button"
                                  onClick={() => toggleDamageArea('Rechte Vordertür')}
                                  className={`absolute transition-all rounded ${
                                    formData.damageAreas?.includes('Rechte Vordertür') 
                                      ? 'bg-blue-500 bg-opacity-50 border-2 border-blue-700' 
                                      : 'bg-transparent hover:bg-blue-300 hover:bg-opacity-40 border border-transparent hover:border-blue-400'
                                  }`}
                                  style={{left: '63%', top: '36%', width: '24%', height: '20%'}}
                                  title="Rechte Vordertür"
                                />
                                {/* Linke Hintertür */}
                                <button
                                  type="button"
                                  onClick={() => toggleDamageArea('Linke Hintertür')}
                                  className={`absolute transition-all rounded ${
                                    formData.damageAreas?.includes('Linke Hintertür') 
                                      ? 'bg-blue-500 bg-opacity-50 border-2 border-blue-700' 
                                      : 'bg-transparent hover:bg-blue-300 hover:bg-opacity-40 border border-transparent hover:border-blue-400'
                                  }`}
                                  style={{left: '13%', top: '57%', width: '24%', height: '20%'}}
                                  title="Linke Hintertür"
                                />
                                {/* Rechte Hintertür */}
                                <button
                                  type="button"
                                  onClick={() => toggleDamageArea('Rechte Hintertür')}
                                  className={`absolute transition-all rounded ${
                                    formData.damageAreas?.includes('Rechte Hintertür') 
                                      ? 'bg-blue-500 bg-opacity-50 border-2 border-blue-700' 
                                      : 'bg-transparent hover:bg-blue-300 hover:bg-opacity-40 border border-transparent hover:border-blue-400'
                                  }`}
                                  style={{left: '63%', top: '57%', width: '24%', height: '20%'}}
                                  title="Rechte Hintertür"
                                />
                                {/* Kofferraum/Heck */}
                                <button
                                  type="button"
                                  onClick={() => toggleDamageArea('Kofferraum/Heck')}
                                  className={`absolute transition-all rounded ${
                                    formData.damageAreas?.includes('Kofferraum/Heck') 
                                      ? 'bg-blue-500 bg-opacity-50 border-2 border-blue-700' 
                                      : 'bg-transparent hover:bg-blue-300 hover:bg-opacity-40 border border-transparent hover:border-blue-400'
                                  }`}
                                  style={{left: '38%', top: '78%', width: '24%', height: '18%'}}
                                  title="Kofferraum/Heck"
                                />
                              </div>
                            </div>
                          </div>

                            {/* Selected Areas List */}
                            {formData.damageAreas && formData.damageAreas.length > 0 && (
                              <div className="mt-6 p-4 bg-white rounded-lg border" style={{borderColor: '#E1E4E8'}}>
                                <h4 className="font-semibold text-gray-900 mb-3">Ausgewählte Schadensbereiche ({formData.damageAreas.length}):</h4>
                                <div className="flex flex-wrap gap-2">
                                  {formData.damageAreas.map((area, idx) => (
                                    <span
                                      key={idx}
                                      className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium flex items-center gap-2"
                                    >
                                      {area}
                                      <button
                                        type="button"
                                        onClick={() => toggleDamageArea(area)}
                                        className="hover:text-blue-900 font-bold"
                                      >
                                        ×
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* OLD CONTENT REMOVED */}
              
              {/* Step 5: Kalkulation & Rechnung - Basierend auf Benutzer-Modulen */}
              {currentStep === 5 && (
                <div className="animate-fade-in w-full">
                  <div className="bg-white rounded-lg shadow-lg border w-full" style={{borderColor: '#E1E4E8'}}>
                    {/* Header */}
                    <div className="p-6 rounded-t-lg" style={{backgroundColor: '#3B82F6'}}>
                      <div className="flex items-center gap-3 text-white">
                        <Calculator className="w-6 h-6" />
                        <h1 className="text-xl font-bold">Kalkulation & Rechnung</h1>
                      </div>
                    </div>

                    {/* Tabs - Nur anzeigen wenn mehrere Module vorhanden */}
                    {(() => {
                      const userModules = user?.modules || [];
                      const hasDatGlass = userModules.includes('dat_glass');
                      const hasDatMyClaim = userModules.includes('dat_myclaim');
                      const hasMultipleModules = (hasDatGlass && hasDatMyClaim) || userModules.length === 0;
                      
                      // Debug logging
                      console.log('User modules:', userModules);
                      console.log('hasDatGlass:', hasDatGlass);
                      console.log('hasDatMyClaim:', hasDatMyClaim);
                      console.log('calculationTab:', formData.calculationTab);
                      
                      // Wenn nur ein Modul, automatisch auswählen
                      const defaultTab = !hasMultipleModules 
                        ? (hasDatMyClaim ? 'soap' : hasDatGlass ? 'dat' : 'invoice')
                        : (formData.calculationTab || 'soap');
                      
                      return (
                        <>
                          {/* Tabs nur anzeigen wenn mehrere Module oder keine Module (Admin/Test) */}
                          {(hasMultipleModules || userModules.length === 0) && (
                            <div className="border-b" style={{borderColor: '#E1E4E8'}}>
                              <div className="flex flex-wrap">
                                {/* DAT myClaim Tab - nur wenn dat_myclaim Modul vorhanden oder keine Module (Admin) */}
                                {(hasDatMyClaim || userModules.length === 0) && (
                                  <button
                                    type="button"
                                    onClick={() => setFormData({...formData, calculationTab: 'soap'})}
                                    className={`px-6 py-3 font-medium transition-colors ${
                                      (!formData.calculationTab || formData.calculationTab === 'soap')
                                        ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                    }`}
                                    data-testid="tab-soap-kalkulation"
                                  >
                                    🚀 DAT Kalkulation
                                  </button>
                                )}
                                {/* Glaskalkulation Tab - nur wenn dat_glass Modul vorhanden oder keine Module (Admin) */}
                                {(hasDatGlass || userModules.length === 0) && (
                                  <button
                                    type="button"
                                    onClick={() => setFormData({...formData, calculationTab: 'dat'})}
                                    className={`px-6 py-3 font-medium transition-colors ${
                                      formData.calculationTab === 'dat'
                                        ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                    }`}
                                    data-testid="tab-dat-kalkulation"
                                  >
                                    📊 Glaskalkulation (iFrame)
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => setFormData({...formData, calculationTab: 'invoice'})}
                                  className={`px-6 py-3 font-medium transition-colors ${
                                    formData.calculationTab === 'invoice'
                                      ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                  }`}
                                  data-testid="tab-rechnung"
                                >
                                  📄 Rechnung
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Tab Content */}
                          <div className="p-8">
                            {/* TAB: SOAP-based DAT Calculation - Zeige NUR wenn:
                                - User hat dat_myclaim ODER
                                - User hat keine spezifischen Module (Admin) 
                                NICHT wenn User NUR dat_glass hat */}
                            {(() => {
                              const showSoap = (formData.calculationTab === 'soap' && hasDatMyClaim) ||
                                (!formData.calculationTab && hasDatMyClaim) ||
                                (!formData.calculationTab && userModules.length === 0);
                              console.log('SOAP show condition:', showSoap, 'hasDatMyClaim:', hasDatMyClaim, 'userModules.length:', userModules.length);
                              if (!showSoap) return null;
                              return (
                                <div className="max-w-4xl mx-auto">
                                  <DatContractForm
                                    type="glass"
                                    caseData={{
                                      customer_name: formData.customer_name || '',
                              customer_anrede: formData.customer_anrede || 'Herr',
                              customer_address: formData.customer_address || '',
                              customer_plz_ort: formData.customer_plz_ort || '',
                              customer_phone: formData.customer_phone || '',
                              customer_email: formData.customer_email || '',
                              vin: formData.vin || '',
                              license_plate: formData.license_plate || '',
                              mileage: formData.mileage || ''
                            }}
                            caseId={caseId}
                            caseNumber={caseNumber || formData.case_number}
                            onContractCreated={async (claimId, type) => {
                              setDatContractId(claimId);
                              toast.success(`DAT Contract ${claimId} erstellt!`);
                              
                              // Set datCalculationResult so the iFrame tab can use it
                              setDatCalculationResult(prev => ({
                                ...prev,
                                success: true,
                                contract_id: claimId
                              }));
                              
                              // Save to case
                              if (caseId) {
                                try {
                                  await fetch(`${getBackendUrl()}/api/cases/${caseId}`, {
                                    method: 'PUT',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      'Authorization': `Bearer ${localStorage.getItem('token')}`
                                    },
                                    body: JSON.stringify({
                                      ...formData,
                                      dat_contract_id: claimId,
                                      dat_contract_type: type
                                    })
                                  });
                                } catch (err) {
                                  console.error('Error saving contract ID:', err);
                                }
                              }
                              
                              // Try to get the iFrame URL for the contract
                              try {
                                const tokenResponse = await fetch(`${getBackendUrl()}/api/dat/token`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                                  },
                                  body: JSON.stringify({ product_variant: 'glassrep.agp' })
                                });
                                
                                if (tokenResponse.ok) {
                                  const tokenData = await tokenResponse.json();
                                  if (tokenData.iframe_url) {
                                    setDatCalculationResult(prev => ({
                                      ...prev,
                                      iframe_url: tokenData.iframe_url
                                    }));
                                  }
                                }
                              } catch (err) {
                                console.error('Error fetching iFrame URL:', err);
                              }
                            }}
                            onCalculationComplete={async (data, claimId, type) => {
                              console.log('📊 DAT Kalkulationsdaten erhalten:', data);
                              
                              if (data.calculation) {
                                setDatCalculationResult({
                                  success: true,
                                  calculation: data.calculation,
                                  contract_id: claimId
                                });
                                
                                // Convert DAT calculation to invoice positions
                                const positions = [];
                                
                                // Add material positions
                                if (data.calculation.materialPositions) {
                                  data.calculation.materialPositions.forEach((pos, idx) => {
                                    positions.push({
                                      id: `mat-${idx}`,
                                      description: pos.description || 'Material',
                                      partNumber: pos.partNumber || '',
                                      quantity: 1,
                                      unitPrice: parseFloat(pos.valueTotal) || 0,
                                      total: parseFloat(pos.valueTotal) || 0,
                                      type: 'material'
                                    });
                                  });
                                }
                                
                                // Add labour positions
                                if (data.calculation.labourPositions) {
                                  data.calculation.labourPositions.forEach((pos, idx) => {
                                    positions.push({
                                      id: `lab-${idx}`,
                                      description: pos.description || 'Arbeit',
                                      duration: pos.duration || '',
                                      quantity: 1,
                                      unitPrice: parseFloat(pos.valueTotal) || 0,
                                      total: parseFloat(pos.valueTotal) || 0,
                                      type: 'labour'
                                    });
                                  });
                                }
                                
                                // If no detailed positions but totals exist, create summary positions
                                if (positions.length === 0) {
                                  if (parseFloat(data.calculation.sparePartsTotal) > 0) {
                                    positions.push({
                                      id: 'spare-parts',
                                      description: 'Ersatzteile (DAT Kalkulation)',
                                      quantity: 1,
                                      unitPrice: parseFloat(data.calculation.sparePartsTotal),
                                      total: parseFloat(data.calculation.sparePartsTotal),
                                      type: 'material'
                                    });
                                  }
                                  if (parseFloat(data.calculation.labourTotal) > 0) {
                                    positions.push({
                                      id: 'labour',
                                      description: 'Arbeitslohn (DAT Kalkulation)',
                                      quantity: 1,
                                      unitPrice: parseFloat(data.calculation.labourTotal),
                                      total: parseFloat(data.calculation.labourTotal),
                                      type: 'labour'
                                    });
                                  }
                                  if (parseFloat(data.calculation.lacquerTotal) > 0) {
                                    positions.push({
                                      id: 'lacquer',
                                      description: 'Lackierung (DAT Kalkulation)',
                                      quantity: 1,
                                      unitPrice: parseFloat(data.calculation.lacquerTotal),
                                      total: parseFloat(data.calculation.lacquerTotal),
                                      type: 'material'
                                    });
                                  }
                                }
                                
                                // Update form data with positions
                                if (positions.length > 0) {
                                  setDatPositions(positions);
                                  setFormData(prev => ({
                                    ...prev,
                                    invoice_positions: positions,
                                    dat_contract_id: claimId
                                  }));
                                  
                                  // Calculate totals
                                  calculateTotalsFromPositions(positions);
                                  
                                  toast.success(`✅ ${positions.length} Positionen aus DAT übernommen!`);
                                } else {
                                  toast.success('Kalkulationsdaten erfolgreich abgerufen!');
                                }
                                
                                // Save to case
                                if (caseId) {
                                  try {
                                    await fetch(`${getBackendUrl()}/api/cases/${caseId}`, {
                                      method: 'PUT',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                                      },
                                      body: JSON.stringify({
                                        dat_contract_id: claimId,
                                        invoice_positions: positions,
                                        dat_calculation: data.calculation
                                      })
                                    });
                                  } catch (err) {
                                    console.error('Error saving positions:', err);
                                  }
                                }
                              }
                            }}
                            onClose={() => {
                              // Switch to invoice tab and show the positions
                              setFormData(prev => ({...prev, calculationTab: 'invoice'}));
                              // Navigate to Step 7 (Rechnung)
                              setCurrentStep(7);
                              toast.info('Zur Rechnung gewechselt');
                            }}
                          />
                                </div>
                              );
                            })()}

                            {/* TAB 1: Glaskalkulation (iFrame) - wenn dat_glass ODER (keine Module UND dat Tab ausgewählt) ODER nur dat_glass Modul */}
                            {((formData.calculationTab === 'dat' || (!formData.calculationTab && hasDatGlass && !hasDatMyClaim)) && (hasDatGlass || userModules.length === 0)) && (
                        <div className="max-w-4xl mx-auto space-y-6">
                          
                          {/* Fahrzeuginfo Card */}
                          <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-6">
                            <h3 className="text-lg font-bold text-blue-900 mb-4">Fahrzeugdaten</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              {formData.vin && (
                                <div>
                                  <p className="text-xs text-blue-700 font-medium mb-1">VIN</p>
                                  <p className="text-sm font-mono text-blue-900">{formData.vin}</p>
                                </div>
                              )}
                              {formData.license_plate && (
                                <div>
                                  <p className="text-xs text-blue-700 font-medium mb-1">Kennzeichen</p>
                                  <p className="text-sm font-semibold text-blue-900">{formData.license_plate}</p>
                                </div>
                              )}
                              {formData.manufacturer && (
                                <div>
                                  <p className="text-xs text-blue-700 font-medium mb-1">Marke</p>
                                  <p className="text-sm text-blue-900">{formData.manufacturer}</p>
                                </div>
                              )}
                              {formData.model && (
                                <div>
                                  <p className="text-xs text-blue-700 font-medium mb-1">Modell</p>
                                  <p className="text-sm text-blue-900">{formData.model}</p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Loading State */}
                          {loadingCalculation && (
                            <div className="bg-white border-2 border-blue-300 rounded-xl p-12 text-center">
                              <div className="flex flex-col items-center gap-4">
                                <svg className="animate-spin h-16 w-16 text-blue-600" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <div>
                                  <h3 className="text-xl font-bold text-gray-900 mb-2">Glaskalkulation wird erstellt...</h3>
                                  <p className="text-sm text-gray-600">Dies kann einige Sekunden dauern.</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Error State */}
                          {calculationError && !loadingCalculation && (
                            <div className="bg-red-50 border-2 border-red-300 rounded-xl p-8">
                              <div className="flex items-start gap-4">
                                <svg className="w-8 h-8 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <div className="flex-1">
                                  <h3 className="text-lg font-bold text-red-900 mb-2">Fehler bei der Kalkulation</h3>
                                  <p className="text-sm text-red-700">{calculationError}</p>
                                  <div className="flex gap-3 mt-4">
                                    <button
                                      onClick={() => setCalculationError(null)}
                                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium"
                                    >
                                      🔄 Erneut versuchen
                                    </button>
                                    {calculationError.toLowerCase().includes('credentials') && (
                                      <a
                                        href="/settings"
                                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium inline-flex items-center gap-2"
                                      >
                                        ⚙️ Zu den Einstellungen
                                      </a>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* DAT-Zugang nicht konfiguriert - Warnung */}
                          {datCredentialsStatus === false && !loadingCalculation && (
                            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-8">
                              <div className="flex items-start gap-4">
                                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                                  <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                  </svg>
                                </div>
                                <div className="flex-1">
                                  <h3 className="text-xl font-bold text-amber-900 mb-2">
                                    ⚠️ DAT-Zugang nicht konfiguriert
                                  </h3>
                                  <p className="text-amber-800 mb-4">
                                    Ihr Account hat noch keinen aktiven DAT-Zugang. Die DAT-Kalkulation wird derzeit als <strong>Demo-Ansicht</strong> angezeigt.
                                  </p>
                                  <div className="bg-white/50 rounded-lg p-4 mb-4">
                                    <h4 className="font-semibold text-amber-900 mb-2">So erhalten Sie Zugang:</h4>
                                    <ol className="list-decimal list-inside text-amber-800 space-y-1 text-sm">
                                      <li>Kontaktieren Sie Ihren Administrator</li>
                                      <li>Der Administrator konfiguriert Ihre DAT-Zugangsdaten im Super Admin Panel</li>
                                      <li>Nach der Freigabe können Sie die DAT-Kalkulation nutzen</li>
                                    </ol>
                                  </div>
                                  <div className="flex gap-3">
                                    <a 
                                      href="/settings" 
                                      className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm font-medium"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      </svg>
                                      Einstellungen öffnen
                                    </a>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Demo Preview */}
                              <div className="mt-6 border-t border-amber-200 pt-6">
                                <p className="text-sm text-amber-700 mb-3 flex items-center gap-2">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Demo-Vorschau (nicht funktionsfähig)
                                </p>
                                <div className="bg-white rounded-lg border border-amber-200 p-8 opacity-50">
                                  <div className="text-center text-gray-400">
                                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                      <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                    </div>
                                    <p className="font-medium">DAT myClaim Kalkulation</p>
                                    <p className="text-sm">Wird nach Freigabe durch Administrator verfügbar</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* DAT Credentials Formular - nur wenn manuell eingegeben werden kann */}
                          {datCredentialsNeeded && datCredentialsStatus !== false && !loadingCalculation && (
                            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-xl p-8">
                              <div className="flex items-start gap-4 mb-6">
                                <div className="w-14 h-14 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                  </svg>
                                </div>
                                <div>
                                  <h3 className="text-xl font-bold text-yellow-900">DAT-Zugangsdaten erforderlich</h3>
                                  <p className="text-sm text-yellow-800 mt-1">
                                    Bitte geben Sie Ihre DAT myClaim Zugangsdaten ein. Diese werden automatisch gespeichert.
                                  </p>
                                </div>
                              </div>
                              
                              <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">DAT-Kundennummer *</label>
                                    <input
                                      type="text"
                                      value={datCredentialsForm.customerNumber}
                                      onChange={(e) => setDatCredentialsForm({...datCredentialsForm, customerNumber: e.target.value})}
                                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                                      placeholder="z.B. 1348925"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Benutzer *</label>
                                    <input
                                      type="text"
                                      value={datCredentialsForm.userLogin}
                                      onChange={(e) => setDatCredentialsForm({...datCredentialsForm, userLogin: e.target.value})}
                                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                                      placeholder="Ihr DAT-Benutzername"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Passwort *</label>
                                    <input
                                      type="password"
                                      value={datCredentialsForm.signatureKey}
                                      onChange={(e) => setDatCredentialsForm({...datCredentialsForm, signatureKey: e.target.value})}
                                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                                      placeholder="Ihr DAT-Passwort"
                                    />
                                  </div>
                                </div>
                                
                                <div className="flex gap-4 mt-6">
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      if (!datCredentialsForm.customerNumber || !datCredentialsForm.userLogin || !datCredentialsForm.signatureKey) {
                                        toast.error('Bitte alle Felder ausfüllen');
                                        return;
                                      }
                                      
                                      setSavingDatCredentials(true);
                                      
                                      try {
                                        // Speichere die Credentials
                                        const saveResponse = await fetch(
                                          `${getBackendUrl()}/api/settings/dat-myclaim`,
                                          {
                                            method: 'POST',
                                            headers: {
                                              'Content-Type': 'application/json',
                                              'Authorization': `Bearer ${localStorage.getItem('token')}`
                                            },
                                            body: JSON.stringify({
                                              customerNumber: datCredentialsForm.customerNumber,
                                              userLogin: datCredentialsForm.userLogin,
                                              signatureKey: datCredentialsForm.signatureKey,
                                              interfacePartnerNumber: '',
                                              interfacePartnerSignature: '',
                                              productVariant: 'glassrep.agp'
                                            })
                                          }
                                        );
                                        
                                        if (saveResponse.ok) {
                                          toast.success('✅ DAT-Zugangsdaten gespeichert!');
                                          
                                          // Jetzt direkt das iFrame öffnen
                                          const iframeResponse = await fetch(
                                            `${getBackendUrl()}/api/dat/open-template-selection`,
                                            {
                                              method: 'POST',
                                              headers: {
                                                'Content-Type': 'application/json',
                                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                                              },
                                              body: JSON.stringify({})
                                            }
                                          );
                                          
                                          if (iframeResponse.ok) {
                                            const data = await iframeResponse.json();
                                            if (data.success && data.iframe_url) {
                                              setDatCalculationResult({
                                                success: true,
                                                requires_manual_calculation: true,
                                                iframe_url: data.iframe_url,
                                                message: 'Wählen Sie eine Vorlage im DAT-Fenster'
                                              });
                                              setDatCredentialsNeeded(false);
                                              toast.success('✅ DAT myClaim geöffnet');
                                            }
                                          } else {
                                            const error = await iframeResponse.json();
                                            toast.error('❌ ' + (error.detail || 'Fehler beim Öffnen'));
                                          }
                                        } else {
                                          const error = await saveResponse.json();
                                          toast.error('❌ ' + (error.detail || 'Fehler beim Speichern'));
                                        }
                                      } catch (error) {
                                        console.error('Fehler:', error);
                                        toast.error('❌ Verbindungsfehler');
                                      } finally {
                                        setSavingDatCredentials(false);
                                      }
                                    }}
                                    disabled={savingDatCredentials}
                                    className="flex-1 px-6 py-4 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-400 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                                  >
                                    {savingDatCredentials ? (
                                      <>
                                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Wird gespeichert...
                                      </>
                                    ) : (
                                      <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Speichern & DAT öffnen
                                      </>
                                    )}
                                  </button>
                                  
                                  <button
                                    type="button"
                                    onClick={() => setDatCredentialsNeeded(false)}
                                    className="px-6 py-4 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-medium"
                                  >
                                    Abbrechen
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Template Selection: Vorlagen-Auswahl iFrame (ohne contract_id) */}
                          {datCalculationResult && datCalculationResult.iframe_url && !datCalculationResult.contract_id && !loadingCalculation && !calculationError && (
                            <div className="space-y-6">
                              {/* Info Banner */}
                              <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-6">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                                    <FileText className="w-7 h-7 text-white" />
                                  </div>
                                  <div>
                                    <h3 className="text-lg font-bold text-blue-900">DAT myClaim geöffnet</h3>
                                    <p className="text-sm text-blue-700 mt-1">
                                      Wählen Sie eine Vorlage (Glaskalkulation oder Schadenkalkulation) im Fenster unten.
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* DAT iFrame für Vorlagen-Auswahl */}
                              <div className="border-2 border-blue-300 rounded-lg overflow-hidden" style={{ height: '700px' }}>
                                <iframe
                                  src={datCalculationResult.iframe_url}
                                  style={{ width: '100%', height: '100%', border: 'none' }}
                                  title="DAT myClaim - Vorlage wählen"
                                  data-testid="dat-template-iframe"
                                />
                              </div>
                              
                              {/* Button zum Zurücksetzen */}
                              <div className="flex justify-center">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setDatCalculationResult(null);
                                    setCalculationError(null);
                                  }}
                                  className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-medium"
                                >
                                  Abbrechen und zurück
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Success: Kalkulation abgeschlossen */}
                          {datCalculationResult && datCalculationResult.contract_id && !loadingCalculation && !calculationError && (
                            <div className="space-y-6">
                              {/* DAT iFrame für Kalkulation - OBEN und GRÖSSER */}
                              {datCalculationResult.iframe_url && (
                                <div className="space-y-4">
                                  <div className="border-2 border-blue-300 rounded-lg overflow-hidden" style={{ height: '800px' }}>
                                    <iframe
                                      src={datCalculationResult.iframe_url}
                                      style={{ width: '100%', height: '100%', border: 'none' }}
                                      title="DAT myClaim Glaskalkulation"
                                      data-testid="dat-iframe"
                                    />
                                  </div>
                                  
                                  {/* Button: Kalkulation übernehmen */}
                                  <div className="flex justify-center gap-3">
                                    <button
                                      type="button"
                                      onClick={async (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        
                                        setLoadingCalculation(true);
                                        
                                        try {
                                          console.log('🚀 Schneller DAT-Abruf gestartet...');
                                          
                                          // Direkter Abruf ohne Polling-Verzögerung beim ersten Versuch
                                          const response = await fetch(
                                            `${getBackendUrl()}/api/dat/fetch-glass-calculation`,
                                            {
                                              method: 'POST',
                                              headers: {
                                                'Content-Type': 'application/json',
                                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                                              },
                                              body: JSON.stringify({
                                                contract_id: datCalculationResult.contract_id,
                                                case_id: caseId
                                              })
                                            }
                                          );
                                          
                                          if (response.ok) {
                                            const data = await response.json();
                                            
                                            if (data.success) {
                                              console.log('✅ Daten sofort erhalten!');
                                              
                                              // Sofortige UI-Aktualisierung - alles in einem Schritt
                                              setFormData(prev => ({
                                                ...prev,
                                                invoice_positions: data.positions,
                                                dat_pdf_url: data.pdf_url,
                                                calculationTab: 'invoice'  // Sofort zum Rechnung-Tab
                                              }));
                                              
                                              toast.success(`✅ ${data.positions.length} Positionen übernommen!`);
                                            } else {
                                              // Nur wenn wirklich nicht bereit: kurzes Retry
                                              console.log('⏳ Nicht sofort bereit, kurzes Retry...');
                                              toast.info('⏳ DAT verarbeitet noch...', { duration: 2000 });
                                              
                                              await new Promise(resolve => setTimeout(resolve, 1500));
                                              
                                              const retryResponse = await fetch(
                                                `${getBackendUrl()}/api/dat/fetch-glass-calculation`,
                                                {
                                                  method: 'POST',
                                                  headers: {
                                                    'Content-Type': 'application/json',
                                                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                                                  },
                                                  body: JSON.stringify({
                                                    contract_id: datCalculationResult.contract_id,
                                                    case_id: caseId
                                                  })
                                                }
                                              );
                                              
                                              if (retryResponse.ok) {
                                                const retryData = await retryResponse.json();
                                                if (retryData.success) {
                                                  setFormData(prev => ({
                                                    ...prev,
                                                    invoice_positions: retryData.positions,
                                                    dat_pdf_url: retryData.pdf_url,
                                                    calculationTab: 'invoice'
                                                  }));
                                                  toast.success(`✅ ${retryData.positions.length} Positionen übernommen!`);
                                                } else {
                                                  toast.error('❌ Kalkulation nicht gefunden. Wurde sie in DAT gespeichert?');
                                                }
                                              }
                                            }
                                          } else {
                                            toast.error('❌ Verbindungsfehler zu DAT');
                                          }
                                          
                                        } catch (error) {
                                          console.error('❌ Exception:', error);
                                          toast.error('Verbindungsfehler');
                                        } finally {
                                          setLoadingCalculation(false);
                                        }
                                      }}
                                      disabled={loadingCalculation}
                                      className="px-8 py-4 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-xl font-bold text-lg flex items-center gap-3 shadow-lg hover:shadow-xl transition-all"
                                    >
                                      {loadingCalculation ? (
                                        <>
                                          <svg className="animate-spin h-6 w-6" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                          </svg>
                                          Wird übernommen...
                                        </>
                                      ) : (
                                        <>
                                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                          </svg>
                                          Kalkulation übernehmen
                                        </>
                                      )}
                                    </button>
                                  </div>
                                  
                                  {/* Informationen UNTER dem iFrame */}
                                  <div className="mt-6 space-y-4">
                                    {/* Contract Info */}
                                    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                                      <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                        </div>
                                        <div>
                                          <h3 className="font-bold text-green-900">Contract erstellt</h3>
                                          <p className="text-sm text-green-700">
                                            Aktennummer: <span className="font-mono font-bold">{caseNumber}</span> | 
                                            DAT Contract: <span className="font-mono">{datCalculationResult.contract_id}</span>
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Anleitung */}
                                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                                      <div className="flex items-start gap-3">
                                        <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <div className="flex-1">
                                          <h4 className="text-sm font-bold text-yellow-900 mb-2">📋 So führen Sie die Glaskalkulation durch:</h4>
                                          <ol className="text-xs text-yellow-800 space-y-1 list-decimal list-inside">
                                            <li>Klicken Sie auf Ihren Auftrag in der DAT-Liste</li>
                                            <li>Wählen Sie die Glasposition aus</li>
                                            <li><strong>Speichern Sie in DAT!</strong></li>
                                            <li>Klicken Sie oben auf "Kalkulation übernehmen"</li>
                                          </ol>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Start Button - Initial State */}
                          {!datCalculationResult && !loadingCalculation && !calculationError && (
                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 rounded-xl p-12 text-center">
                              <div className="flex flex-col items-center gap-6">
                                <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
                                  <Calculator className="w-10 h-10 text-white" />
                                </div>
                                <div className="max-w-lg">
                                  <h3 className="text-2xl font-bold text-gray-900 mb-3">DAT Kalkulation starten</h3>
                                  <p className="text-sm text-gray-700 leading-relaxed mb-6">
                                    Wählen Sie zwischen Glaskalkulation und Schadenkalkulation.
                                    Alle Fahrzeugdaten und Kundeninformationen werden automatisch übermittelt.
                                  </p>
                                  
                                  {/* Hauptbutton: Vorlage wählen - zeigt DAT Popup */}
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      setLoadingCalculation(true);
                                      setCalculationError(null);
                                      
                                      try {
                                        const response = await fetch(
                                          `${getBackendUrl()}/api/dat/open-template-selection`,
                                          {
                                            method: 'POST',
                                            headers: {
                                              'Content-Type': 'application/json',
                                              'Authorization': `Bearer ${localStorage.getItem('token')}`
                                            },
                                            body: JSON.stringify({})
                                          }
                                        );
                                        
                                        if (response.ok) {
                                          const data = await response.json();
                                          console.log('📊 DAT Template Selection URL:', data);
                                          
                                          if (data.success && data.iframe_url) {
                                            setDatCalculationResult({
                                              success: true,
                                              requires_manual_calculation: true,
                                              iframe_url: data.iframe_url,
                                              message: 'Wählen Sie eine Vorlage im DAT-Fenster'
                                            });
                                            setDatCredentialsNeeded(false);
                                            toast.success('✅ DAT myClaim geöffnet - bitte Vorlage wählen');
                                          } else {
                                            setCalculationError(data.message || 'Fehler beim Öffnen');
                                            toast.error('❌ ' + (data.message || 'Fehler'));
                                          }
                                        } else {
                                          const error = await response.json();
                                          // Prüfe ob Credentials fehlen
                                          if (error.detail && (error.detail.includes('Credentials') || error.detail.includes('credentials') || error.detail.includes('hinterlegt'))) {
                                            setDatCredentialsNeeded(true);
                                            toast.info('🔑 Bitte geben Sie Ihre DAT-Zugangsdaten ein');
                                          } else {
                                            setCalculationError(error.detail || 'Fehler');
                                            toast.error('❌ ' + (error.detail || 'Fehler'));
                                          }
                                        }
                                      } catch (error) {
                                        console.error('Fehler:', error);
                                        setCalculationError('Verbindungsfehler');
                                        toast.error('❌ Verbindungsfehler');
                                      } finally {
                                        setLoadingCalculation(false);
                                      }
                                    }}
                                    disabled={loadingCalculation}
                                    className="w-full px-8 py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3 mb-4"
                                  >
                                    <FileText className="w-6 h-6" />
                                    DAT Kalkulation öffnen (Vorlage wählen)
                                  </button>
                                  
                                  {/* Sekundärer Button: Direkte Glaskalkulation */}
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      // Trigger the auto-start logic manually
                                      setLoadingCalculation(true);
                                      setCalculationError(null);
                                      
                                      try {
                                        const response = await fetch(
                                          `${getBackendUrl()}/api/dat/calculate-glass-auto`,
                                          {
                                            method: 'POST',
                                            headers: {
                                              'Content-Type': 'application/json',
                                              'Authorization': `Bearer ${localStorage.getItem('token')}`
                                            },
                                            body: JSON.stringify({
                                              case_id: caseId,
                                              case_number: caseNumber,
                                              vin: formData.vin,
                                              license_plate: formData.license_plate,
                                              manufacturer: formData.manufacturer,
                                              model: formData.model,
                                              customer_name: formData.customer_name,
                                              damage_location: formData.damage_location || 'Windschutzscheibe',
                                              damage_description: formData.damage_description || 'Glasschaden',
                                              customer_address: formData.customer_address,
                                              customer_city: formData.customer_plz_ort ? formData.customer_plz_ort.split(' ').slice(1).join(' ') : '',
                                              customer_zip: formData.customer_plz_ort ? formData.customer_plz_ort.split(' ')[0] : '',
                                              customer_phone: formData.customer_phone,
                                              customer_email: formData.customer_email,
                                              mileage: formData.mileage,
                                              first_registration: formData.first_registration
                                            })
                                          }
                                        );
                                        
                                        if (response.ok) {
                                          const data = await response.json();
                                          
                                          console.log('📊 Glaskalkulation Response:', data);
                                          
                                          if (data.success) {
                                            console.log('✅ Setting datCalculationResult:', {
                                              contract_id: data.contract_id,
                                              requires_manual: data.requires_manual_calculation
                                            });
                                            
                                            setDatCalculationResult(data);
                                            setDatContractId(data.contract_id);
                                            
                                            // Wenn manuelle Kalkulation erforderlich
                                            if (data.requires_manual_calculation) {
                                              toast.success('✅ Contract erstellt! Bitte führen Sie die Kalkulation in DAT durch.');
                                            } else {
                                              // Auto-fill invoice positions (falls direkt verfügbar)
                                              if (data.positions && data.positions.length > 0) {
                                                // Sofortige Aktualisierung in einem Schritt
                                                setFormData(prev => ({
                                                  ...prev,
                                                  invoice_positions: data.positions,
                                                  dat_pdf_url: data.pdf_url,
                                                  calculationTab: 'invoice'  // Sofort wechseln
                                                }));
                                                
                                                toast.success(`✅ ${data.positions.length} Positionen übernommen!`);
                                              }
                                            }
                                          } else {
                                            setCalculationError(data.message || 'Kalkulation fehlgeschlagen');
                                            toast.error('❌ ' + (data.message || 'Fehler bei der Kalkulation'));
                                          }
                                        } else {
                                          const error = await response.json();
                                          setCalculationError(error.detail || 'Fehler');
                                          toast.error('❌ ' + (error.detail || 'Fehler'));
                                        }
                                      } catch (error) {
                                        console.error('Fehler:', error);
                                        setCalculationError('Verbindungsfehler');
                                        toast.error('❌ Verbindungsfehler');
                                      } finally {
                                        setLoadingCalculation(false);
                                      }
                                    }}
                                    disabled={loadingCalculation}
                                    className="w-full px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-xl font-medium text-base shadow hover:shadow-lg transition-all flex items-center justify-center gap-2"
                                  >
                                    <Calculator className="w-5 h-5" />
                                    Schnell: Glaskalkulation (mit Fahrzeugdaten)
                                  </button>
                                  
                                  {/* Trennlinie */}
                                  <div className="flex items-center gap-4 my-6">
                                    <div className="flex-1 h-px bg-gray-300"></div>
                                    <span className="text-sm text-gray-500 font-medium">oder</span>
                                    <div className="flex-1 h-px bg-gray-300"></div>
                                  </div>
                                  
                                  {/* PDF Upload Buttons - Schnell und KI */}
                                  <div className="text-center">
                                    <p className="text-sm text-gray-600 mb-4">
                                      Haben Sie bereits eine DAT-Kalkulation als PDF?
                                    </p>
                                    
                                    <div className="flex flex-wrap justify-center gap-3">
                                      {/* Quick-Scan Button (Schnell) */}
                                      <label className="inline-flex items-center gap-2 px-5 py-3 bg-green-50 hover:bg-green-100 border-2 border-green-300 rounded-xl cursor-pointer transition-all group">
                                        <input
                                          type="file"
                                          accept=".pdf"
                                          className="hidden"
                                          disabled={loadingCalculation}
                                          onChange={async (e) => {
                                            const file = e.target.files?.[0];
                                            if (!file) return;
                                            
                                            if (!file.name.toLowerCase().endsWith('.pdf')) {
                                              toast.error('Nur PDF-Dateien sind erlaubt');
                                              return;
                                            }
                                            
                                            setLoadingCalculation(true);
                                            const toastId = toast.loading('⚡ Schnell-Scan läuft...');
                                            
                                            try {
                                              const formDataUpload = new FormData();
                                              formDataUpload.append('file', file);
                                              
                                              const response = await fetch(
                                                `${getBackendUrl()}/api/cases/${caseId}/quick-scan-pdf`,
                                                {
                                                  method: 'POST',
                                                  headers: {
                                                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                                                  },
                                                  body: formDataUpload
                                                }
                                              );
                                              
                                              if (response.ok) {
                                                const data = await response.json();
                                                
                                                if (data.success && data.calculation) {
                                                  const positions = data.calculation.parts.map((part, idx) => ({
                                                    id: `quick-${idx}`,
                                                    description: part.description,
                                                    quantity: part.quantity,
                                                    unit_price: part.unit_price,
                                                    total_price: part.total_price
                                                  }));
                                                  
                                                  setFormData(prev => ({
                                                    ...prev,
                                                    invoice_positions: positions,
                                                    dat_pdf_url: data.pdf_url,
                                                    calculationTab: 'invoice'
                                                  }));
                                                  
                                                  toast.success(`⚡ ${positions.length} Positionen in <1s!`, { id: toastId });
                                                } else {
                                                  toast.error(data.message || 'Keine Positionen gefunden', { id: toastId });
                                                }
                                              } else {
                                                const error = await response.json();
                                                toast.error(error.detail || 'Fehler', { id: toastId });
                                              }
                                            } catch (error) {
                                              toast.error('Verbindungsfehler', { id: toastId });
                                            } finally {
                                              setLoadingCalculation(false);
                                              e.target.value = '';
                                            }
                                          }}
                                        />
                                        <Zap className="w-5 h-5 text-green-600" />
                                        <div className="text-left">
                                          <span className="text-green-700 font-semibold block">Schnell-Scan</span>
                                          <span className="text-xs text-green-600">&lt;1 Sekunde</span>
                                        </div>
                                      </label>
                                      
                                      {/* KI-Scan Button (Genauer) */}
                                      <label className="inline-flex items-center gap-2 px-5 py-3 bg-blue-50 hover:bg-blue-100 border-2 border-blue-300 rounded-xl cursor-pointer transition-all group">
                                        <input
                                          type="file"
                                          accept=".pdf"
                                          className="hidden"
                                          disabled={loadingCalculation}
                                          onChange={async (e) => {
                                            const file = e.target.files?.[0];
                                            if (!file) return;
                                            
                                            if (!file.name.toLowerCase().endsWith('.pdf')) {
                                              toast.error('Nur PDF-Dateien sind erlaubt');
                                              return;
                                            }
                                            
                                            setLoadingCalculation(true);
                                            const toastId = toast.loading('🤖 KI analysiert...', {
                                              description: 'Dauert ca. 10 Sekunden'
                                            });
                                            
                                            try {
                                              const formDataUpload = new FormData();
                                              formDataUpload.append('file', file);
                                              
                                              const response = await fetch(
                                                `${getBackendUrl()}/api/cases/${caseId}/scan-kalkulation-pdf`,
                                                {
                                                  method: 'POST',
                                                  headers: {
                                                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                                                  },
                                                  body: formDataUpload
                                                }
                                              );
                                              
                                              if (response.ok) {
                                                const data = await response.json();
                                                
                                                if (data.success && data.calculation) {
                                                  const positions = data.calculation.parts.map((part, idx) => ({
                                                    id: `ocr-${idx}`,
                                                    description: part.description,
                                                    quantity: part.quantity,
                                                    unit_price: part.unit_price,
                                                    total_price: part.total_price
                                                  }));
                                                  
                                                  setFormData(prev => ({
                                                    ...prev,
                                                    invoice_positions: positions,
                                                    dat_pdf_url: data.pdf_url,
                                                    calculationTab: 'invoice'
                                                  }));
                                                  
                                                  toast.success(`🤖 ${positions.length} Positionen (KI)`, { id: toastId });
                                                } else {
                                                  toast.error(data.message || 'Keine Positionen gefunden', { id: toastId });
                                                }
                                              } else {
                                                const error = await response.json();
                                                toast.error(error.detail || 'Fehler', { id: toastId });
                                              }
                                            } catch (error) {
                                              toast.error('Verbindungsfehler', { id: toastId });
                                            } finally {
                                              setLoadingCalculation(false);
                                              e.target.value = '';
                                            }
                                          }}
                                        />
                                        <Brain className="w-5 h-5 text-blue-600" />
                                        <div className="text-left">
                                          <span className="text-blue-700 font-semibold block">KI-Scan</span>
                                          <span className="text-xs text-blue-600">Genauer, ~10s</span>
                                        </div>
                                      </label>
                                    </div>
                                    
                                    <p className="text-xs text-gray-500 mt-3">
                                      Schnell-Scan für einfache PDFs • KI-Scan für komplexe Dokumente
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* TAB 2: Rechnung */}
                      {formData.calculationTab === 'invoice' && (
                        <div className="space-y-6">
                          
                          {/* DAT-PDF zur Kontrolle */}
                          {formData.dat_pdf_url ? (
                            <div>
                              <div className="mb-4">
                                <h2 className="text-xl font-bold text-gray-900">📄 DAT-Kalkulationsdokument</h2>
                                <p className="text-sm text-gray-600">Offizielle Glaskalkulation zur Überprüfung</p>
                              </div>
                              
                              <div className="bg-gray-100 border-2 border-gray-300 rounded-lg p-2">
                                <embed
                                  src={`${getBackendUrl()}${formData.dat_pdf_url}`}
                                  type="application/pdf"
                                  width="100%"
                                  height="800px"
                                  className="rounded"
                                />
                              </div>
                              
                              <div className="mt-4 flex justify-center">
                                <a
                                  href={`${getBackendUrl()}${formData.dat_pdf_url}`}
                                  download={`DAT_Kalkulation_${caseNumber}.pdf`}
                                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors shadow-lg"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  DAT-Kalkulation herunterladen
                                </a>
                              </div>
                            </div>
                          ) : (
                            <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-dashed border-gray-300 rounded-xl p-16 text-center">
                              <div className="flex flex-col items-center gap-4">
                                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-md">
                                  <FileText className="w-10 h-10 text-gray-400" />
                                </div>
                                <div className="max-w-lg">
                                  <h3 className="text-xl font-bold text-gray-900 mb-2">Noch keine Kalkulation</h3>
                                  <p className="text-sm text-gray-600 leading-relaxed">
                                    Wechseln Sie zum Tab <strong className="text-blue-600">"📊 Glaskalkulation"</strong> und erstellen Sie die Kalkulation in DAT. 
                                    Nach "Kalkulation übernehmen" erscheint hier die PDF.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                      );
                    })()}
                  </div>
                </div>
              )}
              {/* Step 6: Übersicht */}
              {currentStep === 6 && (
                <div className="animate-fade-in w-full">
                  <div className="bg-white rounded-lg shadow-lg border w-full" style={{borderColor: '#E1E4E8'}}>
                    <div className="p-6 rounded-t-lg" style={{backgroundColor: '#3B82F6'}}>
                      <div className="flex items-center gap-3 text-white">
                        <FileText className="w-6 h-6" />
                        <h1 className="text-xl font-bold">Übersicht</h1>
                      </div>
                    </div>

                    {/* A4 Blatt Container */}
                    <div className="p-6 m-0">
                      {/* Zoom & Print Controls */}
                      <div className="mb-4 no-print flex justify-between items-center">
                        {/* Zoom Controls */}
                        <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1" data-testid="zoom-controls">
                          <button
                            onClick={() => {
                              const doc = document.getElementById('printable-documentation');
                              const transformMatch = doc.style.transform ? doc.style.transform.match(/scale\(([\d.]+)\)/) : null;
                              const currentScale = transformMatch ? parseFloat(transformMatch[1]) : 1;
                              const newScale = Math.max(0.5, currentScale - 0.1);
                              doc.style.transform = 'scale(' + newScale + ')';
                              doc.style.transformOrigin = 'top center';
                            }}
                            className="flex items-center gap-1 px-3 py-2 bg-white text-gray-700 rounded-md hover:bg-gray-200 transition-colors font-medium"
                            type="button"
                            title="Verkleinern"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                            </svg>
                            <span>−</span>
                          </button>
                          
                          <button
                            onClick={() => {
                              const doc = document.getElementById('printable-documentation');
                              doc.style.transform = 'scale(1)';
                              doc.style.transformOrigin = 'top center';
                            }}
                            className="px-3 py-2 bg-white text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm font-medium"
                            type="button"
                            title="Zurücksetzen"
                          >
                            100%
                          </button>
                          
                          <button
                            onClick={() => {
                              const doc = document.getElementById('printable-documentation');
                              const transformMatch = doc.style.transform ? doc.style.transform.match(/scale\(([\d.]+)\)/) : null;
                              const currentScale = transformMatch ? parseFloat(transformMatch[1]) : 1;
                              const newScale = Math.min(2, currentScale + 0.1);
                              doc.style.transform = 'scale(' + newScale + ')';
                              doc.style.transformOrigin = 'top center';
                            }}
                            className="flex items-center gap-1 px-3 py-2 bg-white text-gray-700 rounded-md hover:bg-gray-200 transition-colors font-medium"
                            type="button"
                            title="Vergrößern"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                            <span>+</span>
                          </button>
                        </div>
                        
                        {/* Print Button */}
                        <button
                          onClick={() => window.print()}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                          type="button"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                          </svg>
                          <span>Drucken</span>
                        </button>
                      </div>
                      
                      {/* Scrollbarer Container für gezoomte Ansicht */}
                      <div className="overflow-auto" style={{maxHeight: '90vh'}}>
                        {/* DIN-A4 Container */}
                        <div className="bg-white shadow-xl mx-auto transition-transform duration-200" id="printable-documentation" style={{width: '210mm', minHeight: '297mm'}}>
                    {/* Dokumentation Inhalt - DIN-A4-Format */}
                    <div className="p-12 space-y-8">
                      
                      {/* Firmen-Kopf */}
                      <div className="border-b-2 border-blue-600 pb-6 mb-8">
                        <div className="flex justify-between items-start gap-8">
                          {/* Linke Seite: Firmeninfo */}
                          <div className="flex-1 text-left">
                            <h1 className="text-2xl font-bold text-blue-900">{companyData?.company_name || 'Easyclaim24 Lackierbetrieb'}</h1>
                            <p className="text-xs text-gray-600 mt-1">{companyData?.website || 'Professionelle KFZ-Schadensdokumentation'}</p>
                            {companyData?.managing_director && (
                              <p className="text-xs text-gray-500 mt-1">Geschäftsführer: {companyData.managing_director}</p>
                            )}
                          </div>
                          
                          {/* Mitte: Logo GROSS und ZENTRIERT (aus Firmen-Einstellungen) */}
                          {companyData?.logo_url && (
                            <div className="flex-shrink-0 flex items-center justify-center px-6">
                              <img 
                                src={getBackendUrl() + companyData.logo_url} 
                                alt="Firmenlogo" 
                                className="h-24 w-auto object-contain"
                                data-testid="company-logo"
                              />
                            </div>
                          )}
                          
                          {/* Rechte Seite: Kontaktdaten */}
                          <div className="flex-1 text-right text-xs text-gray-600">
                            <p className="font-medium">{companyData?.company_name || 'Easyclaim24'}</p>
                            <p>{companyData?.address || 'Musterstraße 123'}</p>
                            <p>{companyData?.postal_code || '12345'} {companyData?.city || 'Berlin'}</p>
                            <p className="mt-2">Tel: {companyData?.phone || '+49 30 12345678'}</p>
                            <p>E-Mail: {companyData?.email || 'info@easyclaim24.de'}</p>
                            {companyData?.website && <p>Web: {companyData.website}</p>}
                            {companyData?.tax_number && (
                              <p className="text-xs mt-2">USt-IdNr.: {companyData.tax_number}</p>
                            )}
                            {companyData?.commercial_register && (
                              <p className="text-xs">HRB: {companyData.commercial_register}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Titelbereich - Zweispaltig */}
                      <div className="mb-10 page-break-after">
                        <div className="text-center mb-8">
                          <h2 className="text-2xl font-bold text-blue-900 mb-2">Schadensdokumentation</h2>
                          <p className="text-sm text-gray-600">Vollständige Auftragsübersicht</p>
                        </div>
                        
                        {/* Aktennummer prominent OBEN über beide Spalten */}
                        <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-6">
                          <p className="text-xs uppercase text-gray-500 mb-1">Aktennummer</p>
                          <p className="text-lg font-mono font-bold text-blue-900">{caseNumber || 'Noch nicht vergeben'}</p>
                        </div>
                        
                        {/* Zweispaltiges Layout für Titelblatt - auf gleicher Höhe */}
                        <div className="grid grid-cols-2 gap-8">
                          {/* Linke Spalte: Kunde & Fahrzeug */}
                          <div className="space-y-6">
                            {/* Kundendaten */}
                            <div>
                              <h3 className="text-sm font-bold text-blue-900 mb-3 pb-2 border-b border-blue-200">Kundendaten</h3>
                              <div className="space-y-2">
                                <div>
                                  <p className="text-xs uppercase text-gray-500">Name</p>
                                  <p className="text-sm font-semibold text-gray-900">{formData.customer_name || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-xs uppercase text-gray-500">Adresse</p>
                                  <p className="text-sm text-gray-900">{formData.customer_address || '-'}</p>
                                  <p className="text-sm text-gray-900">{formData.customer_plz_ort || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-xs uppercase text-gray-500">Kontakt</p>
                                  <p className="text-sm text-gray-900">{formData.customer_phone || '-'}</p>
                                  <p className="text-sm text-gray-900">{formData.customer_email || '-'}</p>
                                </div>
                              </div>
                            </div>
                            
                            {/* Fahrzeugdaten */}
                            <div>
                              <h3 className="text-sm font-bold text-blue-900 mb-3 pb-2 border-b border-blue-200">Fahrzeugdaten</h3>
                              <div className="space-y-2">
                                <div>
                                  <p className="text-xs uppercase text-gray-500">Fahrzeug</p>
                                  <p className="text-sm font-semibold text-gray-900">{formData.manufacturer} {formData.model}</p>
                                </div>
                                <div>
                                  <p className="text-xs uppercase text-gray-500">Kennzeichen</p>
                                  <p className="text-sm font-mono font-semibold text-gray-900">{formData.license_plate || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-xs uppercase text-gray-500">FIN</p>
                                  <p className="text-sm font-mono text-gray-900">{formData.vin || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-xs uppercase text-gray-500">Kilometerstand</p>
                                  <p className="text-sm text-gray-900">{formData.mileage ? `${formData.mileage} km` : '-'}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Rechte Spalte: Versicherung & Schaden */}
                          <div className="space-y-6">
                            {/* Versicherungsdaten */}
                            <div>
                              <h3 className="text-sm font-bold text-blue-900 mb-3 pb-2 border-b border-blue-200">Versicherungsdaten</h3>
                              <div className="space-y-2">
                                <div>
                                  <p className="text-xs uppercase text-gray-500">Versicherung</p>
                                  <p className="text-sm font-semibold text-gray-900">{formData.insurance_company || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-xs uppercase text-gray-500">Versicherungsscheinnummer</p>
                                  <p className="text-sm font-mono text-gray-900">{formData.insurance_policy_number || formData.insurance_number || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-xs uppercase text-gray-500">Schadennummer</p>
                                  <p className="text-sm font-mono text-gray-900">{formData.claim_number || '-'}</p>
                                </div>
                              </div>
                            </div>
                            
                            {/* Schadensdaten */}
                            <div>
                              <h3 className="text-sm font-bold text-blue-900 mb-3 pb-2 border-b border-blue-200">Schadensdaten</h3>
                              <div className="space-y-2">
                                <div>
                                  <p className="text-xs uppercase text-gray-500">Schadentag</p>
                                  <p className="text-sm text-gray-900">{formData.date_of_loss ? new Date(formData.date_of_loss).toLocaleDateString('de-DE') : '-'}</p>
                                </div>
                                <div>
                                  <p className="text-xs uppercase text-gray-500">Schadenort</p>
                                  <p className="text-sm text-gray-900">{formData.damage_location || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-xs uppercase text-gray-500">Selbstbeteiligung</p>
                                  <p className="text-sm text-gray-900">{formData.deductible ? `${formData.deductible} €` : '-'}</p>
                                </div>
                                <div>
                                  <p className="text-xs uppercase text-gray-500">Vorsteuerabzug</p>
                                  <p className="text-sm text-gray-900">{formData.vorsteuerabzugsberechtigt === 'ja' ? 'Ja' : 'Nein'}</p>
                                </div>
                                
                                {/* Schadenhöhe HERVORGEHOBEN */}
                                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 mt-4">
                                  <p className="text-xs uppercase text-gray-500 mb-1">Schadenhöhe (geschätzt)</p>
                                  <p className="text-xl font-bold text-gray-900">
                                    {formData.estimated_damage_amount ? `${parseFloat(formData.estimated_damage_amount).toFixed(2)} €` : '- €'}
                                  </p>
                                </div>
                              </div>
                            </div>
                            
                            {/* Dokumentdatum */}
                            <div className="pt-4 border-t border-gray-200">
                              <p className="text-xs uppercase text-gray-500">Dokumentdatum</p>
                              <p className="text-sm font-semibold text-gray-900">{new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 1. Auftragsdaten */}
                      <div className="mb-10">
                        <h2 className="text-xl font-bold text-blue-900 mb-4 pb-2 border-b-2 border-blue-600">1. Auftragsdaten</h2>
                        <div className="grid grid-cols-2 gap-x-8 gap-y-4 ml-4">
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Auftragstyp</p>
                            <p className="text-gray-900 font-medium capitalize">{formData.auftragstyp || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Aktennummer</p>
                            <p className="text-gray-900 font-mono font-medium">{caseNumber || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Vorname</p>
                            <p className="text-gray-900 font-medium">{formData.customer_first_name || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Nachname</p>
                            <p className="text-gray-900 font-medium">{formData.customer_last_name || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Vollständiger Name</p>
                            <p className="text-gray-900 font-medium">{formData.customer_name || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Telefon</p>
                            <p className="text-gray-900 font-medium">{formData.customer_phone || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">E-Mail</p>
                            <p className="text-gray-900 font-medium">{formData.customer_email || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Straße</p>
                            <p className="text-gray-900 font-medium">{formData.customer_address || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">PLZ / Ort</p>
                            <p className="text-gray-900 font-medium">{formData.customer_plz_ort || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Erstellt am</p>
                            <p className="text-gray-900 font-medium">{new Date().toLocaleDateString('de-DE')}</p>
                          </div>
                        </div>
                      </div>

                      {/* 2. Fahrzeugdaten */}
                      <div className="mb-10">
                        <h2 className="text-xl font-bold text-blue-900 mb-4 pb-2 border-b-2 border-blue-600">2. Fahrzeugdaten</h2>
                        <div className="grid grid-cols-2 gap-x-8 gap-y-4 ml-4">
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Hersteller</p>
                            <p className="text-gray-900 font-medium">{formData.manufacturer || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Modell</p>
                            <p className="text-gray-900 font-medium">{formData.model || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Fahrzeug-Identifizierungsnummer (VIN)</p>
                            <p className="text-gray-900 font-mono font-medium">{formData.vin || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Kennzeichen</p>
                            <p className="text-gray-900 font-mono font-medium">{formData.license_plate || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Baujahr</p>
                            <p className="text-gray-900 font-medium">{formData.year || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Erstzulassung</p>
                            <p className="text-gray-900 font-medium">{formData.first_registration ? new Date(formData.first_registration).toLocaleDateString('de-DE') : '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Kilometerstand</p>
                            <p className="text-gray-900 font-medium">{formData.mileage ? `${formData.mileage} km` : '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Vorsteuerabzugsberechtigt</p>
                            <p className="text-gray-900 font-medium">{formData.vorsteuerabzugsberechtigt === 'ja' ? 'Ja' : 'Nein'}</p>
                          </div>
                        </div>
                      </div>

                      {/* 3. Schadensdaten */}
                      <div className="mb-10">
                        <h2 className="text-xl font-bold text-blue-900 mb-4 pb-2 border-b-2 border-blue-600">3. Schadensinformationen</h2>
                        <div className="grid grid-cols-2 gap-x-8 gap-y-4 ml-4">
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Schadensdatum</p>
                            <p className="text-gray-900 font-medium">{formData.date_of_loss ? new Date(formData.date_of_loss).toLocaleDateString('de-DE') : '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Schadenstelle</p>
                            <p className="text-gray-900 font-medium">{formData.damage_location || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Schadenstyp</p>
                            <p className="text-gray-900 font-medium">{formData.damage_type || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Schweregrad</p>
                            <p className="text-gray-900 font-medium">{formData.severity ? `${formData.severity}/5` : '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Selbstbeteiligung</p>
                            <p className="text-gray-900 font-medium">{formData.deductible ? `${formData.deductible} €` : '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Geschätzte Schadenshöhe</p>
                            <p className="text-gray-900 font-medium">{formData.estimated_damage_amount ? `${parseFloat(formData.estimated_damage_amount).toFixed(2)} €` : '-'}</p>
                          </div>
                          {formData.damageAreas && formData.damageAreas.length > 0 && (
                            <div className="col-span-2">
                              <p className="text-xs uppercase text-gray-500 mb-1">Beschädigte Bereiche</p>
                              <p className="text-gray-900 font-medium">{formData.damageAreas.join(', ')}</p>
                            </div>
                          )}
                          <div className="col-span-2">
                            <p className="text-xs uppercase text-gray-500 mb-1">Schadensbeschreibung</p>
                            <p className="text-gray-900 font-medium">{formData.damage_description || '-'}</p>
                          </div>
                          {formData.notes && (
                            <div className="col-span-2">
                              <p className="text-xs uppercase text-gray-500 mb-1">Zusätzliche Bemerkungen</p>
                              <p className="text-gray-900 font-medium whitespace-pre-wrap">{formData.notes}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* 4. Versicherungsdaten */}
                      <div className="mb-10">
                        <h2 className="text-xl font-bold text-blue-900 mb-4 pb-2 border-b-2 border-blue-600">4. Versicherungsdaten</h2>
                        <div className="grid grid-cols-2 gap-x-8 gap-y-4 ml-4">
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Versicherungsgesellschaft</p>
                            <p className="text-gray-900 font-medium">{formData.insurance_company || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Versicherungsnummer/Policennummer</p>
                            <p className="text-gray-900 font-mono font-medium">{formData.insurance_policy_number || formData.insurance_number || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Schadennummer</p>
                            <p className="text-gray-900 font-mono font-medium">{formData.claim_number || formData.insurance_claim_number || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Vertragsbeginn</p>
                            <p className="text-gray-900 font-medium">{formData.contract_begin ? new Date(formData.contract_begin).toLocaleDateString('de-DE') : '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Vertragsende</p>
                            <p className="text-gray-900 font-medium">{formData.contract_end ? new Date(formData.contract_end).toLocaleDateString('de-DE') : '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-gray-500 mb-1">Kaskoversicherung</p>
                            <p className="text-gray-900 font-medium">{formData.has_kasko ? 'Ja' : 'Nein'}</p>
                          </div>
                          {formData.kasko_insurance_company && (
                            <>
                              <div>
                                <p className="text-xs uppercase text-gray-500 mb-1">Kasko-Versicherung</p>
                                <p className="text-gray-900 font-medium">{formData.kasko_insurance_company}</p>
                              </div>
                              <div>
                                <p className="text-xs uppercase text-gray-500 mb-1">Kasko Versicherungsnummer</p>
                                <p className="text-gray-900 font-mono font-medium">{formData.kasko_insurance_number || '-'}</p>
                              </div>
                              <div>
                                <p className="text-xs uppercase text-gray-500 mb-1">Kasko-Typ</p>
                                <p className="text-gray-900 font-medium">{formData.kasko_type || '-'}</p>
                              </div>
                              <div>
                                <p className="text-xs uppercase text-gray-500 mb-1">Kasko Selbstbeteiligung</p>
                                <p className="text-gray-900 font-medium">{formData.kasko_deductible ? `${formData.kasko_deductible} €` : '-'}</p>
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* Versicherungskontaktdaten */}
                        <div className="mt-6 pt-6 border-t border-gray-200">
                          <p className="text-sm font-semibold text-blue-900 mb-3">Kontaktdaten Versicherung</p>
                          <div className="grid grid-cols-2 gap-x-8 gap-y-4 ml-4">
                            <div>
                              <p className="text-xs uppercase text-gray-500 mb-1">Ansprechpartner</p>
                              <p className="text-gray-900 font-medium">{formData.insurance_po_name || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs uppercase text-gray-500 mb-1">Telefon</p>
                              <p className="text-gray-900 font-medium">{formData.insurance_po_telephone || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs uppercase text-gray-500 mb-1">E-Mail</p>
                              <p className="text-gray-900 font-medium">{formData.insurance_po_email || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs uppercase text-gray-500 mb-1">Fax</p>
                              <p className="text-gray-900 font-medium">{formData.insurance_po_fax || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs uppercase text-gray-500 mb-1">Straße</p>
                              <p className="text-gray-900 font-medium">{formData.insurance_po_street || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs uppercase text-gray-500 mb-1">PLZ / Stadt</p>
                              <p className="text-gray-900 font-medium">{formData.insurance_po_zip && formData.insurance_po_city ? `${formData.insurance_po_zip} ${formData.insurance_po_city}` : '-'}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 5. Lichtbilder */}
                      {formData.photos && formData.photos.length > 0 && (
                        <div className="mb-10 page-break-before">
                          <h2 className="text-xl font-bold text-blue-900 mb-4 pb-2 border-b-2 border-blue-600">5. Lichtbilder ({formData.photos.length} Aufnahmen)</h2>
                          <div className="grid grid-cols-2 gap-6 ml-4">
                            {formData.photos.map((photo, index) => (
                              <div key={index} className="border-2 border-gray-300">
                                <img 
                                  src={photo.startsWith('/api/') ? `${getBackendUrl()}${photo}` : photo} 
                                  alt={`Schadensfoto ${index + 1}`}
                                  className="w-full h-64 object-cover"
                                />
                                <div className="p-2 bg-white text-center text-sm text-gray-600 border-t border-gray-300">
                                  Abbildung {index + 1}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 5b. Kalkulationsdaten (wenn vorhanden) */}
                      {(formData.invoice_positions && formData.invoice_positions.length > 0) && (
                        <div className="mb-10 page-break-before">
                          <h2 className="text-xl font-bold text-blue-900 mb-4 pb-2 border-b-2 border-blue-600">Kalkulation / Rechnungspositionen</h2>
                          <div className="ml-4">
                            <table className="w-full border-collapse text-sm">
                              <thead>
                                <tr className="bg-gray-100 border-b-2 border-gray-300">
                                  <th className="text-left p-2 font-semibold">Pos.</th>
                                  <th className="text-left p-2 font-semibold">Beschreibung</th>
                                  <th className="text-right p-2 font-semibold">Menge</th>
                                  <th className="text-right p-2 font-semibold">Einzelpreis</th>
                                  <th className="text-right p-2 font-semibold">Gesamtpreis</th>
                                </tr>
                              </thead>
                              <tbody>
                                {formData.invoice_positions.map((pos, idx) => (
                                  <tr key={idx} className="border-b border-gray-200">
                                    <td className="p-2 text-gray-600">{idx + 1}</td>
                                    <td className="p-2">{pos.description || '-'}</td>
                                    <td className="p-2 text-right">{pos.quantity || 1}</td>
                                    <td className="p-2 text-right">{pos.unit_price ? `${parseFloat(pos.unit_price).toFixed(2)} €` : '-'}</td>
                                    <td className="p-2 text-right font-medium">{pos.total_price ? `${parseFloat(pos.total_price).toFixed(2)} €` : '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="border-t-2 border-gray-400 font-bold">
                                  <td colSpan="4" className="p-2 text-right">Summe Netto:</td>
                                  <td className="p-2 text-right">
                                    {formData.total_netto 
                                      ? `${parseFloat(formData.total_netto).toFixed(2)} €`
                                      : formData.invoice_positions.reduce((sum, pos) => sum + (parseFloat(pos.total_price) || 0), 0).toFixed(2) + ' €'
                                    }
                                  </td>
                                </tr>
                                {formData.tax_amount && (
                                  <tr className="font-medium text-gray-700">
                                    <td colSpan="4" className="p-2 text-right">MwSt ({formData.tax_rate || 19}%):</td>
                                    <td className="p-2 text-right">{parseFloat(formData.tax_amount).toFixed(2)} €</td>
                                  </tr>
                                )}
                                {formData.total && (
                                  <tr className="font-bold text-lg bg-blue-50">
                                    <td colSpan="4" className="p-2 text-right">Gesamtsumme Brutto:</td>
                                    <td className="p-2 text-right">{parseFloat(formData.total).toFixed(2)} €</td>
                                  </tr>
                                )}
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* 6. Abtretungserklärung */}
                      {(formData.abtretung_text || formData.abtretung_signature) && (
                        <div className="mb-10 page-break-before">
                          <h2 className="text-xl font-bold text-blue-900 mb-4 pb-2 border-b-2 border-blue-600">6. Abtretungserklärung</h2>
                          <div className="ml-4 space-y-4">
                            {formData.abtretung_text && (
                              <div className="bg-gray-50 p-6 rounded border border-gray-200">
                                <div className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">
                                  {formData.abtretung_text}
                                </div>
                              </div>
                            )}
                            
                            <div className="space-y-6 mt-8">
                              <div>
                                <p className="text-xs uppercase text-gray-500 mb-1">Ort, Datum</p>
                                <p className="text-gray-900 font-medium">
                                  {formData.abtretung_location && `${formData.abtretung_location}, `}
                                  {formData.abtretung_date ? new Date(formData.abtretung_date).toLocaleDateString('de-DE') : '-'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs uppercase text-gray-500 mb-2">Unterschrift Kunde</p>
                                {formData.abtretung_signature ? (
                                  <div className="border border-gray-300 bg-white p-2 inline-block">
                                    <img 
                                      src={formData.abtretung_signature.startsWith('/api/') 
                                        ? `${getBackendUrl()}${formData.abtretung_signature}`
                                        : formData.abtretung_signature
                                      } 
                                      alt="Unterschrift Abtretungserklärung"
                                      className="h-24"
                                      style={{maxWidth: '400px', imageRendering: '-webkit-optimize-contrast'}}
                                    />
                                  </div>
                                ) : (
                                  <p className="text-gray-400 text-sm italic">Keine Unterschrift</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Footer */}
                      <div className="border-t-2 border-blue-600 pt-8 mt-16">
                        <div className="grid grid-cols-3 gap-8 text-xs text-gray-600 mb-6">
                          <div>
                            <p className="font-semibold text-blue-900 mb-2">Easyclaim24</p>
                            <p>Musterstraße 123</p>
                            <p>12345 Berlin</p>
                          </div>
                          <div>
                            <p className="font-semibold text-blue-900 mb-2">Kontakt</p>
                            <p>Tel: +49 30 12345678</p>
                            <p>E-Mail: info@easyclaim24.de</p>
                          </div>
                          <div>
                            <p className="font-semibold text-blue-900 mb-2">Bankverbindung</p>
                            <p>IBAN: DE89 1234 5678 9012 3456 78</p>
                            <p>BIC: DEUTDEDB123</p>
                          </div>
                        </div>
                        <div className="text-center text-xs text-gray-500 pt-4 border-t border-gray-300">
                          <p>Erstellt am {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })} um {new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr</p>
                          <p className="mt-1">© {new Date().getFullYear()} Easyclaim24 - Alle Rechte vorbehalten</p>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
                </div>
                  </div>
                </div>
              )}

              {/* Step 7: Rechnung */}
              {currentStep === 7 && (
                <div className="animate-fade-in w-full flex justify-center">
                  <div className="bg-white rounded-lg shadow-lg border max-w-4xl w-full" style={{borderColor: '#E1E4E8'}}>
                    <div className="p-6 rounded-t-lg" style={{backgroundColor: '#3B82F6'}}>
                      <div className="flex items-center justify-between text-white">
                        <div className="flex items-center gap-3">
                          <Receipt className="w-6 h-6" />
                          <h1 className="text-xl font-bold">Rechnung</h1>
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              const response = await fetch(
                                `${getBackendUrl()}/api/cases/${caseId}/generate-invoice-pdf`,
                                {
                                  method: 'POST',
                                  headers: {
                                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                                  }
                                }
                              );

                              if (response.ok) {
                                const data = await response.json();
                                toast.success('Rechnung PDF erstellt!');
                                
                                // Add to documents list
                                const invoiceDoc = {
                                  id: Date.now(),
                                  name: `Rechnung-${formData.case_number || caseId}.pdf`,
                                  type: 'Rechnung',
                                  date: new Date().toISOString(),
                                  url: data.pdf_url,
                                  size: 'N/A'
                                };
                                setDocuments([invoiceDoc, ...documents]);
                              } else {
                                toast.error('Fehler beim Erstellen der Rechnung');
                              }
                            } catch (error) {
                              toast.error('Verbindungsfehler');
                            }
                          }}
                          className="px-6 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2 font-medium"
                        >
                          <Download className="w-5 h-5" />
                          Rechnung als PDF
                        </button>
                      </div>
                    </div>

                    {/* Rechnung Content */}
                    <div className="p-8">
                      {/* KI-PDF-Scanner Upload */}
                      <div className="mb-8 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-dashed border-purple-300 rounded-xl p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                              <Brain className="w-7 h-7 text-white" />
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                🤖 KI-PDF-Scanner
                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">NEU</span>
                              </h3>
                              <p className="text-sm text-gray-600 mt-1">
                                Laden Sie eine PDF-Rechnung oder Kalkulation hoch - die KI extrahiert automatisch alle Positionen
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            <input
                              type="file"
                              accept=".pdf"
                              id="ai-pdf-upload"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                
                                if (!caseId) {
                                  toast.error('Bitte speichern Sie zuerst den Fall');
                                  return;
                                }
                                
                                setScanningPdf(true);
                                toast.info('🤖 KI analysiert PDF...', { duration: 10000 });
                                
                                try {
                                  const formDataUpload = new FormData();
                                  formDataUpload.append('file', file);
                                  
                                  const response = await fetch(
                                    `${getBackendUrl()}/api/cases/${caseId}/scan-pdf-with-ai`,
                                    {
                                      method: 'POST',
                                      headers: {
                                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                                      },
                                      body: formDataUpload
                                    }
                                  );
                                  
                                  if (response.ok) {
                                    const data = await response.json();
                                    
                                    if (data.positions && data.positions.length > 0) {
                                      // Merge with existing positions
                                      const updatedPositions = [...datPositions, ...data.positions];
                                      setDatPositions(updatedPositions);
                                      
                                      // Update formData
                                      setFormData(prev => ({
                                        ...prev,
                                        invoice_positions: updatedPositions
                                      }));
                                      
                                      // Recalculate totals
                                      calculateTotalsFromPositions(updatedPositions, customInvoiceItems);
                                      
                                      toast.success(`✅ ${data.count} Positionen extrahiert!`, { duration: 5000 });
                                    } else {
                                      toast.warning('Keine Positionen im PDF gefunden');
                                    }
                                  } else {
                                    const error = await response.json();
                                    toast.error('❌ ' + (error.detail || 'Fehler beim Scannen'));
                                  }
                                } catch (error) {
                                  console.error('PDF Scan Error:', error);
                                  toast.error('❌ Verbindungsfehler beim PDF-Scan');
                                } finally {
                                  setScanningPdf(false);
                                  e.target.value = ''; // Reset input
                                }
                              }}
                              disabled={scanningPdf || !caseId}
                            />
                            <label
                              htmlFor="ai-pdf-upload"
                              className={`px-6 py-3 rounded-xl font-semibold text-white shadow-lg transition-all cursor-pointer flex items-center gap-2 ${
                                scanningPdf || !caseId
                                  ? 'bg-gray-400 cursor-not-allowed'
                                  : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 hover:shadow-xl'
                              }`}
                            >
                              {scanningPdf ? (
                                <>
                                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  KI analysiert...
                                </>
                              ) : (
                                <>
                                  <Zap className="w-5 h-5" />
                                  PDF scannen
                                </>
                              )}
                            </label>
                          </div>
                        </div>
                        
                        {/* Info Text */}
                        <div className="mt-4 flex items-start gap-2 text-xs text-gray-500">
                          <svg className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Die KI erkennt automatisch: Artikelnummern, Beschreibungen, Mengen, Preise und Gesamtsummen aus jeder PDF-Rechnung.</span>
                        </div>
                      </div>

                      {/* Invoice Header */}
                      <div className="mb-8 pb-6 border-b-2 border-gray-200">
                        <div className="flex justify-between items-start">
                          <div>
                            <h2 className="text-3xl font-bold text-gray-900 mb-2">RECHNUNG</h2>
                            <p className="text-gray-600">Rechnungsnummer: {formData.case_number || 'N/A'}</p>
                            <p className="text-gray-600">Datum: {new Date().toLocaleDateString('de-DE')}</p>
                          </div>
                          {companySettings?.logo && (
                            <img 
                              src={`${getBackendUrl()}${companySettings.logo}`}
                              alt="Firmenlogo"
                              className="h-16 object-contain"
                            />
                          )}
                        </div>
                      </div>

                      {/* Customer & Vehicle Info */}
                      <div className="grid grid-cols-2 gap-8 mb-8">
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-3">Rechnungsempfänger</h3>
                          <div className="text-sm text-gray-700 space-y-1">
                            <p className="font-medium">{formData.customer_name || 'Kunde'}</p>
                            <p>{formData.customer_address || ''}</p>
                            <p>{formData.customer_plz_ort || ''}</p>
                          </div>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-3">Fahrzeugdaten</h3>
                          <div className="text-sm text-gray-700 space-y-1">
                            <p><span className="font-medium">Kennzeichen:</span> {formData.license_plate || 'N/A'}</p>
                            <p><span className="font-medium">Hersteller:</span> {formData.manufacturer || 'N/A'}</p>
                            <p><span className="font-medium">Modell:</span> {formData.model || 'N/A'}</p>
                            <p><span className="font-medium">VIN:</span> {formData.vin || 'N/A'}</p>
                          </div>
                        </div>
                      </div>

                      {/* Leistungsbeschreibung */}
                      <div className="mb-6">
                        <h3 className="font-semibold text-gray-900 mb-3">Leistungsbeschreibung</h3>
                        <div className="bg-gray-50 rounded-lg p-4">
                          {/* Loading State */}
                          {loadingDatPositions && (
                            <div className="flex items-center justify-center py-8">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                              <span className="ml-3 text-gray-600">Lade DAT-Positionen...</span>
                            </div>
                          )}
                          
                          {!loadingDatPositions && (
                            <table className="w-full">
                              <thead>
                                <tr className="border-b border-gray-300">
                                  <th className="text-left py-2 text-sm font-semibold text-gray-700">Pos.</th>
                                  <th className="text-left py-2 text-sm font-semibold text-gray-700">Beschreibung</th>
                                  <th className="text-right py-2 text-sm font-semibold text-gray-700">Menge</th>
                                  <th className="text-right py-2 text-sm font-semibold text-gray-700">Einzelpreis</th>
                                  <th className="text-right py-2 text-sm font-semibold text-gray-700">Gesamt</th>
                                </tr>
                              </thead>
                              <tbody>
                                {/* Use datPositions if available, otherwise fall back to formData.calculation */}
                                {datPositions.length > 0 ? (
                                  <>
                                    {/* DAT Extracted Positions */}
                                    {datPositions.map((item, idx) => (
                                      <tr key={`dat-${idx}`} className="border-b border-gray-200">
                                        <td className="py-3 text-sm text-gray-700">{item.position || idx + 1}</td>
                                        <td className="py-3 text-sm text-gray-700">
                                          <div className="font-medium flex items-center gap-2">
                                            {item.description || 'Position'}
                                            <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded">DAT</span>
                                          </div>
                                          {item.article_number && (
                                            <div className="text-xs text-gray-500">Art.-Nr.: {item.article_number}</div>
                                          )}
                                          {item.unit && item.unit !== 'Stk' && (
                                            <div className="text-xs text-gray-500">Einheit: {item.unit}</div>
                                          )}
                                        </td>
                                        <td className="py-3 text-sm text-gray-700 text-right">{item.quantity || 1}</td>
                                        <td className="py-3 text-sm text-gray-700 text-right">{(item.unit_price || 0).toFixed(2)} €</td>
                                        <td className="py-3 text-sm text-gray-900 font-medium text-right">{(item.total_price || 0).toFixed(2)} €</td>
                                      </tr>
                                    ))}
                                  </>
                                ) : (
                                  <>
                                    {/* Fallback: Material Positions from calculation */}
                                    {formData.calculation?.material_items?.map((item, idx) => (
                                      <tr key={`mat-${idx}`} className="border-b border-gray-200">
                                        <td className="py-3 text-sm text-gray-700">{idx + 1}</td>
                                        <td className="py-3 text-sm text-gray-700">
                                          <div className="font-medium">{item.description || 'Material'}</div>
                                          {item.article_number && (
                                            <div className="text-xs text-gray-500">Art.-Nr.: {item.article_number}</div>
                                          )}
                                        </td>
                                        <td className="py-3 text-sm text-gray-700 text-right">{item.quantity || 1}</td>
                                        <td className="py-3 text-sm text-gray-700 text-right">{(item.unit_price || 0).toFixed(2)} €</td>
                                        <td className="py-3 text-sm text-gray-900 font-medium text-right">{(item.total_price || 0).toFixed(2)} €</td>
                                      </tr>
                                    ))}

                                    {/* Fallback: Labor Positions from calculation */}
                                    {formData.calculation?.labor_items?.map((item, idx) => (
                                      <tr key={`lab-${idx}`} className="border-b border-gray-200">
                                        <td className="py-3 text-sm text-gray-700">{(formData.calculation?.material_items?.length || 0) + idx + 1}</td>
                                        <td className="py-3 text-sm text-gray-700">
                                          <div className="font-medium">{item.description || 'Arbeitsleistung'}</div>
                                          {item.hours && (
                                            <div className="text-xs text-gray-500">{item.hours} AW</div>
                                          )}
                                        </td>
                                        <td className="py-3 text-sm text-gray-700 text-right">{item.hours || 0}</td>
                                        <td className="py-3 text-sm text-gray-700 text-right">{(item.hourly_rate || 0).toFixed(2)} €</td>
                                        <td className="py-3 text-sm text-gray-900 font-medium text-right">{(item.total_price || 0).toFixed(2)} €</td>
                                      </tr>
                                    ))}
                                  </>
                                )}

                                {/* Custom Items - always show after DAT or calculation items */}
                                {customInvoiceItems?.map((item, idx) => {
                                  const baseCount = datPositions.length > 0 
                                    ? datPositions.length 
                                    : (formData.calculation?.material_items?.length || 0) + (formData.calculation?.labor_items?.length || 0);
                                  
                                  return (
                                    <tr key={`custom-${idx}`} className="border-b border-gray-200 bg-blue-50">
                                      <td className="py-3 text-sm text-gray-700">{baseCount + idx + 1}</td>
                                      <td className="py-3 text-sm text-gray-700">
                                        <div className="font-medium flex items-center gap-2">
                                          {item.description}
                                          <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded">Custom</span>
                                        </div>
                                      </td>
                                      <td className="py-3 text-sm text-gray-700 text-right">{item.quantity} {item.unit}</td>
                                      <td className="py-3 text-sm text-gray-700 text-right">{(item.unit_price || 0).toFixed(2)} €</td>
                                      <td className="py-3 text-sm text-gray-900 font-medium text-right">
                                        <div className="flex items-center justify-end gap-2">
                                          {(item.total_price || 0).toFixed(2)} €
                                          <button
                                            onClick={() => deleteCustomItem(item.id)}
                                            className="text-red-600 hover:text-red-800"
                                            title="Position löschen"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}

                                {/* If no items at all, show generic fallback */}
                                {datPositions.length === 0 &&
                                 (!formData.calculation?.material_items || formData.calculation.material_items.length === 0) && 
                                 (!formData.calculation?.labor_items || formData.calculation.labor_items.length === 0) &&
                                 (!customInvoiceItems || customInvoiceItems.length === 0) && (
                                  <tr className="border-b border-gray-200">
                                    <td className="py-3 text-sm text-gray-700">1</td>
                                    <td className="py-3 text-sm text-gray-700">
                                      <div className="font-medium">Scheibenaustausch / Reparatur</div>
                                      <div className="text-xs text-gray-500">
                                        {formData.manufacturer} {formData.model} - {formData.license_plate}
                                      </div>
                                    </td>
                                    <td className="py-3 text-sm text-gray-700 text-right">1</td>
                                    <td className="py-3 text-sm text-gray-700 text-right">{(formData.total_netto || 0).toFixed(2)} €</td>
                                    <td className="py-3 text-sm text-gray-900 font-medium text-right">{(formData.total_netto || 0).toFixed(2)} €</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          )}
                          
                          {/* Add Item Button */}
                          <button
                            onClick={() => setShowAddItemModal(true)}
                            className="mt-4 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 text-sm font-medium"
                          >
                            <Plus className="w-4 h-4" />
                            Position hinzufügen
                          </button>
                        </div>
                      </div>

                      {/* Totals */}
                      <div className="flex justify-end mb-8">
                        <div className="w-80">
                          <div className="space-y-2 mb-3">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-700">Zwischensumme:</span>
                              <span className="font-medium text-gray-900">{(formData.total_netto || 0).toFixed(2)} €</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-700">MwSt. ({formData.tax_rate || 19}%):</span>
                              <span className="font-medium text-gray-900">{(formData.tax_amount || 0).toFixed(2)} €</span>
                            </div>
                          </div>
                          <div className="pt-3 border-t-2 border-gray-300">
                            <div className="flex justify-between">
                              <span className="text-lg font-bold text-gray-900">Gesamtbetrag:</span>
                              <span className="text-lg font-bold text-blue-600">{(formData.total || 0).toFixed(2)} €</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Footer Notes */}
                      <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                        <h4 className="font-semibold text-gray-900 mb-2">Zahlungsinformationen</h4>
                        <div className="text-sm text-gray-700 space-y-1">
                          <p>Zahlbar innerhalb von 14 Tagen ohne Abzug.</p>
                          <p>Vielen Dank für Ihren Auftrag!</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 8: Dokumentation - Clean & Modern */}
              {currentStep === 8 && (
                <div className="animate-fade-in w-full">
                  <div className="bg-white rounded-lg shadow-lg border w-full" style={{borderColor: '#E1E4E8'}}>
                    {/* Header */}
                    <div className="p-6 rounded-t-lg" style={{backgroundColor: '#3B82F6'}}>
                      <div className="flex items-center justify-between text-white">
                        <div className="flex items-center gap-3">
                          <FolderOpen className="w-6 h-6" />
                          <div>
                            <h1 className="text-xl font-bold">Dokumente</h1>
                            <p className="text-blue-100 text-sm mt-1">
                              Auftragstyp: {formData.auftragstyp === 'austausch' ? 'Austausch' : formData.auftragstyp === 'reparatur' ? 'Reparatur' : formData.auftragstyp === 'kostenvoranschlag' ? 'Kostenvoranschlag' : formData.auftragstyp}
                            </p>
                          </div>
                        </div>
                        {selectedDocs.length > 0 && (
                          <div className="bg-blue-700 px-4 py-2 rounded-lg">
                            <span className="font-semibold">{selectedDocs.length} ausgewählt</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-4">
                      {/* Toolbar */}
                      <div className="flex flex-wrap items-center gap-3 pb-4 pt-6 px-6 border-b" style={{borderColor: '#E1E4E8'}}>
                        <button 
                          onClick={() => toast.info('Neues Dokument erstellen...')}
                          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 text-sm font-medium"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Neu
                        </button>
                        <button 
                          onClick={() => fileInputRef.current?.click()}
                          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 text-sm font-medium"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          Importieren
                        </button>
                        <button 
                          onClick={() => toast.info('Unternehmensdokument auswählen...')}
                          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 text-sm font-medium"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Unternehmensdokument
                        </button>
                        <button 
                          onClick={() => {
                            setDocumentFilter('today');
                            toast.success('Heute-Filter aktiviert');
                          }}
                          className={`px-4 py-2 border-2 rounded-lg flex items-center gap-2 text-sm font-medium ${
                            documentFilter === 'today' 
                              ? 'border-green-500 text-green-600 bg-green-50' 
                              : 'border-green-500 text-green-600 hover:bg-green-50'
                          }`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Heute
                        </button>
                        <button 
                          onClick={() => toast.info('Kalender öffnen...')}
                          className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </button>
                        <button 
                          onClick={() => toast.info('Dokument wird generiert...')}
                          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 text-sm font-medium"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Generieren
                        </button>
                      </div>

                      {/* Secondary Toolbar */}
                      <div className="flex items-center justify-between gap-4 px-6 pb-4 border-b" style={{borderColor: '#E1E4E8'}}>
                        <div className="flex items-center gap-3">
                          <select className="px-3 py-2 border rounded-lg text-sm" style={{borderColor: '#E1E4E8'}}>
                            <option>Zusammenstellung</option>
                            <option>Rechnung</option>
                            <option>Gutachten</option>
                          </select>
                          <button 
                            onClick={handlePDFBundle}
                            disabled={bundlingPdfs || selectedDocs.length === 0}
                            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium flex items-center gap-2"
                          >
                            {bundlingPdfs ? (
                              <>
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Wird gebündelt...
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                PDF bündeln
                              </>
                            )}
                          </button>
                          <button 
                            onClick={() => toast.info('Einstellungen öffnen...')}
                            className="p-2 border rounded-lg hover:bg-gray-50" 
                            style={{borderColor: '#E1E4E8'}}
                          >
                            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </button>
                        </div>
                        <div className="flex items-center gap-3">
                          <button 
                            onClick={() => {
                              setDocumentFilter('all');
                              toast.success('Alle Dokumente anzeigen');
                            }}
                            className={`px-4 py-2 border rounded-lg text-sm font-medium hover:bg-gray-50 ${
                              documentFilter === 'all' ? 'bg-gray-100' : ''
                            }`} 
                            style={{borderColor: '#E1E4E8'}}
                          >
                            Alle
                          </button>
                          <select 
                            value={documentSort}
                            onChange={(e) => {
                              setDocumentSort(e.target.value);
                              toast.success(`Sortiert nach: ${e.target.value === 'created' ? 'Erstellt am' : e.target.value}`);
                            }}
                            className="px-3 py-2 border rounded-lg text-sm" 
                            style={{borderColor: '#E1E4E8'}}
                          >
                            <option value="created">Erstellt am</option>
                            <option value="modified">Geändert am</option>
                            <option value="name">Name</option>
                            <option value="size">Größe</option>
                          </select>
                          <div className="flex gap-1">
                            <button 
                              onClick={() => {
                                setDocumentView('list');
                                toast.success('Listenansicht aktiviert');
                              }}
                              className={`p-2 border rounded-lg ${
                                documentView === 'list' ? 'bg-blue-50 border-blue-500' : 'border-gray-300'
                              }`}
                            >
                              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                              </svg>
                            </button>
                            <button 
                              onClick={() => {
                                setDocumentView('grid');
                                toast.success('Kachelansicht aktiviert');
                              }}
                              className={`p-2 border rounded-lg ${
                                documentView === 'grid' ? 'bg-blue-50 border-blue-500' : 'border-gray-300'
                              }`}
                            >
                              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Hidden File Input */}
                      <input 
                        type="file" 
                        ref={fileInputRef}
                        onChange={handleDocumentUpload}
                        multiple
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        className="hidden"
                      />

                      <div className="p-8">
                      {/* Toolbar mit Bündel-Button */}
                      {selectedDocs.length > 0 && (
                        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <p className="text-sm text-blue-900">
                            <strong>{selectedDocs.length} Dokument(e)</strong> ausgewählt - Klicken Sie auf "PDF bündeln" oben, um fortzufahren
                          </p>
                        </div>
                      )}

                      {/* Dokumente-Liste mit Checkboxen - Kompakt & Verschiebbar */}
                      <div className="space-y-3">
                        {documents.length > 0 ? (
                          documents.map((doc, index) => (
                            <div 
                              key={doc.id} 
                              draggable="true"
                              onDragStart={(e) => handleDocDragStart(e, index)}
                              onDragOver={(e) => handleDocDragOver(e, index)}
                              onDrop={(e) => handleDocDrop(e, index)}
                              onDragEnd={handleDocDragEnd}
                              className={`border border-gray-200 rounded-lg p-4 hover:border-blue-400 hover:shadow-md transition-all bg-white cursor-move ${
                                draggedDocIndex === index ? 'opacity-50 scale-95' : ''
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                {/* Drag Handle Icon */}
                                <div className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing">
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                  </svg>
                                </div>
                                {/* Checkbox */}
                                <input
                                  type="checkbox"
                                  checked={selectedDocs.includes(doc.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedDocs([...selectedDocs, doc.id]);
                                    } else {
                                      setSelectedDocs(selectedDocs.filter(id => id !== doc.id));
                                    }
                                  }}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                
                                {/* Icon - Klein (wie im Wizard) */}
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                  doc.type === 'Auftrag' ? 'bg-blue-100' :
                                  doc.type === 'Abtretungserklärung' ? 'bg-green-100' :
                                  doc.type === 'Lichtbilder' ? 'bg-purple-100' :
                                  doc.type === 'Rechnung' ? 'bg-indigo-100' :
                                  doc.type === 'Kalkulation' ? 'bg-yellow-100' :
                                  'bg-gray-100'
                                }`}>
                                  {doc.type === 'Auftrag' && <FileText className="w-5 h-5 text-blue-600" />}
                                  {doc.type === 'Abtretungserklärung' && <FileSignature className="w-5 h-5 text-green-600" />}
                                  {doc.type === 'Lichtbilder' && <Camera className="w-5 h-5 text-purple-600" />}
                                  {doc.type === 'Rechnung' && <Receipt className="w-5 h-5 text-indigo-600" />}
                                  {doc.type === 'Kalkulation' && <Calculator className="w-5 h-5 text-yellow-600" />}
                                </div>
                                
                                {/* Info - Kompakt mit Edit-Funktion */}
                                <div className="flex-1 min-w-0">
                                  {editingDocId === doc.id ? (
                                    // Edit-Modus
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="text"
                                        value={editingDocName}
                                        onChange={(e) => setEditingDocName(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            // Speichern
                                            setDocuments(prev => prev.map(d => 
                                              d.id === doc.id ? { ...d, type: editingDocName } : d
                                            ));
                                            setEditingDocId(null);
                                          } else if (e.key === 'Escape') {
                                            setEditingDocId(null);
                                          }
                                        }}
                                        onBlur={() => {
                                          // Speichern beim Verlassen
                                          if (editingDocName.trim()) {
                                            setDocuments(prev => prev.map(d => 
                                              d.id === doc.id ? { ...d, type: editingDocName } : d
                                            ));
                                          }
                                          setEditingDocId(null);
                                        }}
                                        className="text-sm font-bold text-gray-900 border border-blue-400 rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        autoFocus
                                      />
                                    </div>
                                  ) : (
                                    // Anzeige-Modus
                                    <div className="flex items-center gap-2">
                                      <h3 className="text-sm font-bold text-gray-900 truncate">{doc.type}</h3>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingDocId(doc.id);
                                          setEditingDocName(doc.type);
                                        }}
                                        className="text-gray-400 hover:text-blue-600 transition-colors"
                                        title="Name bearbeiten"
                                      >
                                        <Edit className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  )}
                                  <p className="text-xs text-gray-500 truncate">{doc.name}</p>
                                </div>
                                
                                {/* Actions - Nur Icons */}
                                <div className="flex gap-3">
                                  {/* Ansehen - Lupe Icon */}
                                  <button
                                    data-testid={`preview-${doc.type}`}
                                    onClick={() => {
                                      setCurrentPdfTitle(doc.name);
                                      // Ensure full URL for PDF viewer
                                      const fullUrl = doc.url.startsWith('http') 
                                        ? doc.url 
                                        : `${getBackendUrl()}${doc.url}`;
                                      setCurrentPdfUrl(fullUrl);
                                      setPdfViewerOpen(true);
                                    }}
                                    className="text-gray-500 hover:text-gray-700 transition-colors"
                                    title="Ansehen"
                                  >
                                    <Search className="w-5 h-5" />
                                  </button>
                                  
                                  {/* Download Icon */}
                                  <a
                                    href={doc.url.startsWith('http') ? doc.url : `${getBackendUrl()}${doc.url}`}
                                    download={doc.name}
                                    className="text-gray-500 hover:text-gray-700 transition-colors"
                                    title="Download"
                                  >
                                    <Download className="w-5 h-5" />
                                  </a>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-16">
                            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                              <FolderOpen className="w-10 h-10 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Noch keine Dokumente</h3>
                            <p className="text-sm text-gray-600">
                              Dokumente werden automatisch erstellt, wenn Sie die Schritte durchlaufen.
                            </p>
                          </div>
                        )}
                        
                        {/* Gebündelte PDF */}
                        {formData.bundled_pdf_url && (
                          <div className="border-2 border-green-300 bg-green-50 rounded-xl p-6">
                            <div className="flex items-start gap-4">
                              <input
                                type="checkbox"
                                checked={selectedDocs.includes('bundled')}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedDocs([...selectedDocs, 'bundled']);
                                  } else {
                                    setSelectedDocs(selectedDocs.filter(id => id !== 'bundled'));
                                  }
                                }}
                                className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 mt-1"
                              />
                              
                              <div className="w-12 h-12 bg-green-200 rounded-lg flex items-center justify-center flex-shrink-0">
                                <svg className="w-6 h-6 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                              
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="text-lg font-bold text-green-900">Gebündelte Dokumentation</h3>
                                  <span className="px-2 py-1 bg-green-200 text-green-800 text-xs font-bold rounded">NEU</span>
                                </div>
                                <p className="text-sm text-green-700 mb-2">Alle Dokumente_{caseNumber}.pdf</p>
                                <p className="text-xs text-green-600">Enthält: Auftrag, Abtretung, DAT-Kalkulation</p>
                              </div>
                              
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    setCurrentPdfTitle('Gebündelte Dokumentation');
                                    setCurrentPdfUrl(`${getBackendUrl()}${formData.bundled_pdf_url}`);
                                    setPdfViewerOpen(true);
                                  }}
                                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  Ansehen
                                </button>
                                <a
                                  href={`${getBackendUrl()}${formData.bundled_pdf_url}`}
                                  download={`Alle_Dokumente_${caseNumber}.pdf`}
                                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-medium"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  Download
                                </a>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 9: Email */}
              {currentStep === 9 && (
                <div className="animate-fade-in w-full">
                  <div className="bg-white rounded-lg shadow-lg border w-full" style={{borderColor: '#E1E4E8'}}>
                    <div className="p-6 rounded-t-lg" style={{backgroundColor: '#3B82F6'}}>
                      <div className="flex items-center gap-3 text-white">
                        <Mail className="w-6 h-6" />
                        <h1 className="text-xl font-bold">Email-Versand</h1>
                      </div>
                    </div>
                    <div className="px-6 py-6">
                      {!emailConfigured && (
                        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-sm text-yellow-800">
                            ⚠️ Email ist noch nicht konfiguriert. Bitte konfigurieren Sie Ihre Email-Einstellungen unter <strong>"Einstellungen"</strong>.
                          </p>
                        </div>
                      )}
                      
                      <p className="text-gray-600 mb-6">Versenden Sie die Dokumentation an den Kunden oder die Versicherung</p>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Empfänger Email *</label>
                          <input 
                            type="email" 
                            value={emailData.to}
                            onChange={(e) => setEmailData({...emailData, to: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="kunde@example.com"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Betreff *</label>
                          <input 
                            type="text" 
                            value={emailData.subject}
                            onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Nachricht *</label>
                          <textarea 
                            rows={8}
                            value={emailData.body}
                            onChange={(e) => setEmailData({...emailData, body: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Ihre Email-Signatur wird automatisch hinzugefügt.
                          </p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Anhänge auswählen</label>
                          <div className="space-y-2 bg-gray-50 p-4 rounded-lg border border-gray-200">
                            {[
                              { id: 'auftrag', label: 'Auftrag PDF', available: !!formData.auftrag_pdf_url },
                              { id: 'abtretung', label: 'Abtretungserklärung', available: !!formData.abtretung_pdf_url },
                              { id: 'lichtbilder', label: 'Lichtbilder', available: !!formData.lichtbilder_pdf_url },
                              { id: 'dat', label: 'DAT-Kalkulation', available: !!formData.dat_pdf_url },
                              { id: 'rechnung', label: 'Rechnung', available: !!formData.invoice_pdf_url }
                            ].map(doc => (
                              <label key={doc.id} className={`flex items-center gap-2 p-2 rounded hover:bg-gray-100 cursor-pointer ${!doc.available ? 'opacity-50' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={emailData.attachments.includes(doc.id)}
                                  disabled={!doc.available}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setEmailData({...emailData, attachments: [...emailData.attachments, doc.id]});
                                    } else {
                                      setEmailData({...emailData, attachments: emailData.attachments.filter(a => a !== doc.id)});
                                    }
                                  }}
                                  className="w-4 h-4 text-blue-600"
                                />
                                <span className="text-sm text-gray-700">{doc.label}</span>
                                {!doc.available && <span className="text-xs text-gray-400">(nicht verfügbar)</span>}
                              </label>
                            ))}
                          </div>
                          <p className="mt-2 text-xs text-gray-500">
                            {emailData.attachments.length} Anhang/Anhänge ausgewählt
                          </p>
                        </div>
                        
                        <button 
                          type="button" 
                          onClick={sendCaseEmail}
                          disabled={sendingEmail || !emailConfigured || !emailData.to}
                          className="w-full px-6 py-3 rounded-lg text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                          style={{backgroundColor: '#3B82F6'}}
                        >
                          {sendingEmail ? (
                            <span className="flex items-center justify-center gap-2">
                              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Wird gesendet...
                            </span>
                          ) : (
                            '📧 Email senden'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mt-8 sm:justify-end">
                {currentStep > 1 && (
                  <button type="button" onClick={prevStep} className="w-full sm:w-auto px-4 py-2 text-gray-700 font-medium border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-sm" data-testid="prev-button">
                    Zurück
                  </button>
                )}
                {currentStep < 9 ? (
                  <button type="button" onClick={nextStep} className="w-full sm:w-auto px-4 py-2 text-white font-medium rounded-md transition-colors text-sm flex items-center justify-center gap-2" style={{backgroundColor: '#3B82F6'}} data-testid="next-button">
                    Weiter
                    <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button type="submit" disabled={loading} className="w-full sm:w-auto px-4 py-2 text-white font-medium rounded-md transition-colors text-sm" style={{backgroundColor: '#0A4D8C'}} data-testid="submit-button">
                    {loading ? 'Wird gespeichert...' : 'Fall erstellen'}
                  </button>
                )}
                <Link to="/" className="w-full sm:w-auto text-center px-4 py-2 text-gray-700 font-medium border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-sm flex items-center justify-center" data-testid="cancel-button">
                  Abbrechen
                </Link>
              </div>

            </div>
          </div>
        </main>
      </div>

      {/* Add Position Modal */}
      {showAddPositionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowAddPositionModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Neue Position hinzufügen</h3>
                <button
                  onClick={() => setShowAddPositionModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung *</label>
                  <input
                    type="text"
                    value={newPosition.description}
                    onChange={(e) => setNewPosition(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="z.B. Scheibenwischer"
                    data-testid="position-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Menge</label>
                    <input
                      type="number"
                      min="1"
                      value={newPosition.quantity}
                      onChange={(e) => setNewPosition(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      data-testid="position-quantity-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Preis (€) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newPosition.price}
                      onChange={(e) => setNewPosition(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                      data-testid="position-price-input"
                    />
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newPosition.saveAsDefault}
                      onChange={(e) => setNewPosition(prev => ({ ...prev, saveAsDefault: e.target.checked }))}
                      className="mt-1 w-4 h-4 text-blue-600"
                      data-testid="position-save-default-checkbox"
                    />
                    <div>
                      <p className="text-sm font-medium text-blue-900">Als Standard-Position speichern</p>
                      <p className="text-xs text-blue-700 mt-1">
                        Diese Position wird bei zukünftigen Rechnungen automatisch angezeigt.
                      </p>
                    </div>
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddPositionModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    onClick={addCustomPosition}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    data-testid="position-add-btn"
                  >
                    Hinzufügen
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Editor Modal */}
      <ImageEditor
        imageUrl={editingImage}
        isOpen={editingImage !== null}
        onClose={() => {
          setEditingImage(null);
          setEditingImageIndex(null);
        }}
        onSave={async (blob) => {
          try {
            // Upload edited image
            const formDataUpload = new FormData();
            formDataUpload.append('file', blob, 'edited-image.jpg');

            const response = await fetch(`${getBackendUrl()}/api/upload`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: formDataUpload
            });

            if (response.ok) {
              const data = await response.json();
              // Replace old photo with edited one
              const newPhotos = [...formData.photos];
              newPhotos[editingImageIndex] = data.url;
              setFormData({ ...formData, photos: newPhotos });
              toast.success('✅ Bearbeitetes Bild gespeichert');
            } else {
              toast.error('❌ Fehler beim Speichern');
            }
          } catch (error) {
            console.error('Error saving edited image:', error);
            toast.error('❌ Fehler beim Speichern');
          }
        }}
      />
      
      {/* PDF-Viewer Modal - Nur PDF-Vorschau */}
      <Dialog open={pdfViewerOpen} onOpenChange={setPdfViewerOpen}>
        <DialogContent className="max-w-7xl h-[95vh] p-0 overflow-hidden">
          <div className="h-full w-full flex flex-col">
            {/* Header mit Aktionen */}
            <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b">
              <h3 className="text-sm font-medium text-gray-700 truncate max-w-md">{currentPdfTitle || 'PDF Vorschau'}</h3>
              <div className="flex gap-2 mr-10">
                <a
                  href={currentPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  In neuem Tab öffnen
                </a>
                <a
                  href={currentPdfUrl}
                  download
                  className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                >
                  Download
                </a>
              </div>
            </div>
            {/* PDF Embed */}
            <div className="flex-1 overflow-hidden">
              {currentPdfUrl && (
                <object
                  data={currentPdfUrl}
                  type="application/pdf"
                  className="w-full h-full"
                >
                  {/* Fallback wenn PDF nicht angezeigt werden kann */}
                  <div className="flex flex-col items-center justify-center h-full bg-gray-50 p-8">
                    <FolderOpen className="w-16 h-16 text-gray-400 mb-4" />
                    <p className="text-gray-600 mb-4 text-center">
                      PDF kann nicht im Browser angezeigt werden.
                    </p>
                    <div className="flex gap-3">
                      <a
                        href={currentPdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        In neuem Tab öffnen
                      </a>
                      <a
                        href={currentPdfUrl}
                        download
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                      >
                        Download
                      </a>
                    </div>
                  </div>
                </object>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Custom Item Modal */}
      {showAddItemModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowAddItemModal(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl m-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 bg-blue-600">
              <div className="flex items-center justify-between text-white">
                <h2 className="text-xl font-bold">Position hinzufügen</h2>
                <button onClick={() => setShowAddItemModal(false)} className="hover:bg-blue-700 p-2 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Description with Autocomplete */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">Beschreibung *</label>
                <input
                  type="text"
                  value={newItem.description}
                  onChange={(e) => handleDescriptionChange(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="z.B. Fahrtkosten, Zusätzliche Arbeitsleistung"
                  autoFocus
                />
                
                {/* Autocomplete Suggestions */}
                {filteredSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {filteredSuggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => selectSuggestion(suggestion)}
                        className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">{suggestion.description}</div>
                        <div className="text-xs text-gray-500">
                          {suggestion.unit_price?.toFixed(2)} € / {suggestion.unit} • {suggestion.usage_count}x verwendet
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4">
                {/* Quantity */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Menge *</label>
                  <input
                    type="number"
                    value={newItem.quantity}
                    onChange={(e) => setNewItem({...newItem, quantity: parseFloat(e.target.value) || 1})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="0.01"
                  />
                </div>

                {/* Unit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Einheit</label>
                  <select
                    value={newItem.unit}
                    onChange={(e) => setNewItem({...newItem, unit: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Stk">Stk</option>
                    <option value="h">Stunden</option>
                    <option value="km">km</option>
                    <option value="m²">m²</option>
                    <option value="l">Liter</option>
                    <option value="kg">kg</option>
                    <option value="Pauschal">Pauschal</option>
                  </select>
                </div>

                {/* Unit Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Einzelpreis * (€)</label>
                  <input
                    type="number"
                    value={newItem.unit_price}
                    onChange={(e) => setNewItem({...newItem, unit_price: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Total Preview */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Gesamt:</span>
                  <span className="text-lg font-bold text-gray-900">
                    {(newItem.quantity * newItem.unit_price).toFixed(2)} €
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowAddItemModal(false)}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                Abbrechen
              </button>
              <button
                onClick={addCustomItem}
                className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
              >
                Position hinzufügen
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default NewCase;
